import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { getProductItems } from '../../utils/productCatalog';
import { getLiveCollection, getLiveDoc } from '../../utils/liveData';
import { useAuth, ROLES } from '../../hooks/useAuth';
import { PRODUCT_TYPES, ORDER_STATUS, ORDER_STATUS_LABELS, TUL_PILE_MODELS, KANAT_PILE_MODELS, PAYMENT_TYPES, PAYMENT_TYPE_LABELS } from '../../constants';
import { formatPrice, calculatePileDensity, calculateUsedM2, calculateOzelProje, buildStorZebraExtras, hasAnyStorZebraExtra, STOR_ZEBRA_EXTRA_PRICE_FIELDS, STOR_ZEBRA_EXTRA_COST_FIELDS, emptyStorZebraExtraOrderFields, pickStorZebraExtraOrderFields, calculateCapsulePrice, calculateCapsuleCost, calculateTotalPrice, calculateDiscountedPrice, calculateStorZebraUsedM2, calculateStorZebraTotalPrice, calculateJaluziUsedM2, calculateJaluziTotalPrice, calculateDikeyPerdeTotalPrice, calculatePliseUsedM2, calculatePliseTotalPrice, calculateAksesuarTotalPrice, calculateKatlamaliPerdeUsedM, calculateKatlamaliPerdeTotalPrice, calculateTotalCost, calculateStorZebraTotalCost, calculateJaluziTotalCost, calculateDikeyPerdeTotalCost, calculatePliseTotalCost, calculateKatlamaliPerdeTotalCost, calculateRustikTotalCost, calculateAksesuarTotalCost, calculateAstarPrice, calculateAstarCost, parseMotorSelection, findMotorCompany } from '../../utils';
import { generateCustomerPDF, generateCustomerPreOrderPDF, generateWorkshopPDF, generateInstallationPDF, shareCustomerPDF, shareCustomerPreOrderPDF } from '../../utils/pdfGenerators';
import { normalizePhoneForWhatsApp } from '../../utils/whatsappUtils';
import { generateInvoiceExcel } from '../../utils/excelGenerators';
import OrderForm from './OrderForm';
import { SupplierCheck } from './SupplierOrderNotes';
import KesifMeasurements from './KesifMeasurements';
import './OrderDetails.css';
import './OrderDetailsModern.css';

// <input type="datetime-local"> için yerel saatle 'YYYY-MM-DDTHH:mm'
const toDateTimeLocal = (date = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const OrderDetails = ({
  customerId,
  customerName,
  customerInfo,
  isLoading,
  orderStatus,
  onOrderStatusChange,
  onCustomerSaved,
  isEditMode, // Düzenleme modu mu?
  onVadeliYuzdeChange, // Vadeli yüzde değiştiğinde parent'a bildir
  onGenelIskontoYuzdeChange, // Genel iskonto yüzde değiştiğinde parent'a bildir
  onSaveVadeliSuccess, // Vadeli/İskonto kaydetme başarılı olduğunda bildir
  permissions = {} // Rol bazlı yetkiler
}) => {
  const { user, isMontajci } = useAuth();
  const [orders, setOrders] = useState([]);
  const [isAddingOrder, setIsAddingOrder] = useState(false);
  const [selectedProductType, setSelectedProductType] = useState('');
  const [editingOrderId, setEditingOrderId] = useState(null); // Edit modu için
  const [deleteConfirm, setDeleteConfirm] = useState(null); // Silme onayı için
  const [vadeliYuzde, setVadeliYuzde] = useState(''); // Vadeli yüzde için
  const [genelIskontoYuzde, setGenelIskontoYuzde] = useState(''); // Genel iskonto yüzde için
  const [manualGrandTotal, setManualGrandTotal] = useState(''); // Elle girilen genel toplam
  const [isSavingVadeli, setIsSavingVadeli] = useState(false); // Vadeli/İskonto kaydetme durumu
  const [paidAmount, setPaidAmount] = useState(''); // Ödenen tutar
  const [isPaymentCompleted, setIsPaymentCompleted] = useState(false); // Ödeme tamamlandı mı
  const [isSavingPayment, setIsSavingPayment] = useState(false); // Ödeme kaydetme durumu
  const [pileModels, setPileModels] = useState([]); // Pile modelleri
  const [showSystemConfirm, setShowSystemConfirm] = useState(false); // Sistem seçim onayı için

  // Ödeme geçmişi state'leri
  const [paymentHistory, setPaymentHistory] = useState([]); // Ödeme geçmişi listesi
  const [newPaymentAmount, setNewPaymentAmount] = useState(''); // Yeni ödeme tutarı
  const [newPaymentType, setNewPaymentType] = useState(PAYMENT_TYPES.CASH); // Yeni ödeme tipi
  const [newPaymentDate, setNewPaymentDate] = useState(() => toDateTimeLocal()); // Yeni ödeme tarihi
  // "Ödeme Tamamlandı" işaretlenince kalan tutar için tarih ve ödeme tipi sorulur
  const [completePaymentForm, setCompletePaymentForm] = useState(null); // { date, type } | null
  const [isAddingPayment, setIsAddingPayment] = useState(false); // Ödeme ekleme durumu
  const [cancelPaymentConfirm, setCancelPaymentConfirm] = useState(null); // İptal edilecek ödeme
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' }); // Bildirim sistemi

  const [adminSettings, setAdminSettings] = useState({});
  const [exchangeRates, setExchangeRates] = useState({ USD: 1, EUR: 1 });

  const showToast = useCallback((message, type = 'error') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 4000);
  }, []);

  // Mobilde durum şeridi yana kayar: mevcut adımı görünür alana getir
  const statusStepsRef = useRef(null);
  useEffect(() => {
    const container = statusStepsRef.current;
    const current = container?.querySelector('.od-status-step.is-current');
    if (!container || !current || container.scrollWidth <= container.clientWidth) return;
    const containerRect = container.getBoundingClientRect();
    const currentRect = current.getBoundingClientRect();
    const currentLeftInStrip = currentRect.left - containerRect.left + container.scrollLeft;
    container.scrollLeft = Math.max(0, currentLeftInStrip - (container.clientWidth - currentRect.width) / 2);
  }, [orderStatus]);

  // ---- Ürün markaları: siparişte ürün adı tutulur, marka Ürün Listesi'ndeki "Firma Adı"ndan bulunur ----
  const [productBrands, setProductBrands] = useState({ byTypeName: {}, byName: {} });
  useEffect(() => {
    if (isMontajci) return;
    let cancelled = false;
    getProductItems()
      .then((items) => {
        if (cancelled) return;
        const byTypeName = {};
        const byName = {};
        items.forEach(({ name, productType, companyName }) => {
          const brand = (companyName || '').trim();
          if (!name || !brand) return;
          byTypeName[`${productType}|${name}`] = brand;
          if (!byName[name]) byName[name] = brand;
        });
        setProductBrands({ byTypeName, byName });
      })
      .catch((err) => console.error('Ürün markaları okunamadı:', err));
    return () => { cancelled = true; };
  }, [isMontajci]);

  const getOrderBrands = (order) => {
    const names = order.productType === 'plise'
      ? [order.pliseProduct1Name || order.productName, order.pliseProduct2Name]
      : [order.productName];
    const brands = names
      .filter(Boolean)
      .map((name) => productBrands.byTypeName[`${order.productType}|${name}`] || productBrands.byName[name])
      .filter(Boolean);
    return [...new Set(brands)];
  };

  const renderBrandChips = (order) => getOrderBrands(order).map((brand) => (
    <span key={brand} className="od-brand-chip" title="Marka">{brand}</span>
  ));

  // ---- Montaj notu (müşteri kaydında: montajNote, montajNoteAt, montajNoteBy) ----
  const [montajNote, setMontajNote] = useState(customerInfo?.montajNote || '');
  const [montajNoteMeta, setMontajNoteMeta] = useState(
    customerInfo?.montajNoteAt ? { at: customerInfo.montajNoteAt, by: customerInfo.montajNoteBy || '' } : null
  );
  const [montajNoteDirty, setMontajNoteDirty] = useState(false);
  const [isSavingMontajNote, setIsSavingMontajNote] = useState(false);
  const canEditMontajNote = isMontajci || !!permissions?.canEditCustomer;

  // Keşif ölçüleri state'i (müşteri kaydıyla birlikte aşağıda okunur)
  const [kesifItems, setKesifItems] = useState(Array.isArray(customerInfo?.kesifItems) ? customerInfo.kesifItems : []);
  const [convertingKesifId, setConvertingKesifId] = useState(null); // Formu dolduran keşif kaydı
  const [kesifFill, setKesifFill] = useState(null); // Ürün tipi seçildikten sonra uygulanacak ölçüler

  // Güncel notu müşteri kaydından oku (liste eski olabilir)
  useEffect(() => {
    if (!customerId || customerId.startsWith('temp_')) return;
    let cancelled = false;
    // Müşteri listesi zaten canlı dinleniyor; belgeyi oradan al
    getLiveCollection('customers')
      .then((customersSnap) => {
        const snap = customersSnap.docs.find(d => d.id === customerId);
        if (cancelled || !snap) return;
        const data = snap.data();
        setMontajNote(data.montajNote || '');
        setMontajNoteMeta(data.montajNoteAt ? { at: data.montajNoteAt, by: data.montajNoteBy || '' } : null);
        setMontajNoteDirty(false);
        setKesifItems(Array.isArray(data.kesifItems) ? data.kesifItems : []);
      })
      .catch((err) => console.error('Montaj notu okunamadı:', err));
    return () => { cancelled = true; };
  }, [customerId]);

  // ---- Keşif ölçüleri (müşteri kaydında: kesifItems) ----
  // Keşifte oda oda girilen ürün tipi + ölçüler; "Siparişe dönüştür" sipariş formunu bu bilgilerle açar.
  const saveKesifItems = async (list) => {
    if (!customerId || customerId.startsWith('temp_')) return false;
    try {
      await updateDoc(doc(db, 'customers', customerId), { kesifItems: list, updatedAt: new Date() });
      setKesifItems(list);
      return true;
    } catch (error) {
      console.error('Keşif ölçüleri kaydedilemedi:', error);
      showToast('Keşif ölçüleri kaydedilemedi. Yetkiniz olmayabilir.');
      return false;
    }
  };

  const formatNoteDate = (value) => {
    const date = value?.toDate ? value.toDate() : new Date(value);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleSaveMontajNote = async () => {
    if (!customerId || customerId.startsWith('temp_')) return;
    const text = montajNote.trim();
    const now = new Date();
    const by = user?.email || user?.uid || '';
    setIsSavingMontajNote(true);
    try {
      await updateDoc(doc(db, 'customers', customerId), {
        montajNote: text,
        montajNoteAt: now,
        montajNoteBy: by,
        updatedAt: now
      });
      setMontajNote(text);
      setMontajNoteMeta({ at: now, by });
      setMontajNoteDirty(false);
      showToast('Montaj notu kaydedildi.', 'success');
    } catch (error) {
      console.error('Montaj notu kaydedilemedi:', error);
      showToast('Montaj notu kaydedilemedi. Yetkiniz olmayabilir.');
    } finally {
      setIsSavingMontajNote(false);
    }
  };

  // ---- Firmaya sipariş geçildi notları (siparişte: supplierOrders) ----
  // Firma seçilip WhatsApp ile gönderildiğinde siparişe not düşülür; not iptal edilebilir.
  const [supplierBusyId, setSupplierBusyId] = useState(null);

  const persistSupplierOrders = async (orderId, list) => {
    await updateDoc(doc(db, 'orders', orderId), { supplierOrders: list });
    setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, supplierOrders: list } : o)));
  };

  const canPersistEditingOrder = () => !!editingOrderId && !!customerId && !customerId.startsWith('temp_');

  const handleSupplierOrderSent = async (firmaInfo, part = 'urun') => {
    if (!firmaInfo) return;
    const entry = {
      id: `${Date.now()}-${part}`,
      part,
      firmaId: firmaInfo.id || '',
      firmaName: firmaInfo.name || '',
      sentAt: new Date().toISOString(),
      sentBy: user?.email || user?.uid || ''
    };
    const current = Array.isArray(newOrder.supplierOrders) ? newOrder.supplierOrders : [];
    // Aynı parça aynı firmaya yeniden gönderildiyse yalnızca tarih güncellenir
    const nextList = [...current.filter(e => !(e.part === part && e.firmaId === entry.firmaId)), entry];
    setNewOrder(prev => ({ ...prev, supplierOrders: nextList }));
    if (canPersistEditingOrder()) {
      try {
        await persistSupplierOrders(editingOrderId, nextList);
      } catch (error) {
        console.error('Sipariş notu kaydedilemedi:', error);
        showToast('Sipariş notu kaydedilemedi; siparişi kaydettiğinizde eklenecek.');
      }
    }
  };

  const handleSupplierOrderCancelInForm = async (entryId) => {
    const current = Array.isArray(newOrder.supplierOrders) ? newOrder.supplierOrders : [];
    const nextList = current.filter(e => e.id !== entryId);
    setNewOrder(prev => ({ ...prev, supplierOrders: nextList }));
    if (canPersistEditingOrder()) {
      setSupplierBusyId(entryId);
      try {
        await persistSupplierOrders(editingOrderId, nextList);
      } catch (error) {
        console.error('Sipariş notu iptal edilemedi:', error);
        showToast('Sipariş notu iptal edilemedi; siparişi kaydettiğinizde güncellenecek.');
      } finally {
        setSupplierBusyId(null);
      }
    }
  };

  // Listedeki "Firmaya geçildi" kutusu: elle işaretleme / işareti kaldırma
  const saveSupplierListFromList = async (order, nextList, busyKey, successMessage, errorMessage) => {
    if (editingOrderId === order.id) setNewOrder(prev => ({ ...prev, supplierOrders: nextList }));
    if (order.isTemp || !customerId || customerId.startsWith('temp_')) {
      setOrders(prev => prev.map(o => (o.id === order.id ? { ...o, supplierOrders: nextList } : o)));
      return;
    }
    setSupplierBusyId(busyKey);
    try {
      await persistSupplierOrders(order.id, nextList);
      showToast(successMessage, 'success');
    } catch (error) {
      console.error(errorMessage, error);
      showToast(`${errorMessage} Yetkiniz olmayabilir.`);
    } finally {
      setSupplierBusyId(null);
    }
  };

  const handleSupplierCheck = (order) => {
    const entry = {
      id: `${Date.now()}-manual`,
      part: 'urun',
      firmaId: '',
      firmaName: getOrderBrands(order).join(' + '), // Ürün Listesi'ndeki firma adı (varsa)
      sentAt: new Date().toISOString(),
      sentBy: user?.email || user?.uid || '',
      manual: true
    };
    return saveSupplierListFromList(order, [...(order.supplierOrders || []), entry], `check-${order.id}`,
      'Firmaya sipariş geçildi olarak işaretlendi.', 'İşaretlenemedi.');
  };

  const handleSupplierUncheck = (order) => saveSupplierListFromList(order, [], `check-${order.id}`,
    'Firmaya geçildi işareti kaldırıldı.', 'İşaret kaldırılamadı.');

  const hasSupplierOrder = (order) => Array.isArray(order.supplierOrders) && order.supplierOrders.length > 0;

  // Ürün adının altında: işaretliyse yalnızca firma adı görünür
  const renderSupplierCell = (order) => {
    const entries = hasSupplierOrder(order) ? order.supplierOrders : [];
    const firmaNames = [...new Set(entries.map(e => e.firmaName).filter(Boolean))];
    return (
      <div className="od-supplier-cell">
        <SupplierCheck
          checked={entries.length > 0}
          checkedLabel={firmaNames.join(', ') || 'Firmaya geçildi'}
          canEdit={!!permissions?.canEditOrder}
          busy={supplierBusyId === `check-${order.id}`}
          onCheck={() => handleSupplierCheck(order)}
          onUncheck={() => handleSupplierUncheck(order)}
        />
      </div>
    );
  };

  // 1. adım: müşteriyle WhatsApp sohbetini hazır mesajla aç (mesaj WhatsApp'ta elle gönderilir)
  const handleCustomerWhatsAppMessage = (kind) => {
    const phone = normalizePhoneForWhatsApp(customerInfo?.phone1);
    if (!phone) {
      showToast('Müşterinin kayıtlı telefon numarası bulunamadı!', 'error');
      return;
    }
    const name = customerInfo?.name || '';
    const formText = kind === 'teklif' ? 'Teklif formunuzu hazırladım' : 'Sipariş formunuz hazır';
    const message = `Merhaba ${name}, Ay Home Lotus'tan yazıyorum. ${formText}, birazdan bu sohbet üzerinden PDF olarak paylaşacağım. İyi günler dileriz.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const [newOrder, setNewOrder] = useState({
    roomName: '',
    productName: '',
    productVariant: '',
    width: '',
    height: '',
    pileModel: '',
    pileDensity: '',
    usedM2: '',
    unitPrice: '',
    extraPrice: '',
    manualExtraCost: '',
    capsuleUnitPrice: '',
    capsuleUnitCost: '',
    capsulePrice: '',
    capsuleCost: '',
    jumboSystem: '',
    reductor: '',
    bottomSkirt: '',
    closedCase: '',
    laser: '',
    mounting: 0,
    motorType: '',
    motorPrice: '',
    motorPriceCurrency: '',
    motorCost: 0,
    kumandaPriceCurrency: '',
    isJumbo: false,
    hasAltEtekDilim: false,
    hasAltEtekKatlama: false,
    hasOzelOlcu: false,
    isKapaliKasa: false,
    // Ahşap Jaluzi
    sistemTipi: '',
    hasZigil: false,
    zigilAmount: 0,
    zigilCostAmount: 0,
    hasReduktor: false,
    hasKurdela: false,
    baseUnitPrice: '',
    baseUnitCost: '',
    // Metal Jaluzi
    hasMetalZigil: false,
    metalZigilAmount: 0,
    metalZigilCostAmount: 0,
    hasMetalReduktor: false,
    hasIkiCamArasi: false,
    ikiCamArasiAmount: 0,
    ikiCamArasiCostAmount: 0,
    // Stor/Zebra Ek Özellikler
    direction: '',
    hasZebraJumbo: false,
    hasZebraAltEtekDilim: false,
    hasZebraKapaliKasa: false,
    zebraJumboAmount: 0,
    zebraJumboCostAmount: 0,
    zebraAltEtekDilimAmount: 0,
    zebraAltEtekDilimCostAmount: 0,
    zebraKapaliKasaAmount: 0,
    zebraKapaliKasaCostAmount: 0,
    // Stor Alanları
    hasStorJumbo: false,
    storJumboAmount: 0,
    storJumboCostAmount: 0,
    hasStorAltEtekDilim: false,
    storAltEtekDilimAmount: 0,
    storAltEtekDilimCostAmount: 0,
    hasStorKapaliKasa: false,
    storKapaliKasaAmount: 0,
    storKapaliKasaCostAmount: 0,
    hasStorAltEtekLazer: false,
    storAltEtekLazerAmount: 0,
    storAltEtekLazerCostAmount: 0,
    hasStorDekoratifLama: false,
    storDekoratifLamaAmount: 0,
    storDekoratifLamaCostAmount: 0,
    hasStorDekoratifKasa: false,
    storDekoratifKasaAmount: 0,
    storDekoratifKasaCostAmount: 0,
    hasStorAltEtekBeyazIs: false,
    storAltEtekBeyazIsAmount: 0,
    storAltEtekBeyazIsCostAmount: 0,
    // Ahşap Jaluzi
    sistemTipi: '',
    hasZigil: false,
    zigilAmount: 0,
    hasReduktor: false,
    hasKurdela: false,
    baseUnitPrice: '',
    // Metal Jaluzi
    hasMetalZigil: false,
    metalZigilAmount: 0,
    hasMetalReduktor: false,
    hasIkiCamArasi: false,
    ikiCamArasiAmount: 0,
    // Plise Özel
    pliseItems: [],
    pliseProduct1Name: '',
    pliseProduct1Price: '',
    pliseProduct2Name: '',
    pliseProduct2Price: '',
    pliseProduct1Variation: '',
    pliseProduct2Variation: '',
    pliseCaseVariation: '',
    // Common / Generic
    isJumbo: false,
    hasAltEtekDilim: false,
    isKapaliKasa: false,
    // Maliyet Bilgileri
    unitCost: '',
    pliseProduct1Cost: '',
    pliseProduct2Cost: '',
    mountingCost: '',
    totalCost: '0.00',
    hasOzelTasarim: false,
    ozelTasarimProductName: '',
    ozelTasarimProductVariant: '',
    ozelTasarimWidth: '',
    ozelTasarimHeight: '',
    ozelTasarimUnitPrice: '',
    ozelTasarimUnitCost: '',
    ozelTasarimBaseUnitPrice: '',
    ozelTasarimBaseUnitCost: '',
    ozelTasarimPileModel: '',
    ozelTasarimPileDensity: '',
    ozelTasarimUsedM2: '',
    ozelTasarimExtraPrice: '',
    ozelTasarimManualExtraCost: '',
    ozelTasarimQuantity: '',
    ozelTasarimCapsuleUnitPrice: '',
    ozelTasarimCapsuleUnitCost: '',
    ozelTasarimCapsulePrice: '',
    ozelTasarimCapsuleCost: '',
    kanatAccessories: [],
    ozelProjeKumasEni: '',
    ozelProjeTopBoyu: '',
    ozelProjeRapor: '',
    ozelProjeBoyPayi: '',
    hasOzelProje: false,
    ...emptyStorZebraExtraOrderFields()
  });

  // Siparişleri Firebase'den çek
  const fetchOrders = useCallback(async () => {
    if (!customerId || customerId.startsWith('temp_')) return;

    try {
      // Montajcı yalnızca bu müşterinin siparişlerini okur; diğerleri paylaşılan canlı listeden filtreler (ek okuma yok)
      const querySnapshot = isMontajci
        ? await getDocs(query(collection(db, 'orders'), where('customerId', '==', customerId)))
        : await getLiveCollection('orders');
      const ordersData = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(order => order.customerId === customerId);

      // Client-side'da tarihe göre sırala (en yeni en üstte)
      ordersData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
        return dateB.getTime() - dateA.getTime(); // Descending order
      });

      setOrders(ordersData);
    } catch (error) {
      console.error('Siparişler yüklenirken hata:', error);
    }
  }, [customerId, isMontajci]);

  // Component mount olduğunda siparişleri çek
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Ayarları ve Kurları Çek
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docSnap = await getLiveDoc('settings/global');
        if (docSnap.exists()) {
          setAdminSettings(docSnap.data());
        }
      } catch (err) { }
    };
    fetchSettings();

    const fetchRates = async () => {
      try {
        const res = await fetch('https://open.er-api.com/v6/latest/USD');
        const data = await res.json();
        if (data && data.rates && data.rates.TRY && data.rates.EUR) {
          const usdToTry = data.rates.TRY;
          const eurToTry = data.rates.TRY / data.rates.EUR;
          setExchangeRates({ USD: usdToTry.toFixed(2), EUR: eurToTry.toFixed(2) });
        }
      } catch (err) { }
    };
    fetchRates();
  }, []);


  // Pile modellerini sadece constants'tan al (Firestore'dan çekme)
  useEffect(() => {
    // Sadece constants'taki modelleri kullan - Tül ve Kanat için
    const tulModels = Object.entries(TUL_PILE_MODELS).map(([key, modelName]) => ({
      id: `const_tul_${key}`,
      modelName: modelName,
      density: calculatePileDensity(modelName)
    }));
    const kanatModels = Object.entries(KANAT_PILE_MODELS).map(([key, modelName]) => ({
      id: `const_kanat_${key}`,
      modelName: modelName,
      density: calculatePileDensity(modelName)
    }));
    // Her iki tipi birleştir (tekrarları kaldır)
    const allModels = [...tulModels];
    kanatModels.forEach(km => {
      if (!allModels.find(m => m.modelName === km.modelName)) {
        allModels.push(km);
      }
    });
    setPileModels(allModels);
  }, []);

  // Modal açıldığında edit-customer-section panel'ini yukarı scroll et
  useEffect(() => {
    if (deleteConfirm || cancelPaymentConfirm || showSystemConfirm) {
      const panel = document.querySelector('.edit-customer-section');
      if (panel) {
        panel.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [deleteConfirm, cancelPaymentConfirm, showSystemConfirm]);

  // Müşteri bilgilerinden vadeli yüzde ve genel iskonto bilgisini yükle
  useEffect(() => {
    if (customerInfo) {
      setVadeliYuzde(customerInfo.vadeliYuzde || '');
      setGenelIskontoYuzde(customerInfo.genelIskontoYuzde || '');
      setManualGrandTotal(customerInfo.manualGrandTotal !== undefined && customerInfo.manualGrandTotal !== null ? customerInfo.manualGrandTotal.toString() : '');
    }
  }, [customerInfo]);


  // Müşteri bilgilerinden ödeme bilgilerini yükle
  useEffect(() => {
    if (customerInfo) {
      // paidAmount number ise string'e çevir
      const paidAmountStr = customerInfo.paidAmount !== undefined && customerInfo.paidAmount !== null
        ? (typeof customerInfo.paidAmount === 'number' ? customerInfo.paidAmount.toString() : customerInfo.paidAmount.toString())
        : '';
      setPaidAmount(paidAmountStr);
      setIsPaymentCompleted(customerInfo.isPaymentCompleted || false);
      // Ödeme geçmişini yükle
      setPaymentHistory(customerInfo.paymentHistory || []);
    }
  }, [customerInfo]);

  // Vadeli yüzde değiştiğinde parent'a bildir
  const handleVadeliYuzdeChange = (value) => {
    let val = value;
    if (val !== '') {
      const num = parseFloat(val);
      if (num > 100) val = '100';
      if (num < 0) val = '0';
    }
    setVadeliYuzde(val);
    // Vade farkı değiştiğinde manuel genel toplam sıfırlanır (hesaplama otomatiğe döner)
    setManualGrandTotal('');
    // Yeni müşteri ekleme modunda parent'a bildir
    if (onVadeliYuzdeChange) {
      onVadeliYuzdeChange(val);
    }
  };

  // Genel iskonto yüzde değiştiğinde parent'a bildir
  const handleGenelIskontoYuzdeChange = (value) => {
    let val = value;
    if (val !== '') {
      const num = parseFloat(val);
      if (num > 100) val = '100';
      if (num < 0) val = '0';
    }
    setGenelIskontoYuzde(val);
    // Genel iskonto değiştiğinde manuel genel toplam sıfırlanır (hesaplama otomatiğe döner)
    setManualGrandTotal('');
    // Yeni müşteri ekleme modunda parent'a bildir
    if (onGenelIskontoYuzdeChange) {
      onGenelIskontoYuzdeChange(val);
    }
  };

  // Genel Toplam (Net) elle değiştirildiğinde: aktif duruma göre vade farkı veya genel iskonto oranını geri hesapla
  // (AyHome_App'teki handleManuelToplamOdemeChange ile aynı mantık; oranlar Ara Toplam üzerinden hesaplanır)
  const handleManualGrandTotalChange = (value) => {
    setManualGrandTotal(value);
    if (!value || value === '') {
      // Boşaltılırsa hesaplamayı otomatiğe bırak
      return;
    }
    const enteredTotal = parseFloat(value);
    // totalAmount (Ara Toplam) aşağıda hesaplanır; olay anında güncel değeri kullanılır
    if (isNaN(enteredTotal) || totalAmount <= 0) return;

    const aktifVade = parseFloat(vadeliYuzde) || 0;
    let newVadeli = vadeliYuzde;
    let newIskonto = genelIskontoYuzde;

    if (aktifVade > 0 || enteredTotal > totalAmount) {
      if (enteredTotal > totalAmount) {
        // Vade senaryosu: vade% = (girilen / ara toplam - 1) * 100, iskonto sıfırlanır
        newVadeli = parseFloat(((enteredTotal / totalAmount - 1) * 100).toFixed(6)).toString();
        newIskonto = '';
      } else {
        // Vade aktifken tutar ara toplama eşit veya altına indirildi
        newVadeli = '';
        newIskonto = enteredTotal < totalAmount
          ? parseFloat(((1 - enteredTotal / totalAmount) * 100).toFixed(6)).toString()
          : '';
      }
    } else {
      // İskonto senaryosu: iskonto% = (1 - girilen / ara toplam) * 100
      newIskonto = enteredTotal < totalAmount
        ? parseFloat(((1 - enteredTotal / totalAmount) * 100).toFixed(6)).toString()
        : '';
    }

    setVadeliYuzde(newVadeli);
    setGenelIskontoYuzde(newIskonto);
    // Yeni müşteri ekleme modunda güncellenen oranları parent'a bildir (kaydedilebilmeleri için)
    if (onVadeliYuzdeChange && newVadeli !== vadeliYuzde) onVadeliYuzdeChange(newVadeli);
    if (onGenelIskontoYuzdeChange && newIskonto !== genelIskontoYuzde) onGenelIskontoYuzdeChange(newIskonto);
  };

  // Plise Batch Hesaplama Yardımcı Fonksiyonu
  const calculatePliseBatchTotals = (items = [], p1Price = 0, p2Price = 0, mounting = 0, discount = 0, globalQty = 1, extraPrice = 0, p1Cost = 0, p2Cost = 0, extraCost = 0, mountCost = 0) => {
    const gQty = parseFloat(globalQty) || 1;
    const p1 = parseFloat(p1Price) || 0;
    const p2 = parseFloat(p2Price) || 0;
    const mount = parseFloat(mounting) || 0;
    const disc = parseFloat(discount) || 0;
    const extra = parseFloat(extraPrice) || 0;
    const cost1 = parseFloat(p1Cost) || 0;
    const cost2 = parseFloat(p2Cost) || 0;
    const manualExtraCost = parseFloat(extraCost) || 0;
    const mountingCost = parseFloat(mountCost) || 0;

    let totalRowsSum = 0;
    let totalRowsCostSum = 0;

    const updatedItems = items.map(item => {
      const m2 = calculatePliseUsedM2(item.width, item.height);
      const qty = parseFloat(item.quantity) || 1;
      
      const unitPrice = p1 + (item.multisistem ? p2 : 0);
      const rowTotal = m2 * unitPrice * qty;
      totalRowsSum += rowTotal;

      const unitCostRow = cost1 + (item.multisistem ? cost2 : 0);
      const rowCost = m2 * unitCostRow * qty;
      totalRowsCostSum += rowCost;

      return {
        ...item,
        usedM2: m2.toFixed(2),
        rowTotal: rowTotal.toFixed(2)
      };
    });

    const finalTotal = (totalRowsSum + mount + extra) * gQty;
    const totalPrice = finalTotal.toFixed(2);
    
    const finalCost = (totalRowsCostSum + manualExtraCost + mountingCost) * gQty;
    const totalCost = isNaN(finalCost) ? "0.00" : finalCost.toFixed(2);

    let discountedPrice = null;
    if (disc > 0) {
      discountedPrice = calculateDiscountedPrice(finalTotal, disc).toFixed(2);
    }

    return {
      items: updatedItems,
      totalPrice,
      discountedPrice,
      totalCost
    };
  };

  // Düzenleme modunda fiyat bilgilerini (vade/iskonto) kaydet
  const handleSavePricingFields = async () => {
    if (!customerId || customerId.startsWith('temp_')) {
      return;
    }

    try {
      setIsSavingVadeli(true);
      await updateDoc(doc(db, 'customers', customerId), {
        vadeliYuzde: vadeliYuzde,
        genelIskontoYuzde: genelIskontoYuzde,
        manualGrandTotal: manualGrandTotal === '' ? null : parseFloat(manualGrandTotal),
        updatedAt: new Date()
      });

      // Başarılı kaydetme bildirimini parent'a gönder
      if (onSaveVadeliSuccess) {
        onSaveVadeliSuccess({
          vadeliYuzde,
          genelIskontoYuzde,
          manualGrandTotal: manualGrandTotal === '' ? null : parseFloat(manualGrandTotal)
        });
      }
      showToast('Fiyat bilgileri başarıyla kaydedildi.', 'success');
    } catch (error) {
      console.error('Fiyat bilgileri kaydedilirken hata:', error);
      showToast('Fiyat bilgileri kaydedilirken bir hata oluştu.');
    } finally {
      setIsSavingVadeli(false);
    }
  };

  // OrderStatus değiştiğinde combobox'ı güncelle
  useEffect(() => {
    // Bu useEffect orderStatus prop'u değiştiğinde çalışacak
    // Combobox otomatik olarak güncellenecek çünkü value={orderStatus} kullanıyoruz
  }, [orderStatus]);

  // Kopyalama sonrası hesaplamaları tetikle
  useEffect(() => {
    if (isAddingOrder && (selectedProductType === 'tul' || selectedProductType === 'kanat') && newOrder.pileModel) {
      // Kopyalama sonrası hesaplamaları yap
      const updatedOrder = { ...newOrder };

      // Pile sıklığını hesapla
      if (newOrder.pileModel) {
        // Önce Firestore'dan pile sıklığını al
        const selectedModel = pileModels.find(m => m.modelName === newOrder.pileModel);
        let pileDensity = 0;
        if (selectedModel) {
          pileDensity = selectedModel.density;
        } else {
          // Fallback: Eski calculatePileDensity fonksiyonunu kullan
          pileDensity = calculatePileDensity(newOrder.pileModel);
        }
        updatedOrder.pileDensity = pileDensity.toString();
      }

      // Kullanılan mt2'yi hesapla
      if (newOrder.width && newOrder.height && newOrder.pileModel) {
        const usedM2 = calculateUsedM2(newOrder.width, newOrder.height, newOrder.pileModel);
        updatedOrder.usedM2 = usedM2.toFixed(2);
      }

      // Kapsül Pile için kapsül ücretini hesapla
      if (updatedOrder.pileModel === 'Kapsül Pile' && updatedOrder.usedM2) {
        if (updatedOrder.capsuleUnitPrice) {
          const capsulePrice = calculateCapsulePrice(updatedOrder.usedM2, updatedOrder.capsuleUnitPrice);
          updatedOrder.capsulePrice = capsulePrice.toFixed(2);
        }

        if (updatedOrder.capsuleUnitCost) {
          const capsuleCost = calculateCapsuleCost(updatedOrder.usedM2, updatedOrder.capsuleUnitCost);
          updatedOrder.capsuleCost = capsuleCost.toFixed(2);
        }
      }

      // Özel Tasarım için kopyalama sonrası hesaplamaları yap
      if (selectedProductType === 'kanat' && updatedOrder.hasOzelTasarim && updatedOrder.ozelTasarimPileModel) {
        const selectedModel = pileModels.find(m => m.modelName === updatedOrder.ozelTasarimPileModel);
        let pileDensity = 0;
        if (selectedModel) {
          pileDensity = selectedModel.density;
        } else {
          pileDensity = calculatePileDensity(updatedOrder.ozelTasarimPileModel);
        }
        updatedOrder.ozelTasarimPileDensity = pileDensity.toString();

        if (updatedOrder.ozelTasarimWidth && updatedOrder.ozelTasarimHeight) {
          const usedM2 = calculateUsedM2(updatedOrder.ozelTasarimWidth, updatedOrder.ozelTasarimHeight, updatedOrder.ozelTasarimPileModel);
          updatedOrder.ozelTasarimUsedM2 = usedM2.toFixed(2);
        }

        if (updatedOrder.ozelTasarimPileModel === 'Kapsül Pile' && updatedOrder.ozelTasarimUsedM2) {
          if (updatedOrder.ozelTasarimCapsuleUnitPrice) {
            const capsulePrice = calculateCapsulePrice(updatedOrder.ozelTasarimUsedM2, updatedOrder.ozelTasarimCapsuleUnitPrice);
            updatedOrder.ozelTasarimCapsulePrice = capsulePrice.toFixed(2);
          }
          if (updatedOrder.ozelTasarimCapsuleUnitCost) {
            const capsuleCost = calculateCapsuleCost(updatedOrder.ozelTasarimUsedM2, updatedOrder.ozelTasarimCapsuleUnitCost);
            updatedOrder.ozelTasarimCapsuleCost = capsuleCost.toFixed(2);
          }
        }
      }

      // Toplam tutarı hesapla
      let totalPriceMain = 0;
      if (updatedOrder.usedM2 && updatedOrder.unitPrice) {
        const baseTotalPrice = calculateTotalPrice(
          updatedOrder.usedM2,
          updatedOrder.unitPrice,
          updatedOrder.extraPrice,
          updatedOrder.pileModel,
          updatedOrder.capsulePrice
        );
        const quantity = parseFloat(updatedOrder.quantity) || 1;
        totalPriceMain = baseTotalPrice * quantity;
      }

      let totalPriceOzel = 0;
      if (selectedProductType === 'kanat' && updatedOrder.hasOzelTasarim && updatedOrder.ozelTasarimUsedM2 && updatedOrder.ozelTasarimUnitPrice) {
        const baseOzelPrice = calculateTotalPrice(
          updatedOrder.ozelTasarimUsedM2,
          updatedOrder.ozelTasarimUnitPrice,
          updatedOrder.ozelTasarimExtraPrice,
          updatedOrder.ozelTasarimPileModel,
          updatedOrder.ozelTasarimCapsulePrice
        );
        const ozelQuantity = parseFloat(updatedOrder.ozelTasarimQuantity) || 1;
        totalPriceOzel = baseOzelPrice * ozelQuantity;
      }

      let totalPriceAstar = 0;
      if (selectedProductType === 'kanat' && updatedOrder.hasAstar && updatedOrder.usedM2 && updatedOrder.astarUnitPrice) {
        totalPriceAstar = calculateAstarPrice(updatedOrder.usedM2, updatedOrder.astarUnitPrice, updatedOrder.quantity);
        updatedOrder.astarPrice = totalPriceAstar.toFixed(2);
      }

      let totalPriceAccessories = 0;
      if ((selectedProductType === 'kanat' || selectedProductType === 'tul') && Array.isArray(updatedOrder.kanatAccessories)) {
        totalPriceAccessories = updatedOrder.kanatAccessories.reduce((sum, acc) => sum + (parseFloat(acc.total) || 0), 0);
      }

      const overallTotalPrice = totalPriceMain + totalPriceOzel + totalPriceAstar + totalPriceAccessories;
      updatedOrder.totalPrice = overallTotalPrice.toFixed(2);

      // İndirimli tutarı hesapla
      if (updatedOrder.totalPrice && updatedOrder.discount) {
        const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
        updatedOrder.discountedPrice = discountedPrice.toFixed(2);
      }

      setNewOrder(updatedOrder);
    }

    // Ahşap Jaluzi ve Metal Jaluzi için kopyalama sonrası hesaplamaları tetikle
    if (isAddingOrder && (selectedProductType === 'ahsapJaluzi' || selectedProductType === 'metalJaluzi') && newOrder.width && newOrder.height) {
      // Kopyalama sonrası hesaplamaları yap
      const updatedOrder = { ...newOrder };

      // Kullanılan mt2'yi hesapla
      if (newOrder.width && newOrder.height) {
        const usedM2 = calculateJaluziUsedM2(newOrder.width, newOrder.height);
        updatedOrder.usedM2 = usedM2.toFixed(2);
      }

      // Toplam tutarı hesapla - adet dahil et
      if (updatedOrder.usedM2 && updatedOrder.unitPrice) {
        const totalPrice = calculateJaluziTotalPrice(
          updatedOrder.usedM2,
          updatedOrder.unitPrice,
          updatedOrder.mounting,
          updatedOrder.motorPrice,
          (parseFloat(updatedOrder.zigilAmount) || 0) + (parseFloat(updatedOrder.metalZigilAmount) || 0),
          parseFloat(updatedOrder.ikiCamArasiAmount) || 0
        );
        // Adet ile çarp
        const quantity = parseFloat(updatedOrder.quantity) || 1;
        updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);
      }

      // İndirimli tutarı hesapla
      if (updatedOrder.totalPrice && updatedOrder.discount) {
        const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
        updatedOrder.discountedPrice = discountedPrice.toFixed(2);
      }

      setNewOrder(updatedOrder);
    }

    // Dikey Perde için kopyalama sonrası hesaplamaları tetikle
    if (isAddingOrder && selectedProductType === 'dikeyPerde' && newOrder.width && newOrder.height) {
      // Kopyalama sonrası hesaplamaları yap
      const updatedOrder = { ...newOrder };

      // Kullanılan mt2'yi hesapla
      if (newOrder.width && newOrder.height) {
        const usedM2 = calculateJaluziUsedM2(newOrder.width, newOrder.height);
        updatedOrder.usedM2 = usedM2.toFixed(2);
      }

      // Toplam tutarı hesapla - adet dahil et
      if (updatedOrder.usedM2 && updatedOrder.unitPrice) {
        const totalPrice = calculateDikeyPerdeTotalPrice(
          updatedOrder.usedM2,
          updatedOrder.unitPrice,
          0, // Fon ücreti zaten birim fiyata dahil ediliyor, burada 0 geçiyoruz
          updatedOrder.mounting,
          parseFloat(updatedOrder.motorPrice) || parseFloat(updatedOrder.motorPriceTL) || 0
        );
        // Adet ile çarp
        const quantity = parseFloat(updatedOrder.quantity) || 1;
        updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);
      }

      // İndirimli tutarı hesapla
      if (updatedOrder.totalPrice && updatedOrder.discount) {
        const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
        updatedOrder.discountedPrice = discountedPrice.toFixed(2);
      }

      setNewOrder(updatedOrder);
    }

    // Dikey PVC ve Dikey Stor için kopyalama sonrası hesaplamaları tetikle
    if (isAddingOrder && (selectedProductType === 'dikeyPvc' || selectedProductType === 'dikeyStor') && newOrder.width && newOrder.height) {
      // Kopyalama sonrası hesaplamaları yap
      const updatedOrder = { ...newOrder };

      // Kullanılan mt2'yi hesapla
      if (newOrder.width && newOrder.height) {
        const usedM2 = calculateJaluziUsedM2(newOrder.width, newOrder.height);
        updatedOrder.usedM2 = usedM2.toFixed(2);
      }

      // Toplam tutarı hesapla
      if (updatedOrder.usedM2 && updatedOrder.unitPrice) {
        const totalPrice = calculateJaluziTotalPrice(
          updatedOrder.usedM2,
          updatedOrder.unitPrice,
          updatedOrder.mounting,
          updatedOrder.motorPrice,
          (parseFloat(updatedOrder.zigilAmount) || 0) + (parseFloat(updatedOrder.metalZigilAmount) || 0),
          parseFloat(updatedOrder.ikiCamArasiAmount) || 0
        );
        // Adet ile çarp
        const quantity = parseFloat(updatedOrder.quantity) || 1;
        updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);
      }

      // İndirimli tutarı hesapla
      if (updatedOrder.totalPrice && updatedOrder.discount) {
        const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
        updatedOrder.discountedPrice = discountedPrice.toFixed(2);
      }

      setNewOrder(updatedOrder);
    }

    // Plise Major Refactor - Kopyalama/Yükleme sonrası batch hesaplamaları tetikle
    if (isAddingOrder && selectedProductType === 'plise') {
      const { items, totalPrice, discountedPrice, totalCost } = calculatePliseBatchTotals(
        newOrder.pliseItems,
        newOrder.pliseProduct1Price,
        newOrder.pliseProduct2Price,
        newOrder.mounting,
        newOrder.discount,
        newOrder.quantity,
        newOrder.extraPrice,
        newOrder.unitCost,
        newOrder.manualExtraCost,
        newOrder.mountingCost
      );

      // Sadece değerler gerçekten farklıysa state güncelle (Sonsuz döngüyü engellemek için)
      if (totalPrice !== newOrder.totalPrice || items.length !== (newOrder.pliseItems?.length || 0) || totalCost !== newOrder.totalCost) {
        setNewOrder(prev => ({
          ...prev,
          pliseItems: items,
          totalPrice,
          discountedPrice,
          totalCost
        }));
      }
    }

    // Aksesuar için kopyalama sonrası hesaplamaları tetikle
    if (isAddingOrder && selectedProductType === 'aksesuar' && newOrder.unitPrice && newOrder.quantity) {
      // Kopyalama sonrası hesaplamaları yap
      const updatedOrder = { ...newOrder };

      // Toplam tutarı hesapla
      if (newOrder.unitPrice && newOrder.quantity) {
        const totalPrice = calculateAksesuarTotalPrice(newOrder.unitPrice, newOrder.quantity, newOrder.usedMeter, newOrder.aksesuarBirimTipi);
        updatedOrder.totalPrice = totalPrice.toFixed(2);
      }

      // İndirimli tutarı hesapla
      if (updatedOrder.totalPrice && updatedOrder.discount) {
        const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
        updatedOrder.discountedPrice = discountedPrice.toFixed(2);
      }

      setNewOrder(updatedOrder);
    }
  }, [isAddingOrder, selectedProductType, newOrder.pileModel, newOrder.width, newOrder.height, newOrder.unitPrice, newOrder.pliseProduct1Price, newOrder.pliseProduct2Price, newOrder.pliseItems, newOrder.mounting, newOrder.discount, newOrder.extraPrice, newOrder.unitCost, newOrder.manualExtraCost, newOrder.mountingCost]);

  // Ürün tipi değiştiğinde alanları güncelle
  const handleProductTypeChange = (productType) => {
    setSelectedProductType(productType);
    setNewOrder({
      roomName: '',
      productName: '',
      productVariant: '',
      width: '',
      height: '',
      pileModel: '',
      pileDensity: '',
      usedM2: '',
      unitPrice: '',
      extraPrice: '',
      capsuleUnitPrice: '',
      capsulePrice: '',
      jumboSystem: '',
      reductor: '',
      bottomSkirt: '',
      closedCase: '',
      laser: '',
      mounting: '',
      backgroundCurtain: '',
      pliseProduct1Name: '',
      pliseProduct1Price: '',
      pliseProduct1Variation: '',
      pliseProduct2Name: '',
      pliseProduct2Price: '',
      pliseProduct2Variation: '',
      fabricPrice: '',
      mechanismType: '',
      mechanismPrice: '',
      accessoryPrice: '',
      quantity: '',
      discount: '',
      totalPrice: '',
      discountedPrice: '',
      notes: '',
      olcuTamamlama: '',
      usedMeter: '',
      // Stor Alanları
      hasStorJumbo: false,
      storJumboAmount: 0,
      storJumboCostAmount: 0,
      hasStorAltEtekDilim: false,
      storAltEtekDilimAmount: 0,
      storAltEtekDilimCostAmount: 0,
      hasAltEtekKatlama: false,
      hasStorKapaliKasa: false,
      storKapaliKasaAmount: 0,
      storKapaliKasaCostAmount: 0,
      hasStorAltEtekLazer: false,
      storAltEtekLazerAmount: 0,
      storAltEtekLazerCostAmount: 0,
      hasStorDekoratifLama: false,
      storDekoratifLamaAmount: 0,
      storDekoratifLamaCostAmount: 0,
      hasStorDekoratifKasa: false,
      storDekoratifKasaAmount: 0,
      storDekoratifKasaCostAmount: 0,
      hasStorAltEtekBeyazIs: false,
      storAltEtekBeyazIsAmount: 0,
      storAltEtekBeyazIsCostAmount: 0,
      // Zebra Alanları
      hasZebraJumbo: false,
      zebraJumboAmount: 0,
      zebraJumboCostAmount: 0,
      hasZebraAltEtekDilim: false,
      zebraAltEtekDilimAmount: 0,
      zebraAltEtekDilimCostAmount: 0,
      hasZebraKapaliKasa: false,
      zebraKapaliKasaAmount: 0,
      zebraKapaliKasaCostAmount: 0,
      // Ahşap Jaluzi
      sistemTipi: '',
      hasZigil: false,
      zigilAmount: 0,
      zigilCostAmount: 0,
      hasReduktor: false,
      hasKurdela: false,
      baseUnitPrice: '',
      baseUnitCost: '',
      // Metal Jaluzi
      hasMetalZigil: false,
      metalZigilAmount: 0,
      metalZigilCostAmount: 0,
      hasMetalReduktor: false,
      hasIkiCamArasi: false,
      ikiCamArasiAmount: 0,
      ikiCamArasiCostAmount: 0,
      // Common / Generic
      direction: '',
      isJumbo: false,
      hasAltEtekDilim: false,
      hasAltEtekKatlama: false,
      hasOzelOlcu: false,
      isKapaliKasa: false,
      // Rustik Alanları
      usedCm: '',
      ayakModel: '',
      ayakCap: '',
      ayakUzunluk: '',
      ayakRenkKodu: '',
      ayakSayisi: '',
      ekAyakSayisi: '',
      ayakUcreti: '',
      rustikAksesuarlar: [],
      rustikRaySecenekleri: [],
      mountingCost: '',
      manualExtraCost: '',
      unitCost: '',
      totalCost: '0.00',
      hasOzelTasarim: false,
      ozelTasarimProductName: '',
      ozelTasarimProductVariant: '',
      ozelTasarimWidth: '',
      ozelTasarimHeight: '',
      ozelTasarimUnitPrice: '',
      ozelTasarimUnitCost: '',
      ozelTasarimBaseUnitPrice: '',
      ozelTasarimBaseUnitCost: '',
      ozelTasarimPileModel: '',
      ozelTasarimPileDensity: '',
      ozelTasarimUsedM2: '',
      ozelTasarimExtraPrice: '',
      ozelTasarimManualExtraCost: '',
      ozelTasarimQuantity: '',
      ozelTasarimCapsuleUnitPrice: '',
      ozelTasarimCapsuleUnitCost: '',
      ozelTasarimCapsulePrice: '',
      ozelTasarimCapsuleCost: '',
      kanatAccessories: [],
      ozelProjeKumasEni: '',
      ozelProjeTopBoyu: '',
      ozelProjeRapor: '',
      ozelProjeBoyPayi: '',
      hasOzelProje: false,
      ...emptyStorZebraExtraOrderFields()
    });
  };

  // Alan değişikliklerinde hesaplamaları yap
  const handleFieldChange = (field, value) => {
    setNewOrder(prevOrder => {
      const updatedOrder = { ...prevOrder, [field]: value };

      // Motor seçimi Boş'a çekilirse motor ücretini ve kurunu sıfırla
      if (field === 'motorType' && !value) {
        updatedOrder.motorPrice = 0;
        updatedOrder.motorPriceCurrency = '';
      }
      
      // Montaj ücreti silinirse maliyetini de sıfırla
      if (field === 'mounting' && (!value || parseFloat(value) === 0)) {
        updatedOrder.mountingCost = '';
      }

      // Ek ücret silinirse maliyetini de sıfırla
      if (field === 'extraPrice' && (!value || parseFloat(value) === 0)) {
        updatedOrder.manualExtraCost = '';
      }

      // Tül ve Kanat için özel hesaplamalar
      if (selectedProductType === 'tul' || selectedProductType === 'kanat') {
        // Pile modeli değiştiğinde pile sıklığını hesapla
        if (field === 'pileModel') {
          // Önce pileModels state'inden pile sıklığını al
          let pileDensity = 0;
          if (pileModels && pileModels.length > 0) {
            const selectedModel = pileModels.find(m => m.modelName === value);
            if (selectedModel && selectedModel.density) {
              pileDensity = selectedModel.density;
            } else {
              // Fallback: calculatePileDensity fonksiyonunu kullan
              pileDensity = calculatePileDensity(value);
            }
          } else {
            // pileModels boşsa direkt calculatePileDensity kullan
            pileDensity = calculatePileDensity(value);
          }

          updatedOrder.pileDensity = pileDensity.toString();
          updatedOrder.pileModel = value; // Pile modelini güncelle

          // Pile modeli değiştiğinde kullanılan mt2'yi hesapla
          if (updatedOrder.width && updatedOrder.height) {
            // Pile sıklığını kullan (eğer varsa)
            const density = parseFloat(updatedOrder.pileDensity) || 0;
            const usedM2 = calculateUsedM2(updatedOrder.width, updatedOrder.height, value, density > 0 ? density : null, updatedOrder.hasOzelOlcu);
            updatedOrder.usedM2 = usedM2.toFixed(2);

            if (value === 'Kapsül Pile') {
              if (updatedOrder.capsuleUnitPrice) {
                const capsulePrice = calculateCapsulePrice(updatedOrder.usedM2, updatedOrder.capsuleUnitPrice);
                updatedOrder.capsulePrice = capsulePrice.toFixed(2);
              }
              if (updatedOrder.capsuleUnitCost) {
                const capsuleCost = calculateCapsuleCost(updatedOrder.usedM2, updatedOrder.capsuleUnitCost);
                updatedOrder.capsuleCost = capsuleCost.toFixed(2);
              }
            }

            // Toplam tutarı hesapla - mevcut adet değerini koru
            if (updatedOrder.unitPrice) {
              const totalPrice = calculateTotalPrice(
                updatedOrder.usedM2,
                updatedOrder.unitPrice,
                updatedOrder.extraPrice,
                updatedOrder.pileModel,
                updatedOrder.capsulePrice
              );
              // Mevcut adet değerini koru - updatedOrder'dan al
              const quantity = parseFloat(updatedOrder.quantity) || 1;
              updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);

              // İndirimli tutarı hesapla
              const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
              updatedOrder.discountedPrice = discountedPrice.toFixed(2);
            }
          }
        }

        // Pile sıklığı değiştiğinde kullanılan mt2'yi yeniden hesapla
        if (field === 'pileDensity' && updatedOrder.pileModel && updatedOrder.width && updatedOrder.height) {
          const density = parseFloat(value) || 0;
          if (density > 0) {
            const usedM2 = calculateUsedM2(updatedOrder.width, updatedOrder.height, updatedOrder.pileModel, density, updatedOrder.hasOzelOlcu);
            updatedOrder.usedM2 = usedM2.toFixed(2);

            if (updatedOrder.pileModel === 'Kapsül Pile') {
              if (updatedOrder.capsuleUnitPrice) {
                const capsulePrice = calculateCapsulePrice(updatedOrder.usedM2, updatedOrder.capsuleUnitPrice);
                updatedOrder.capsulePrice = capsulePrice.toFixed(2);
              }
              if (updatedOrder.capsuleUnitCost) {
                const capsuleCost = calculateCapsuleCost(updatedOrder.usedM2, updatedOrder.capsuleUnitCost);
                updatedOrder.capsuleCost = capsuleCost.toFixed(2);
              }
            }

            // Toplam tutarı yeniden hesapla
            if (updatedOrder.unitPrice) {
              const totalPrice = calculateTotalPrice(
                updatedOrder.usedM2,
                updatedOrder.unitPrice,
                updatedOrder.extraPrice,
                updatedOrder.pileModel,
                updatedOrder.capsulePrice
              );
              const quantity = parseFloat(updatedOrder.quantity) || 1;
              updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);

              // İndirimli tutarı hesapla
              const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
              updatedOrder.discountedPrice = discountedPrice.toFixed(2);
            }
          }
        }

        // En, Boy değiştiğinde kullanılan mt2'yi hesapla
        if (['width', 'height'].includes(field)) {
          // Pile sıklığını kullan (eğer varsa)
          const density = parseFloat(updatedOrder.pileDensity) || 0;
          const usedM2 = calculateUsedM2(updatedOrder.width, updatedOrder.height, updatedOrder.pileModel, density > 0 ? density : null, updatedOrder.hasOzelOlcu);
          updatedOrder.usedM2 = usedM2.toFixed(2);

          if (updatedOrder.pileModel === 'Kapsül Pile') {
            if (updatedOrder.capsuleUnitPrice) {
              const capsulePrice = calculateCapsulePrice(updatedOrder.usedM2, updatedOrder.capsuleUnitPrice);
              updatedOrder.capsulePrice = capsulePrice.toFixed(2);
            }
            if (updatedOrder.capsuleUnitCost) {
              const capsuleCost = calculateCapsuleCost(updatedOrder.usedM2, updatedOrder.capsuleUnitCost);
              updatedOrder.capsuleCost = capsuleCost.toFixed(2);
            }
          }

          // En, Boy değiştiğinde toplam tutarı da hesapla
          if (updatedOrder.unitPrice) {
            const totalPrice = calculateTotalPrice(
              updatedOrder.usedM2,
              updatedOrder.unitPrice,
              updatedOrder.extraPrice,
              updatedOrder.pileModel,
              updatedOrder.capsulePrice
            );
            // Mevcut adet değerini koru - updatedOrder'dan al
            const quantity = parseFloat(updatedOrder.quantity) || 1;
            updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);

            // İndirimli tutarı hesapla
            const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
            updatedOrder.discountedPrice = discountedPrice.toFixed(2);
          }
        }

        // Özel Ölçü checkbox'ı değiştiğinde kullanılan metreyi yeniden hesapla (kumaş 90° çevrilerek)
        if (field === 'hasOzelOlcu' && updatedOrder.pileModel && updatedOrder.width && updatedOrder.height) {
          const density = parseFloat(updatedOrder.pileDensity) || 0;
          const usedM2 = calculateUsedM2(updatedOrder.width, updatedOrder.height, updatedOrder.pileModel, density > 0 ? density : null, value);
          updatedOrder.usedM2 = usedM2.toFixed(2);

          if (updatedOrder.pileModel === 'Kapsül Pile') {
            if (updatedOrder.capsuleUnitPrice) {
              const capsulePrice = calculateCapsulePrice(updatedOrder.usedM2, updatedOrder.capsuleUnitPrice);
              updatedOrder.capsulePrice = capsulePrice.toFixed(2);
            }
            if (updatedOrder.capsuleUnitCost) {
              const capsuleCost = calculateCapsuleCost(updatedOrder.usedM2, updatedOrder.capsuleUnitCost);
              updatedOrder.capsuleCost = capsuleCost.toFixed(2);
            }
          }
          // Toplam fiyat/maliyet, aşağıdaki genel 'hasOzelOlcu' tetikleyicili bloklarda
          // (özel tasarım + astar + aksesuar dahil) yeniden hesaplanıyor.
        }

        if (updatedOrder.pileModel === 'Kapsül Pile' && ['usedM2', 'capsuleUnitPrice', 'capsuleUnitCost'].includes(field)) {
          if (updatedOrder.capsuleUnitPrice) {
            const capsulePrice = calculateCapsulePrice(updatedOrder.usedM2, updatedOrder.capsuleUnitPrice);
            updatedOrder.capsulePrice = capsulePrice.toFixed(2);
          }
          if (updatedOrder.capsuleUnitCost) {
            const capsuleCost = calculateCapsuleCost(updatedOrder.usedM2, updatedOrder.capsuleUnitCost);
            updatedOrder.capsuleCost = capsuleCost.toFixed(2);
          }
        }

        // Özel Tasarım Pile modeli değiştiğinde pile sıklığını hesapla
        if (field === 'ozelTasarimPileModel') {
          let pileDensity = 0;
          if (pileModels && pileModels.length > 0) {
            const selectedModel = pileModels.find(m => m.modelName === value);
            if (selectedModel && selectedModel.density) {
              pileDensity = selectedModel.density;
            } else {
              pileDensity = calculatePileDensity(value);
            }
          } else {
            pileDensity = calculatePileDensity(value);
          }

          updatedOrder.ozelTasarimPileDensity = pileDensity.toString();
          updatedOrder.ozelTasarimPileModel = value;

          if (updatedOrder.ozelTasarimWidth && updatedOrder.ozelTasarimHeight) {
            const density = parseFloat(updatedOrder.ozelTasarimPileDensity) || 0;
            const usedM2 = calculateUsedM2(updatedOrder.ozelTasarimWidth, updatedOrder.ozelTasarimHeight, value, density > 0 ? density : null);
            updatedOrder.ozelTasarimUsedM2 = usedM2.toFixed(2);

            if (value === 'Kapsül Pile') {
              if (updatedOrder.ozelTasarimCapsuleUnitPrice) {
                const capsulePrice = calculateCapsulePrice(updatedOrder.ozelTasarimUsedM2, updatedOrder.ozelTasarimCapsuleUnitPrice);
                updatedOrder.ozelTasarimCapsulePrice = capsulePrice.toFixed(2);
              }
              if (updatedOrder.ozelTasarimCapsuleUnitCost) {
                const capsuleCost = calculateCapsuleCost(updatedOrder.ozelTasarimUsedM2, updatedOrder.ozelTasarimCapsuleUnitCost);
                updatedOrder.ozelTasarimCapsuleCost = capsuleCost.toFixed(2);
              }
            }
          }
        }

        // Özel Tasarım Pile sıklığı değiştiğinde kullanılan mt2'yi yeniden hesapla
        if (field === 'ozelTasarimPileDensity' && updatedOrder.ozelTasarimPileModel && updatedOrder.ozelTasarimWidth && updatedOrder.ozelTasarimHeight) {
          const density = parseFloat(value) || 0;
          if (density > 0) {
            const usedM2 = calculateUsedM2(updatedOrder.ozelTasarimWidth, updatedOrder.ozelTasarimHeight, updatedOrder.ozelTasarimPileModel, density);
            updatedOrder.ozelTasarimUsedM2 = usedM2.toFixed(2);

            if (updatedOrder.ozelTasarimPileModel === 'Kapsül Pile') {
              if (updatedOrder.ozelTasarimCapsuleUnitPrice) {
                const capsulePrice = calculateCapsulePrice(updatedOrder.ozelTasarimUsedM2, updatedOrder.ozelTasarimCapsuleUnitPrice);
                updatedOrder.ozelTasarimCapsulePrice = capsulePrice.toFixed(2);
              }
              if (updatedOrder.ozelTasarimCapsuleUnitCost) {
                const capsuleCost = calculateCapsuleCost(updatedOrder.ozelTasarimUsedM2, updatedOrder.ozelTasarimCapsuleUnitCost);
                updatedOrder.ozelTasarimCapsuleCost = capsuleCost.toFixed(2);
              }
            }
          }
        }

        // Özel Tasarım En, Boy değiştiğinde kullanılan mt2'yi hesapla
        if (['ozelTasarimWidth', 'ozelTasarimHeight'].includes(field)) {
          const density = parseFloat(updatedOrder.ozelTasarimPileDensity) || 0;
          const usedM2 = calculateUsedM2(updatedOrder.ozelTasarimWidth, updatedOrder.ozelTasarimHeight, updatedOrder.ozelTasarimPileModel, density > 0 ? density : null);
          updatedOrder.ozelTasarimUsedM2 = usedM2.toFixed(2);

          if (updatedOrder.ozelTasarimPileModel === 'Kapsül Pile') {
            if (updatedOrder.ozelTasarimCapsuleUnitPrice) {
              const capsulePrice = calculateCapsulePrice(updatedOrder.ozelTasarimUsedM2, updatedOrder.ozelTasarimCapsuleUnitPrice);
              updatedOrder.ozelTasarimCapsulePrice = capsulePrice.toFixed(2);
            }
            if (updatedOrder.ozelTasarimCapsuleUnitCost) {
              const capsuleCost = calculateCapsuleCost(updatedOrder.ozelTasarimUsedM2, updatedOrder.ozelTasarimCapsuleUnitCost);
              updatedOrder.ozelTasarimCapsuleCost = capsuleCost.toFixed(2);
            }
          }
        }

        // Özel Tasarım Kapsül Pile alanları doğrudan değiştiğinde
        if (updatedOrder.ozelTasarimPileModel === 'Kapsül Pile' && ['ozelTasarimUsedM2', 'ozelTasarimCapsuleUnitPrice', 'ozelTasarimCapsuleUnitCost'].includes(field)) {
          if (updatedOrder.ozelTasarimCapsuleUnitPrice) {
            const capsulePrice = calculateCapsulePrice(updatedOrder.ozelTasarimUsedM2, updatedOrder.ozelTasarimCapsuleUnitPrice);
            updatedOrder.ozelTasarimCapsulePrice = capsulePrice.toFixed(2);
          }
          if (updatedOrder.ozelTasarimCapsuleUnitCost) {
            const capsuleCost = calculateCapsuleCost(updatedOrder.ozelTasarimUsedM2, updatedOrder.ozelTasarimCapsuleUnitCost);
            updatedOrder.ozelTasarimCapsuleCost = capsuleCost.toFixed(2);
          }
        }

        // Toplam tutarı hesapla (herhangi bir fiyat alanı değiştiğinde)
        if (['usedM2', 'unitPrice', 'extraPrice', 'capsulePrice', 'capsuleUnitPrice', 'quantity', 'width', 'height', 'pileModel', 'pileDensity', 'discount', 'totalPrice', 'hasAltEtekKatlama', 'hasOzelOlcu',
          'hasOzelTasarim', 'ozelTasarimUsedM2', 'ozelTasarimUnitPrice', 'ozelTasarimExtraPrice', 'ozelTasarimCapsulePrice', 'ozelTasarimCapsuleUnitPrice', 'ozelTasarimQuantity', 'ozelTasarimWidth', 'ozelTasarimHeight', 'ozelTasarimPileModel', 'ozelTasarimPileDensity',
          'hasAstar', 'astarProductName', 'astarUnitPrice', 'astarUnitCost',
          'kanatAccessories'].includes(field)) {
          
          if (updatedOrder.pileModel === 'Kapsül Pile' && (field === 'capsuleUnitPrice' || field === 'capsuleUnitCost')) {
            if (updatedOrder.capsuleUnitPrice) {
              const capsulePrice = calculateCapsulePrice(updatedOrder.usedM2, updatedOrder.capsuleUnitPrice);
              updatedOrder.capsulePrice = capsulePrice.toFixed(2);
            }
            if (updatedOrder.capsuleUnitCost) {
              const capsuleCost = calculateCapsuleCost(updatedOrder.usedM2, updatedOrder.capsuleUnitCost);
              updatedOrder.capsuleCost = capsuleCost.toFixed(2);
            }
          }

          if (updatedOrder.ozelTasarimPileModel === 'Kapsül Pile' && (field === 'ozelTasarimCapsuleUnitPrice' || field === 'ozelTasarimCapsuleUnitCost')) {
            if (updatedOrder.ozelTasarimCapsuleUnitPrice) {
              const capsulePrice = calculateCapsulePrice(updatedOrder.ozelTasarimUsedM2, updatedOrder.ozelTasarimCapsuleUnitPrice);
              updatedOrder.ozelTasarimCapsulePrice = capsulePrice.toFixed(2);
            }
            if (updatedOrder.ozelTasarimCapsuleUnitCost) {
              const capsuleCost = calculateCapsuleCost(updatedOrder.ozelTasarimUsedM2, updatedOrder.ozelTasarimCapsuleUnitCost);
              updatedOrder.ozelTasarimCapsuleCost = capsuleCost.toFixed(2);
            }
          }

          let totalPriceMain = 0;
          if (updatedOrder.usedM2 && updatedOrder.unitPrice) {
            const baseTotalPrice = calculateTotalPrice(
              updatedOrder.usedM2,
              updatedOrder.unitPrice,
              updatedOrder.extraPrice,
              updatedOrder.pileModel,
              updatedOrder.capsulePrice
            );
            const quantity = parseFloat(updatedOrder.quantity) || 1;
            totalPriceMain = baseTotalPrice * quantity;
          }

          let totalPriceOzel = 0;
          if (selectedProductType === 'kanat' && updatedOrder.hasOzelTasarim && updatedOrder.ozelTasarimUsedM2 && updatedOrder.ozelTasarimUnitPrice) {
            const baseOzelPrice = calculateTotalPrice(
              updatedOrder.ozelTasarimUsedM2,
              updatedOrder.ozelTasarimUnitPrice,
              updatedOrder.ozelTasarimExtraPrice,
              updatedOrder.ozelTasarimPileModel,
              updatedOrder.ozelTasarimCapsulePrice
            );
            const ozelQuantity = parseFloat(updatedOrder.ozelTasarimQuantity) || 1;
            totalPriceOzel = baseOzelPrice * ozelQuantity;
            updatedOrder.ozelTasarimTotalPrice = totalPriceOzel.toFixed(2);
          } else {
            updatedOrder.ozelTasarimTotalPrice = 0;
          }

          let totalPriceAstar = 0;
          if (selectedProductType === 'kanat' && updatedOrder.hasAstar && updatedOrder.usedM2 && updatedOrder.astarUnitPrice) {
            const astarPriceVal = calculateAstarPrice(updatedOrder.usedM2, updatedOrder.astarUnitPrice, updatedOrder.quantity);
            totalPriceAstar = astarPriceVal;
            updatedOrder.astarPrice = astarPriceVal.toFixed(2);
          } else {
            updatedOrder.astarPrice = 0;
          }

          let totalPriceAccessories = 0;
          if ((selectedProductType === 'kanat' || selectedProductType === 'tul') && Array.isArray(updatedOrder.kanatAccessories)) {
            totalPriceAccessories = updatedOrder.kanatAccessories.reduce((sum, acc) => sum + (parseFloat(acc.total) || 0), 0);
          }

          const overallTotalPrice = totalPriceMain + totalPriceOzel + totalPriceAstar + totalPriceAccessories;
          updatedOrder.totalPrice = overallTotalPrice.toFixed(2);

          // İndirimli tutarı hesapla
          const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
          updatedOrder.discountedPrice = discountedPrice.toFixed(2);
        }
      }

      // Stor ve Zebra için özel hesaplamalar
      if (selectedProductType === 'stor' || selectedProductType === 'zebra') {
        // En veya Boy değiştiğinde kullanılan mt2'yi hesapla
        if (['width', 'height'].includes(field)) {
          const usedM2 = calculateStorZebraUsedM2(updatedOrder.width, updatedOrder.height);
          updatedOrder.usedM2 = usedM2.toFixed(2);
        }

        // Toplam tutarı hesapla
        if (['usedM2', 'unitPrice', 'quantity', 'mounting', 'motorPrice', 'motorPriceCurrency', 'kumandaPriceCurrency', 'motorType',
          'width', 'height', 'productName',
          ...STOR_ZEBRA_EXTRA_PRICE_FIELDS,
          'totalPrice', 'discount'].includes(field)) {

          // Jumbo / Metal Zincir sabit (adet başı), diğerleri en ile çarpılır
          const extras = buildStorZebraExtras(updatedOrder, selectedProductType, 'price');

          const totalPricePreAdet = calculateStorZebraTotalPrice(
            updatedOrder.usedM2,
            updatedOrder.unitPrice,
            updatedOrder.width,
            extras,
            updatedOrder.motorPrice,
            updatedOrder.mounting
          );

          const quantity = parseFloat(updatedOrder.quantity) || 1;
          updatedOrder.totalPrice = (totalPricePreAdet * quantity).toFixed(2);

          const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
          updatedOrder.discountedPrice = discountedPrice.toFixed(2);
        }
      }

      // Ahşap Jaluzi ve Metal Jaluzi için özel hesaplamalar
      if (selectedProductType === 'ahsapJaluzi' || selectedProductType === 'metalJaluzi') {
        // En veya Boy değiştiğinde kullanılan mt2'yi hesapla
        if (['width', 'height'].includes(field)) {
          const usedM2 = calculateJaluziUsedM2(updatedOrder.width, updatedOrder.height);
          updatedOrder.usedM2 = usedM2.toFixed(2);

          // En, Boy değiştiğinde toplam tutarı da hesapla
          if (updatedOrder.unitPrice) {
            const totalPrice = calculateJaluziTotalPrice(
              updatedOrder.usedM2,
              updatedOrder.unitPrice,
              updatedOrder.mounting,
              updatedOrder.motorPrice,
              (parseFloat(updatedOrder.zigilAmount) || 0) + (parseFloat(updatedOrder.metalZigilAmount) || 0),
              parseFloat(updatedOrder.ikiCamArasiAmount) || 0
            );
            // Mevcut adet değerini koru
            const quantity = parseFloat(updatedOrder.quantity) || 1;
            updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);

            // İndirimli tutarı hesapla
            if (updatedOrder.discount) {
              const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
              updatedOrder.discountedPrice = discountedPrice.toFixed(2);
            }
          }
        }

        // Toplam tutarı hesapla (herhangi bir fiyat alanı değiştiğinde)
        if (['usedM2', 'unitPrice', 'mounting', 'motorPrice', 'motorPriceCurrency', 'kumandaPriceCurrency', 'motorType', 'zigilAmount', 'hasZigil', 'hasReduktor', 'hasKurdela', 'quantity', 'metalZigilAmount', 'hasMetalZigil', 'ikiCamArasiAmount', 'hasIkiCamArasi', 'hasMetalReduktor', 'width', 'height', 'totalPrice', 'discount'].includes(field)) {
          const totalPrice = calculateJaluziTotalPrice(
            updatedOrder.usedM2,
            updatedOrder.unitPrice,
            updatedOrder.mounting,
            updatedOrder.motorPrice,
            (parseFloat(updatedOrder.zigilAmount) || 0) + (parseFloat(updatedOrder.metalZigilAmount) || 0),
            parseFloat(updatedOrder.ikiCamArasiAmount) || 0
          );
          // Adet ile çarp
          const quantity = parseFloat(updatedOrder.quantity) || 1;
          updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);

          // İndirimli tutarı hesapla
          const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
          updatedOrder.discountedPrice = discountedPrice.toFixed(2);
        }
      }

      // Plise Major Refactor - Batch Hesaplama
      if (selectedProductType === 'plise') {
        // İlgili alanlardan biri değiştiğinde tüm listeyi ve toplam tutarı yeniden hesapla
        if (['pliseItems', 'pliseProduct1Price', 'pliseProduct2Price', 'mounting', 'discount', 'quantity', 'extraPrice', 'pliseProduct1Cost', 'pliseProduct2Cost', 'manualExtraCost', 'mountingCost'].includes(field)) {
          const { items, totalPrice, discountedPrice, totalCost } = calculatePliseBatchTotals(
            updatedOrder.pliseItems,
            updatedOrder.pliseProduct1Price,
            updatedOrder.pliseProduct2Price,
            updatedOrder.mounting,
            updatedOrder.discount,
            updatedOrder.quantity,
            updatedOrder.extraPrice,
            updatedOrder.pliseProduct1Cost,
            updatedOrder.pliseProduct2Cost,
            updatedOrder.manualExtraCost,
            updatedOrder.mountingCost
          );

          updatedOrder.pliseItems = items;
          updatedOrder.totalPrice = totalPrice;
          updatedOrder.discountedPrice = discountedPrice;
          updatedOrder.totalCost = totalCost;
        }
      }

      // Aksesuar için özel hesaplamalar
      if (selectedProductType === 'aksesuar') {
        // Toplam tutarı hesapla (birim fiyat veya adet değiştiğinde)
        if (['unitPrice', 'quantity', 'usedMeter', 'aksesuarBirimTipi'].includes(field)) {
          const totalPrice = calculateAksesuarTotalPrice(updatedOrder.unitPrice, updatedOrder.quantity, updatedOrder.usedMeter, updatedOrder.aksesuarBirimTipi);
          updatedOrder.totalPrice = totalPrice.toFixed(2);
        }

        // İndirimli tutarı hesapla (toplam tutar veya iskonto değiştiğinde)
        if (['totalPrice', 'discount'].includes(field) || ['unitPrice', 'quantity', 'usedMeter', 'aksesuarBirimTipi'].includes(field)) {
          const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
          updatedOrder.discountedPrice = discountedPrice.toFixed(2);
        }
      }

      // Ray Sistemi için özel hesaplamalar
      if (selectedProductType === 'raySistemi') {
        // En veya Ölçü Tamamlama değişince Kullanılan Metreyi hesapla
        if (['width', 'olcuTamamlama'].includes(field)) {
          const widthCm = parseFloat(updatedOrder.width) || 0;
          let usedMeter = 0;
          const olcu = updatedOrder.olcuTamamlama;
          if (widthCm > 0) {
            if (olcu === '25cm') {
              // 25 cm'nin en yakın katına tamamla, minimum 1m
              usedMeter = Math.max(1, Math.ceil(widthCm / 25) * 0.25);
            } else if (olcu === '50cm') {
              // 50 cm'nin en yakın katına tamamla, minimum 1m
              usedMeter = Math.max(1, Math.ceil(widthCm / 50) * 0.50);
            } else if (olcu === 'urunBoyutu') {
              // Doğrudan cm -> m, minimum 1m
              usedMeter = Math.max(1, widthCm / 100);
            }
          }
          updatedOrder.usedMeter = usedMeter > 0 ? usedMeter.toFixed(2) : '';
        }

        // Toplam tutarı hesapla (birim fiyat, kullanılan metre, montaj veya adet değişince)
        if (['usedMeter', 'unitPrice', 'mounting', 'quantity', 'width', 'olcuTamamlama', 'rayExtraPrice'].includes(field)) {
          const usedM = parseFloat(updatedOrder.usedMeter) || 0;
          const unitP = parseFloat(updatedOrder.unitPrice) || 0;
          const mountP = parseFloat(updatedOrder.mounting) || 0;
          const qty = parseFloat(updatedOrder.quantity) || 1;
          const extraP = parseFloat(updatedOrder.rayExtraPrice) || 0; // adet başına ek ücret
          if (usedM > 0 && unitP > 0) {
            const totalPrice = (usedM * unitP + mountP) * qty + extraP * qty;
            updatedOrder.totalPrice = totalPrice.toFixed(2);
          }
        }

        // İndirimli tutarı hesapla
        if (['totalPrice', 'discount', 'usedMeter', 'unitPrice', 'mounting', 'quantity', 'width', 'olcuTamamlama', 'rayExtraPrice'].includes(field)) {
          const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
          updatedOrder.discountedPrice = discountedPrice.toFixed(2);
        }
      }

      // Rustik için özel hesaplamalar
      if (selectedProductType === 'rustik') {
        // En değiştiğinde Kullanılan Cm ve Ayak Sayısını hesapla
        if (['width'].includes(field)) {
          const widthCm = parseFloat(updatedOrder.width) || 0;
          if (widthCm > 0) {
            // En'i 40cm katlarına tamamla. Minimum 120cm.
            // 120, 160, 200, 240, 280, 320, 360, 400, 440, 480, 520, 560, 600cm
            let usedCm = Math.ceil(widthCm / 40) * 40;
            if (usedCm < 120) usedCm = 120;
            updatedOrder.usedCm = usedCm;

            // Ayak Sayısı Atama (Kullanılan Cm'ye göre - Otomatik)
            if (usedCm <= 200) {
              updatedOrder.ayakSayisi = 2;
            } else if (usedCm <= 400) {
              updatedOrder.ayakSayisi = 3;
            } else {
              updatedOrder.ayakSayisi = 4;
            }
          } else {
            updatedOrder.usedCm = '';
            updatedOrder.ayakSayisi = '';
          }

          // Ray seçeneklerinin En bilgisini ana En ile senkronize et (ama elle düzenlenebilir kalsın)
          if (Array.isArray(updatedOrder.rustikRaySecenekleri)) {
            updatedOrder.rustikRaySecenekleri = updatedOrder.rustikRaySecenekleri.map(ray => ({
              ...ray,
              quantity: value // quantity alanını En olarak kullanıyoruz
            }));
          }
        }

        // Toplam tutarı hesapla
        if (['unitPrice', 'ayakSayisi', 'ekAyakSayisi', 'ayakUcreti', 'mounting', 'quantity', 'rustikAksesuarlar', 'rustikRaySecenekleri', 'width'].includes(field)) {
          const unitP = parseFloat(updatedOrder.unitPrice) || 0;
          const ayakS = parseFloat(updatedOrder.ayakSayisi) || 0;
          const ekAyakS = parseFloat(updatedOrder.ekAyakSayisi) || 0;
          const ayakU = parseFloat(updatedOrder.ayakUcreti) || 0;
          const mounting = parseFloat(updatedOrder.mounting) || 0;
          const qty = parseFloat(updatedOrder.quantity) || 1;

          // Aksesuar ve Ray seçenekleri toplamı
          const accTotal = (updatedOrder.rustikAksesuarlar || []).reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1), 0);

          // Ray tutar hesaplaması: birim fiyat * (kullanılan cm / 100)
          const usedCmNum = parseFloat(updatedOrder.usedCm) || 0;
          const rayTotal = (updatedOrder.rustikRaySecenekleri || []).reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (usedCmNum / 100), 0);

          // Toplam Tutar = Adet * (Birim Fiyat + (ek ayak sayısı * ayak ücreti) + ray toplam + aksesuar toplam + montaj)
          // Not: Normal ayak sayısı (ayakSayisi) fiyata dahil edilmiyor.
          const baseTotal = unitP + (ekAyakS * ayakU) + accTotal + rayTotal + mounting;
          updatedOrder.totalPrice = (baseTotal * qty).toFixed(2);
        }

        // İndirimli tutarı hesapla
        if (['totalPrice', 'discount'].includes(field) || ['unitPrice', 'ayakSayisi', 'ekAyakSayisi', 'ayakUcreti', 'mounting', 'quantity', 'rustikAksesuarlar', 'rustikRaySecenekleri', 'width'].includes(field)) {
          const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
          updatedOrder.discountedPrice = discountedPrice.toFixed(2);
        }
      }

      // Motor + Kumanda Döviz Hesaplaması ve Auto-fill (Tüm motorlu ürünler için)
      // newOrder.motorType artık "Firma Adı - Motor" veya "Firma Adı - Motor + Kumanda" biçiminde bir seçim.
      const motorProductTypes = ['ahsapJaluzi', 'metalJaluzi', 'stor', 'zebra', 'dikeyPerde', 'dikeyPvc', 'dikeyStor', 'katlamaliPerde'];
      if (motorProductTypes.includes(selectedProductType) && ['motorPriceCurrency', 'kumandaPriceCurrency', 'motorType'].includes(field)) {
        const motorCompanies = adminSettings[`${selectedProductType}MotorCompanies`] || [];
        const { companyName, hasKumanda } = parseMotorSelection(updatedOrder.motorType);
        const motorCompany = findMotorCompany(motorCompanies, companyName);
        const specificCurrency = motorCompany?.currency || 'USD';
        const manualRate = motorCompany?.rate;
        const systemRate = exchangeRates[specificCurrency] || 1;
        const rate = manualRate && Number(manualRate) > 0 ? Number(manualRate) : Number(systemRate);

        // Fiyat auto-fill (sadece motorType değiştiğinde ve yeni bir firma/mod seçildiğinde)
        if (field === 'motorType' && value) {
          updatedOrder.motorPriceCurrency = motorCompany?.motorPrice || '';
          updatedOrder.kumandaPriceCurrency = hasKumanda ? (motorCompany?.kumandaPrice || '') : '';
        } else if (field === 'motorType' && !value) {
          updatedOrder.motorPriceCurrency = '';
          updatedOrder.kumandaPriceCurrency = '';
        }

        const motorAmount = Number(updatedOrder.motorPriceCurrency) || 0;
        const kumandaAmount = hasKumanda ? (Number(updatedOrder.kumandaPriceCurrency) || 0) : 0;
        if (updatedOrder.motorType && (motorAmount || kumandaAmount)) {
          updatedOrder.motorPrice = ((motorAmount + kumandaAmount) * rate).toFixed(2);
        } else {
          updatedOrder.motorPrice = 0;
        }

        // Motor + Kumanda maliyetini de güncelle
        const motorCostAmount = Number(motorCompany?.motorCost) || 0;
        const kumandaCostAmount = hasKumanda ? (Number(motorCompany?.kumandaCost) || 0) : 0;
        if (updatedOrder.motorType) {
          updatedOrder.motorCost = ((motorCostAmount + kumandaCostAmount) * rate).toFixed(2);
        } else {
          updatedOrder.motorCost = 0;
        }
      }


      // Stor ve Zebra için özel hesaplamalar
      if (selectedProductType === 'stor' || selectedProductType === 'zebra') {
        if (['width', 'height'].includes(field)) {
          const usedM2 = calculateStorZebraUsedM2(updatedOrder.width, updatedOrder.height);
          updatedOrder.usedM2 = usedM2.toFixed(2);
        }

        if (['width', 'height', 'usedM2', 'unitPrice', 'extraPrice', 'mounting', 'motorPrice', 'motorPriceCurrency', 'kumandaPriceCurrency', 'motorType', 'totalPrice', 'discount', 'quantity', ...STOR_ZEBRA_EXTRA_PRICE_FIELDS].includes(field)) {
          const extraPrices = buildStorZebraExtras(updatedOrder, selectedProductType, 'price');

          const totalPrice = calculateStorZebraTotalPrice(
            updatedOrder.usedM2,
            updatedOrder.unitPrice,
            updatedOrder.width,
            extraPrices,
            parseFloat(updatedOrder.motorPrice) || 0,
            updatedOrder.mounting
          );
          const quantity = parseFloat(updatedOrder.quantity) || 1;
          updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);

          const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
          updatedOrder.discountedPrice = discountedPrice.toFixed(2);
        }
      }

      // Ahşap Jaluzi, Metal Jaluzi, Dikey PVC ve Dikey Stor için özel hesaplamalar
      if (['ahsapJaluzi', 'metalJaluzi', 'dikeyPvc', 'dikeyStor'].includes(selectedProductType)) {
        if (['width', 'height'].includes(field)) {
          const usedM2 = calculateJaluziUsedM2(updatedOrder.width, updatedOrder.height);
          updatedOrder.usedM2 = usedM2.toFixed(2);
        }

        if (['width', 'height', 'usedM2', 'unitPrice', 'extraPrice', 'sistemTipi', 'mounting', 'motorPrice', 'motorPriceCurrency', 'kumandaPriceCurrency', 'motorType', 'totalPrice', 'discount', 'quantity', 'zigilAmount', 'metalZigilAmount', 'ikiCamArasiAmount'].includes(field)) {
          const totalPrice = calculateJaluziTotalPrice(
            updatedOrder.usedM2,
            updatedOrder.unitPrice,
            updatedOrder.mounting,
            parseFloat(updatedOrder.motorPrice) || 0,
            (parseFloat(updatedOrder.zigilAmount) || 0) + (parseFloat(updatedOrder.metalZigilAmount) || 0),
            parseFloat(updatedOrder.ikiCamArasiAmount) || 0
          );
          const quantity = parseFloat(updatedOrder.quantity) || 1;
          updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);

          const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
          updatedOrder.discountedPrice = discountedPrice.toFixed(2);
        }
      }

      // Katlamalı Perde için özel hesaplamalar
      if (selectedProductType === 'katlamaliPerde') {
        if (['width'].includes(field)) {
          const usedM = calculateKatlamaliPerdeUsedM(updatedOrder.width);
          updatedOrder.usedM2 = usedM.toFixed(2);
        }

        if (['width', 'usedM2', 'fabricPrice', 'mechanismPrice', 'accessoryPrice', 'accessories', 'mounting', 'quantity', 'motorPrice', 'motorPriceCurrency', 'kumandaPriceCurrency', 'motorType', 'totalPrice', 'discount', 'extraPrice'].includes(field)) {
          const totalPrice = calculateKatlamaliPerdeTotalPrice(
            updatedOrder.usedM2,
            updatedOrder.fabricPrice,
            updatedOrder.mechanismPrice,
            updatedOrder.accessories || updatedOrder.accessoryPrice,
            updatedOrder.mounting,
            updatedOrder.motorPrice
          );
          const quantity = parseFloat(updatedOrder.quantity) || 1;
          updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);

          const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
          updatedOrder.discountedPrice = discountedPrice.toFixed(2);
        }
      }

      // Dikey Perde için özel hesaplamalar
      if (selectedProductType === 'dikeyPerde') {
        if (['width', 'height'].includes(field)) {
          const usedM2 = calculateJaluziUsedM2(updatedOrder.width, updatedOrder.height);
          updatedOrder.usedM2 = usedM2.toFixed(2);
        }

        if (['width', 'height', 'usedM2', 'unitPrice', 'extraPrice', 'backgroundCurtain', 'hasDikeyFon', 'dikeyFonAmount', 'mounting', 'motorPrice', 'motorPriceTL', 'motorPriceCurrency', 'kumandaPriceCurrency', 'motorType', 'totalPrice', 'discount', 'quantity'].includes(field)) {
          const totalPrice = calculateDikeyPerdeTotalPrice(
            updatedOrder.usedM2,
            updatedOrder.unitPrice,
            updatedOrder.mounting,
            parseFloat(updatedOrder.motorPrice) || parseFloat(updatedOrder.motorPriceTL) || 0
          );
          const quantity = parseFloat(updatedOrder.quantity) || 1;
          updatedOrder.totalPrice = (totalPrice * quantity).toFixed(2);

          const discountedPrice = calculateDiscountedPrice(updatedOrder.totalPrice, updatedOrder.discount);
          updatedOrder.discountedPrice = discountedPrice.toFixed(2);
        }
      }

      // Genel Maliyet Hesaplama
      const costTriggerFields = ['usedM2', 'unitCost', 'manualExtraCost', 'mountingCost', 'rayExtraCost', 'extraPrice', 'mounting', 'quantity', 'usedMeter', 'width', 'height', 'pliseItems', 'usedCm', 'ayakSayisi', 'ekAyakSayisi', 'ayakUcreti', 'pileModel', 'capsuleUnitCost', 'capsuleCost', 'hasAltEtekKatlama', 'hasOzelOlcu',
        'hasOzelTasarim', 'ozelTasarimUsedM2', 'ozelTasarimUnitCost', 'ozelTasarimManualExtraCost', 'ozelTasarimQuantity', 'ozelTasarimWidth', 'ozelTasarimHeight', 'ozelTasarimPileModel', 'ozelTasarimPileDensity', 'ozelTasarimCapsuleUnitCost', 'ozelTasarimCapsuleCost',
        'hasAstar', 'astarProductName', 'astarUnitPrice', 'astarUnitCost',
        'kanatAccessories',
        ...STOR_ZEBRA_EXTRA_COST_FIELDS,
        'zigilCostAmount', 'metalZigilCostAmount', 'ikiCamArasiCostAmount', 'motorCost', 'motorType', 'sistemTipi',
        'hasZigil', 'hasMetalZigil', 'hasIkiCamArasi', 'hasDikeyFon',
        'accessories', 'fabricPrice', 'mechanismPrice', 'mechanismCost',
        'ayakUcretiCost', 'rustikAksesuarlar', 'rustikRaySecenekleri', 'aksesuarBirimTipi'
      ];
      if (costTriggerFields.includes(field)) {
        const qty = parseFloat(updatedOrder.quantity) || 1;

        if (selectedProductType === 'plise') {
          // Plise maliyeti yukarıdaki batch hesaplamada (satır 1012 civarı) zaten hesaplanıyor.
          // Burada tekrar hesaplamaya gerek yok, aksi halde çakışma olabilir.
        } else if (selectedProductType === 'raySistemi') {
          const usedM = parseFloat(updatedOrder.usedMeter) || 0;
          const uCost = parseFloat(updatedOrder.unitCost) || 0;
          const extraCost = parseFloat(updatedOrder.rayExtraCost) || 0; // adet başına ek maliyet
          updatedOrder.totalCost = (usedM * uCost * qty + extraCost * qty + (parseFloat(updatedOrder.mountingCost) || 0)).toFixed(2);
        } else if (selectedProductType === 'aksesuar') {
          const tCost = calculateAksesuarTotalCost(updatedOrder.unitCost, updatedOrder.quantity, updatedOrder.mountingCost, updatedOrder.usedMeter, updatedOrder.aksesuarBirimTipi);
          updatedOrder.totalCost = tCost.toFixed(2);
        } else if (selectedProductType === 'rustik') {
          const tCost = calculateRustikTotalCost(
            updatedOrder.unitCost,
            updatedOrder.ekAyakSayisi,
            updatedOrder.ayakUcretiCost,
            updatedOrder.mountingCost,
            updatedOrder.usedCm,
            updatedOrder.rustikRaySecenekleri || [],
            updatedOrder.rustikAksesuarlar || []
          );
          updatedOrder.totalCost = (tCost * qty).toFixed(2);
        } else if (selectedProductType === 'stor' || selectedProductType === 'zebra') {
          const extraCosts = buildStorZebraExtras(updatedOrder, selectedProductType, 'cost');
          const tCost = calculateStorZebraTotalCost(
            updatedOrder.usedM2,
            updatedOrder.unitCost,
            updatedOrder.width,
            extraCosts,
            updatedOrder.motorCost || 0,
            updatedOrder.mountingCost || 0
          );
          updatedOrder.totalCost = (tCost * qty).toFixed(2);
        } else if (selectedProductType === 'ahsapJaluzi' || selectedProductType === 'metalJaluzi' || selectedProductType === 'dikeyPvc' || selectedProductType === 'dikeyStor') {
          const tCost = calculateJaluziTotalCost(
            updatedOrder.usedM2,
            updatedOrder.unitCost,
            updatedOrder.motorCost || 0,
            (parseFloat(updatedOrder.zigilCostAmount) || 0) + (parseFloat(updatedOrder.metalZigilCostAmount) || 0),
            parseFloat(updatedOrder.ikiCamArasiCostAmount) || 0,
            updatedOrder.mountingCost || 0
          );
          updatedOrder.totalCost = (tCost * qty).toFixed(2);
        } else if (selectedProductType === 'dikeyPerde') {
          const tCost = calculateDikeyPerdeTotalCost(
            updatedOrder.usedM2,
            updatedOrder.unitCost,
            updatedOrder.motorCost || 0,
            updatedOrder.mountingCost || 0
          );
          updatedOrder.totalCost = (tCost * qty).toFixed(2);
        } else if (selectedProductType === 'katlamaliPerde') {
          const tCost = calculateKatlamaliPerdeTotalCost(
            updatedOrder.usedM2,
            updatedOrder.unitCost,
            updatedOrder.mechanismCost || 0,
            updatedOrder.accessories || [],
            updatedOrder.motorCost || 0,
            updatedOrder.mountingCost || 0
          );
          updatedOrder.totalCost = (tCost * qty).toFixed(2);
        } else {
          // Bu kısım tul ve kanat için çalışıyor
          const tCost = calculateTotalCost(
            updatedOrder.usedM2,
            updatedOrder.unitCost,
            updatedOrder.manualExtraCost || 0,
            updatedOrder.pileModel,
            updatedOrder.capsuleCost || 0
          );
          const baseCostMain = tCost * qty;

          let costOzel = 0;
          if (selectedProductType === 'kanat' && updatedOrder.hasOzelTasarim && updatedOrder.ozelTasarimUsedM2 && updatedOrder.ozelTasarimUnitCost) {
            const ozelCostVal = calculateTotalCost(
              updatedOrder.ozelTasarimUsedM2,
              updatedOrder.ozelTasarimUnitCost,
              updatedOrder.ozelTasarimManualExtraCost || 0,
              updatedOrder.ozelTasarimPileModel,
              updatedOrder.ozelTasarimCapsuleCost || 0
            );
            const ozelQty = parseFloat(updatedOrder.ozelTasarimQuantity) || 1;
            costOzel = ozelCostVal * ozelQty;
          }

          let costAstar = 0;
          if (selectedProductType === 'kanat' && updatedOrder.hasAstar && updatedOrder.usedM2 && updatedOrder.astarUnitCost) {
            const astarCostVal = calculateAstarCost(updatedOrder.usedM2, updatedOrder.astarUnitCost, updatedOrder.quantity);
            costAstar = astarCostVal;
            updatedOrder.astarCost = astarCostVal.toFixed(2);
          } else {
            updatedOrder.astarCost = 0;
          }

          let costAccessories = 0;
          if ((selectedProductType === 'kanat' || selectedProductType === 'tul') && Array.isArray(updatedOrder.kanatAccessories)) {
            costAccessories = updatedOrder.kanatAccessories.reduce((sum, acc) => sum + (parseFloat(acc.costTotal) || 0), 0);
          }

          updatedOrder.totalCost = (baseCostMain + costOzel + costAstar + costAccessories).toFixed(2);
        }
      }

      return updatedOrder;
    });
  };

  // Yeni sipariş ekle veya mevcut siparişi güncelle
  const handleAddOrder = async (e, forceSave = false) => {
    if (e) e.preventDefault();

    // Stor/Zebra için ek özellik kontrolü
    if (!forceSave && (selectedProductType === 'stor' || selectedProductType === 'zebra')) {
      if (!hasAnyStorZebraExtra(newOrder, selectedProductType)) {
        setShowSystemConfirm(true);
        return;
      }
    }

    // customerId kontrolü
    if (!customerId) {
      showToast('Müşteri ID bulunamadı!');
      return;
    }

    // Geçici ID kontrolü (temp_ ile başlıyorsa local state'e ekle)
    if (customerId.startsWith('temp_')) {
      const tempOrder = {
        id: editingOrderId || Date.now(),
        customerId: customerId,
        productType: selectedProductType,
        ...newOrder,
        createdAt: new Date(),
        isTemp: true // Geçici sipariş işareti
      };

      if (editingOrderId) {
        // Edit modu - mevcut siparişi güncelle
        setOrders(orders.map(order =>
          order.id === editingOrderId ? tempOrder : order
        ));
      } else {
        // Yeni sipariş ekle
        setOrders([...orders, tempOrder]);
      }

      setIsAddingOrder(false);
      setEditingOrderId(null);
      setNewOrder({
        roomName: '',
        productName: '',
        productVariant: '',
        width: '',
        height: '',
        pileModel: '',
        pileDensity: '',
        usedM2: '',
        unitPrice: '',
        extraPrice: '',
        capsuleUnitPrice: '',
        capsulePrice: '',
        jumboSystem: '',
        reductor: '',
        bottomSkirt: '',
        closedCase: '',
        laser: '',
        mounting: 0,
        pliseItems: [],
        pliseProduct1Name: '',
        pliseProduct1Price: '',
        pliseProduct2Name: '',
        pliseProduct2Price: '',
        pliseProduct1Variation: '',
        pliseProduct2Variation: '',
        pliseCaseVariation: '',
        backgroundCurtain: '',
        blackout: '',
        crepTul: '',
        multisistem: '',
        honeycomb: '',
        standartPlise: '',
        fabricPrice: '',
        mechanismType: '',
        mechanismPrice: '',
        accessoryPrice: '',
        quantity: '',
        discount: '',
        totalPrice: '',
        discountedPrice: '',
        notes: '',
        direction: '',
        hasZebraJumbo: false,
        hasZebraAltEtekDilim: false,
        hasZebraKapaliKasa: false,
        zebraJumboAmount: 0,
        zebraAltEtekDilimAmount: 0,
        zebraKapaliKasaAmount: 0,
        hasOzelTasarim: false,
        ozelTasarimProductName: '',
        ozelTasarimProductVariant: '',
        ozelTasarimWidth: '',
        ozelTasarimHeight: '',
        ozelTasarimUnitPrice: '',
        ozelTasarimUnitCost: '',
        ozelTasarimBaseUnitPrice: '',
        ozelTasarimBaseUnitCost: '',
        ozelTasarimPileModel: '',
        ozelTasarimPileDensity: '',
        ozelTasarimUsedM2: '',
        ozelTasarimExtraPrice: '',
        ozelTasarimManualExtraCost: '',
        ozelTasarimQuantity: '',
        ozelTasarimCapsuleUnitPrice: '',
        ozelTasarimCapsuleUnitCost: '',
        ozelTasarimCapsulePrice: '',
        ozelTasarimCapsuleCost: '',
        kanatAccessories: [],
        ozelProjeKumasEni: '',
        ozelProjeTopBoyu: '',
        ozelProjeRapor: '',
        ozelProjeBoyPayi: '',
        hasOzelProje: false,
        ...emptyStorZebraExtraOrderFields()
      });
      setSelectedProductType('');
      return;
    }

    // Tül ve Kanat için gerekli alanları kontrol et
    if (selectedProductType === 'tul' || selectedProductType === 'kanat') {
      if (!newOrder.roomName || !newOrder.productName || !newOrder.width || !newOrder.height || !newOrder.pileModel || !newOrder.unitPrice) {
        showToast('Lütfen tüm zorunlu alanları doldurun!');
        return;
      }
    } else if (selectedProductType === 'stor' || selectedProductType === 'zebra') {
      // Stor ve Zebra için gerekli alanları kontrol et
      if (!newOrder.roomName || !newOrder.productName || !newOrder.width || !newOrder.height || !newOrder.unitPrice || !newOrder.direction) {
        showToast('Lütfen tüm zorunlu alanları doldurun! (Yön seçimi dahil)');
        return;
      }
    } else if (selectedProductType === 'ahsapJaluzi' || selectedProductType === 'metalJaluzi') {
      // Ahşap Jaluzi ve Metal Jaluzi için gerekli alanları kontrol et
      if (!newOrder.roomName || !newOrder.productName || !newOrder.width || !newOrder.height || !newOrder.unitPrice || !newOrder.direction) {
        showToast('Lütfen tüm zorunlu alanları doldurun! (Yön seçimi dahil)');
        return;
      }
    } else if (selectedProductType === 'dikeyPerde' || selectedProductType === 'dikeyPvc' || selectedProductType === 'dikeyStor') {
      // Dikey ürünler için gerekli alanları kontrol et
      if (!newOrder.roomName || !newOrder.productName || !newOrder.width || !newOrder.height || !newOrder.unitPrice || !newOrder.direction) {
        showToast('Lütfen tüm zorunlu alanları doldurun! (Yön seçimi dahil)');
        return;
      }

      // Fon seçiliyse, Fon Adı ve Fon Sayısı zorunludur ve sayı geçerli olmalıdır
      if (newOrder.hasDikeyFon) {
        if (!newOrder.dikeyFonName || String(newOrder.dikeyFonName).trim() === '') {
          showToast('Lütfen Fon Adı alanını doldurun!');
          return;
        }
        if (!newOrder.dikeyFonCount || parseInt(newOrder.dikeyFonCount) <= 0) {
          showToast('Fon Sayısı en az 1 olmalıdır!');
          return;
        }
      }
    } else if (selectedProductType === 'plise') {
      // Plise için gerekli alanları kontrol et
      if (!newOrder.roomName || !newOrder.pliseProduct1Name || !newOrder.pliseProduct1Price) {
        showToast('Lütfen Oda Adı, 1. Ürün Adı ve 1. Ürün Fiyatı alanlarını doldurun!');
        return;
      }
      if (!Array.isArray(newOrder.pliseItems) || newOrder.pliseItems.length === 0) {
        showToast('Lütfen en az bir ölçü ekleyin!');
        return;
      }
      // Her satırı kontrol et
      const invalidItem = newOrder.pliseItems.find(item => !item.width || !item.height || !item.quantity);
      if (invalidItem) {
        showToast('Lütfen listedeki tüm ölçü ve adet alanlarını doldurun!');
        return;
      }
    } else if (selectedProductType === 'katlamaliPerde') {
      // Katlamalı Perde için gerekli alanları kontrol et
      if (!newOrder.roomName || !newOrder.productName || !newOrder.width || !newOrder.height || !newOrder.fabricPrice || !newOrder.mechanismType || !newOrder.mechanismPrice) {
        showToast('Lütfen tüm zorunlu alanları doldurun!');
        return;
      }
    } else if (selectedProductType === 'aksesuar') {
      // Aksesuar için gerekli alanları kontrol et
      if (!newOrder.roomName || !newOrder.productName || !newOrder.unitPrice || !newOrder.quantity) {
        showToast('Lütfen tüm zorunlu alanları doldurun!');
        return;
      }
    } else if (selectedProductType === 'rustik') {
      // Rustik için gerekli alanları kontrol et
      if (!newOrder.roomName || !newOrder.productName || !newOrder.width || !newOrder.unitPrice ||
        !newOrder.ayakModel || !newOrder.ayakCap || !newOrder.ayakUzunluk || !newOrder.ayakRenkKodu || !newOrder.ayakSayisi) {
        showToast('Lütfen tüm zorunlu alanları doldurun!');
        return;
      }
    } else {
      // Diğer ürün tipleri için genel kontrol
      if (!newOrder.roomName || !newOrder.productName) {
        showToast('Lütfen tüm zorunlu alanları doldurun!');
        return;
      }
    }

    // Kaydetmeden önce Plise maliyetini son kez hesapla (Emin olmak için)
    let finalTotalCost = newOrder.totalCost;
    if (selectedProductType === 'plise') {
      const { totalCost } = calculatePliseBatchTotals(
        newOrder.pliseItems,
        newOrder.pliseProduct1Price,
        newOrder.pliseProduct2Price,
        newOrder.mounting,
        newOrder.discount,
        newOrder.quantity,
        newOrder.extraPrice,
        newOrder.pliseProduct1Cost,
        newOrder.pliseProduct2Cost,
        newOrder.manualExtraCost,
        newOrder.mountingCost
      );
      finalTotalCost = totalCost;
    }

    // Kaydetmeden önce Tül ve Kanat toplamlarını son kez doğru hesapla
    let finalTotalPrice = newOrder.totalPrice;
    let finalDiscountedPrice = newOrder.discountedPrice;
    if (selectedProductType === 'tul' || selectedProductType === 'kanat') {
      const qty = parseFloat(newOrder.quantity) || 1;
      // Ana ürün fiyatı
      let priceMain = 0;
      if (newOrder.usedM2 && newOrder.unitPrice) {
        const base = calculateTotalPrice(
          newOrder.usedM2,
          newOrder.unitPrice,
          newOrder.extraPrice,
          newOrder.pileModel,
          newOrder.capsulePrice
        );
        priceMain = base * qty;
      }

      // Özel tasarım fiyatı (sadece kanat)
      let priceOzel = 0;
      if (selectedProductType === 'kanat' && newOrder.hasOzelTasarim && newOrder.ozelTasarimUsedM2 && newOrder.ozelTasarimUnitPrice) {
        const baseOzel = calculateTotalPrice(
          newOrder.ozelTasarimUsedM2,
          newOrder.ozelTasarimUnitPrice,
          newOrder.ozelTasarimExtraPrice,
          newOrder.ozelTasarimPileModel,
          newOrder.ozelTasarimCapsulePrice
        );
        const ozelQty = parseFloat(newOrder.ozelTasarimQuantity) || 1;
        priceOzel = baseOzel * ozelQty;
        newOrder.ozelTasarimTotalPrice = priceOzel.toFixed(2);
      } else {
        newOrder.ozelTasarimTotalPrice = 0;
      }

      let priceAstar = 0;
      if (selectedProductType === 'kanat' && newOrder.hasAstar && newOrder.usedM2 && newOrder.astarUnitPrice) {
        priceAstar = calculateAstarPrice(newOrder.usedM2, newOrder.astarUnitPrice, newOrder.quantity);
        newOrder.astarPrice = priceAstar.toFixed(2);
      }

      // Aksesuar fiyatları
      let priceAccessories = 0;
      if (Array.isArray(newOrder.kanatAccessories)) {
        priceAccessories = newOrder.kanatAccessories.reduce((sum, acc) => sum + (parseFloat(acc.total) || 0), 0);
      }

      finalTotalPrice = (priceMain + priceOzel + priceAstar + priceAccessories).toFixed(2);

      // İndirimli tutar
      if (finalTotalPrice && newOrder.discount) {
        finalDiscountedPrice = calculateDiscountedPrice(finalTotalPrice, newOrder.discount).toFixed(2);
      }

      // Maliyet hesabı
      const tCostMain = calculateTotalCost(
        newOrder.usedM2,
        newOrder.unitCost,
        newOrder.manualExtraCost || 0,
        newOrder.pileModel,
        newOrder.capsuleCost || 0
      );
      const baseCostMain = (isNaN(tCostMain) ? 0 : tCostMain) * qty;

      let costOzel = 0;
      if (selectedProductType === 'kanat' && newOrder.hasOzelTasarim && newOrder.ozelTasarimUsedM2 && newOrder.ozelTasarimUnitCost) {
        const ozelCostVal = calculateTotalCost(
          newOrder.ozelTasarimUsedM2,
          newOrder.ozelTasarimUnitCost,
          newOrder.ozelTasarimManualExtraCost || 0,
          newOrder.ozelTasarimPileModel,
          newOrder.ozelTasarimCapsuleCost || 0
        );
        const ozelQty = parseFloat(newOrder.ozelTasarimQuantity) || 1;
        costOzel = (isNaN(ozelCostVal) ? 0 : ozelCostVal) * ozelQty;
      }

      let costAstar = 0;
      if (selectedProductType === 'kanat' && newOrder.hasAstar && newOrder.usedM2 && newOrder.astarUnitCost) {
        const astarCostVal = calculateAstarCost(newOrder.usedM2, newOrder.astarUnitCost, newOrder.quantity);
        costAstar = isNaN(astarCostVal) ? 0 : astarCostVal;
        newOrder.astarCost = costAstar.toFixed(2);
      }

      let costAccessories = 0;
      if (Array.isArray(newOrder.kanatAccessories)) {
        costAccessories = newOrder.kanatAccessories.reduce((sum, acc) => sum + (parseFloat(acc.costTotal) || 0), 0);
      }

      finalTotalCost = (baseCostMain + costOzel + costAstar + costAccessories).toFixed(2);
    }


    // undefined değerleri temizle (Firebase undefined kabul etmez)
    const cleanOrder = Object.entries(newOrder).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});

    const order = {
      customerId: customerId, // Müşteri ID'sini ekle
      productType: selectedProductType,
      ...cleanOrder,
      totalCost: finalTotalCost,
      ...(selectedProductType === 'tul' || selectedProductType === 'kanat' ? {
        totalPrice: finalTotalPrice,
        discountedPrice: finalDiscountedPrice,
      } : {})
    };

    // Yeni sipariş için createdAt ekle (düzenlemede ekleme)
    if (!editingOrderId) {
      order.createdAt = new Date();
    }

    try {
      if (editingOrderId) {
        // Edit modu - mevcut siparişi güncelle
        await updateDoc(doc(db, 'orders', editingOrderId), order);
      } else {
        // Yeni sipariş ekle
        const orderRef = await addDoc(collection(db, 'orders'), order);
        // Keşif ölçüsünden açıldıysa ölçüyü "siparişte" olarak işaretle
        if (convertingKesifId) {
          await saveKesifItems(kesifItems.map(item => (
            item.id === convertingKesifId ? { ...item, convertedOrderId: orderRef.id, convertedAt: new Date().toISOString() } : item
          )));
        }
      }
      setConvertingKesifId(null);

      fetchOrders(); // Sipariş listesini yeniden çek
      setIsAddingOrder(false);
      setEditingOrderId(null);
      setNewOrder({
        roomName: '',
        productName: '',
        productVariant: '',
        width: '',
        height: '',
        pileModel: '',
        pileDensity: '',
        usedM2: '',
        unitPrice: '',
        extraPrice: '',
        capsuleUnitPrice: '',
        capsulePrice: '',
        jumboSystem: '',
        reductor: '',
        bottomSkirt: '',
        closedCase: '',
        laser: '',
        mounting: 0,
        pliseItems: [],
        pliseProduct1Name: '',
        pliseProduct1Price: '',
        pliseProduct2Name: '',
        pliseProduct2Price: '',
        pliseProduct1Variation: '',
        pliseProduct2Variation: '',
        pliseCaseVariation: '',
        backgroundCurtain: '',
        blackout: '',
        crepTul: '',
        multisistem: '',
        honeycomb: '',
        standartPlise: '',
        fabricPrice: '',
        mechanismType: '',
        mechanismPrice: '',
        accessoryPrice: '',
        quantity: '',
        discount: '',
        totalPrice: '',
        discountedPrice: '',
        notes: '',
        direction: '',
        hasZebraJumbo: false,
        hasZebraAltEtekDilim: false,
        hasZebraKapaliKasa: false,
        zebraJumboAmount: 0,
        zebraAltEtekDilimAmount: 0,
        zebraKapaliKasaAmount: 0,
        hasOzelTasarim: false,
        ozelTasarimProductName: '',
        ozelTasarimProductVariant: '',
        ozelTasarimWidth: '',
        ozelTasarimHeight: '',
        ozelTasarimUnitPrice: '',
        ozelTasarimUnitCost: '',
        ozelTasarimBaseUnitPrice: '',
        ozelTasarimBaseUnitCost: '',
        ozelTasarimPileModel: '',
        ozelTasarimPileDensity: '',
        ozelTasarimUsedM2: '',
        ozelTasarimExtraPrice: '',
        ozelTasarimManualExtraCost: '',
        ozelTasarimQuantity: '',
        ozelTasarimCapsuleUnitPrice: '',
        ozelTasarimCapsuleUnitCost: '',
        ozelTasarimCapsulePrice: '',
        ozelTasarimCapsuleCost: '',
        kanatAccessories: [],
        ozelProjeKumasEni: '',
        ozelProjeTopBoyu: '',
        ozelProjeRapor: '',
        ozelProjeBoyPayi: '',
        hasOzelProje: false,
        ...emptyStorZebraExtraOrderFields()
      });
      setSelectedProductType('');

      // Ekranı yukarı scroll et (mobilde input alanları alt alta olduğu için)
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    } catch (error) {
      console.error('Sipariş eklenirken hata:', error);
      showToast('Sipariş eklenirken bir hata oluştu: ' + error.message);
    }
  };

  // Sipariş silme onayı
  const handleDeleteConfirm = (order) => {
    setDeleteConfirm(order);
  };

  // Sipariş sil
  const handleDeleteOrder = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteDoc(doc(db, 'orders', deleteConfirm.id));
      setDeleteConfirm(null);
      fetchOrders(); // Sipariş silindikten sonra listeyi yeniden çek
    } catch (error) {
      console.error('Sipariş silinirken hata:', error);
      showToast('Sipariş silinirken bir hata oluştu.');
    }
  };

  // Sipariş düzenle
  const handleEditOrder = (order) => {
    setSelectedProductType(order.productType);
    // Plise için geriye dönük uyumluluk dönüşümü
    let pliseItems = order.pliseItems || [];
    if (order.productType === 'plise' && (!pliseItems || pliseItems.length === 0) && order.width && order.height) {
      pliseItems = [{
        id: Date.now(),
        width: order.width,
        height: order.height,
        usedM2: order.usedM2 || '1.00',
        quantity: order.quantity || '1',
        multisistem: order.multisistem || false,
        rowTotal: order.totalPrice || '0.00'
      }];
    }

    const initialOrder = {
      roomName: order.roomName,
      productName: order.productName,
      productVariant: order.productVariant || '',
      width: order.width,
      height: order.height,
      pileModel: order.pileModel,
      pileDensity: order.pileDensity,
      usedM2: order.usedM2,
      usedCm: order.usedCm,
      usedMeter: order.usedMeter,
      olcuTamamlama: order.olcuTamamlama,
      unitPrice: order.unitPrice,
      unitCost: order.unitCost,
      mountingCost: order.mountingCost || '',
      rayExtraPrice: order.rayExtraPrice || '', // Ray sistemi: adet başına ek ücret
      rayExtraCost: order.rayExtraCost || '', // Ray sistemi: adet başına ek ücret maliyeti
      extraPrice: order.extraPrice,
      manualExtraCost: order.manualExtraCost || '',
      capsuleUnitPrice: order.capsuleUnitPrice,
      capsuleUnitCost: order.capsuleUnitCost,
      capsulePrice: order.capsulePrice,
      capsuleCost: order.capsuleCost,
      jumboSystem: order.jumboSystem,
      reductor: order.reductor,
      bottomSkirt: order.bottomSkirt,
      closedCase: order.closedCase,
      laser: order.laser,
      mounting: order.mounting,
      backgroundCurtain: order.backgroundCurtain,
      blackout: order.blackout,
      crepTul: order.crepTul,
      multisistem: order.multisistem,
      honeycomb: order.honeycomb,
      standartPlise: order.standartPlise,
      fabricPrice: order.fabricPrice,
      mechanismType: order.mechanismType,
      mechanismPrice: order.mechanismPrice,
      mechanismCost: order.mechanismCost || 0,
      accessoryPrice: order.accessoryPrice,
      accessories: order.accessories || [],
      rustikAksesuarlar: order.rustikAksesuarlar || [],
      rustikRaySecenekleri: order.rustikRaySecenekleri || [],
      ayakModel: order.ayakModel || '',
      ayakCap: order.ayakCap || '',
      ayakUzunluk: order.ayakUzunluk || '',
      ayakRenkKodu: order.ayakRenkKodu || '',
      ayakSayisi: order.ayakSayisi || '',
      ekAyakSayisi: order.ekAyakSayisi || '',
      ayakUcreti: order.ayakUcreti || 0,
      ayakUcretiCost: order.ayakUcretiCost || 0,
      aksesuarBirimTipi: order.aksesuarBirimTipi || 'adet',
      quantity: order.quantity,
      discount: order.discount,
      totalPrice: order.totalPrice,
      totalCost: order.totalCost,
      motorCost: order.motorCost || 0,
      discountedPrice: order.discountedPrice,
      notes: order.notes,
      supplierOrders: order.supplierOrders, // Firmaya geçilen sipariş notları
      motorType: order.motorType || '',
      motorPrice: order.motorPrice || 0,
      motorPriceCurrency: order.motorPriceCurrency || '',
      kumandaPriceCurrency: order.kumandaPriceCurrency || '',
      direction: order.direction || '',
      // Plise Özel
      pliseItems: pliseItems,
      pliseProduct1Name: order.pliseProduct1Name || (order.productType === 'plise' ? order.productName : ''),
      pliseProduct1Price: order.pliseProduct1Price || (order.productType === 'plise' ? (order.unitPrice || 0) : 0),
      pliseProduct1Cost: order.pliseProduct1Cost || (order.productType === 'plise' ? (order.unitCost || 0) : 0),
      pliseProduct2Name: order.pliseProduct2Name || '',
      pliseProduct2Price: order.pliseProduct2Price || 0,
      pliseProduct2Cost: order.pliseProduct2Cost || 0,
      pliseProduct1Variation: order.pliseProduct1Variation || '',
      pliseProduct2Variation: order.pliseProduct2Variation || '',
      pliseCaseVariation: order.pliseCaseVariation || '',
      hasStorJumbo: order.hasStorJumbo || false,
      hasAltEtekKatlama: order.hasAltEtekKatlama || false,
      hasOzelOlcu: order.hasOzelOlcu || false,
      hasStorAltEtekDilim: order.hasStorAltEtekDilim || false,
      hasStorKapaliKasa: order.hasStorKapaliKasa || false,
      hasStorAltEtekLazer: order.hasStorAltEtekLazer || false,
      hasStorDekoratifLama: order.hasStorDekoratifLama || false,
      hasStorDekoratifKasa: order.hasStorDekoratifKasa || false,
      hasStorAltEtekBeyazIs: order.hasStorAltEtekBeyazIs || false,
      storJumboAmount: order.storJumboAmount || 0,
      storAltEtekDilimAmount: order.storAltEtekDilimAmount || 0,
      storKapaliKasaAmount: order.storKapaliKasaAmount || 0,
      storAltEtekLazerAmount: order.storAltEtekLazerAmount || 0,
      storDekoratifLamaAmount: order.storDekoratifLamaAmount || 0,
      storDekoratifKasaAmount: order.storDekoratifKasaAmount || 0,
      storAltEtekBeyazIsAmount: order.storAltEtekBeyazIsAmount || 0,
      storJumboCostAmount: order.storJumboCostAmount || 0,
      storAltEtekDilimCostAmount: order.storAltEtekDilimCostAmount || 0,
      storKapaliKasaCostAmount: order.storKapaliKasaCostAmount || 0,
      storAltEtekLazerCostAmount: order.storAltEtekLazerCostAmount || 0,
      storDekoratifLamaCostAmount: order.storDekoratifLamaCostAmount || 0,
      storDekoratifKasaCostAmount: order.storDekoratifKasaCostAmount || 0,
      storAltEtekBeyazIsCostAmount: order.storAltEtekBeyazIsCostAmount || 0,
      hasZebraJumbo: order.hasZebraJumbo || false,
      hasZebraAltEtekDilim: order.hasZebraAltEtekDilim || false,
      hasZebraKapaliKasa: order.hasZebraKapaliKasa || false,
      zebraJumboAmount: order.zebraJumboAmount || 0,
      zebraAltEtekDilimAmount: order.zebraAltEtekDilimAmount || 0,
      zebraKapaliKasaAmount: order.zebraKapaliKasaAmount || 0,
      zebraJumboCostAmount: order.zebraJumboCostAmount || 0,
      zebraAltEtekDilimCostAmount: order.zebraAltEtekDilimCostAmount || 0,
      zebraKapaliKasaCostAmount: order.zebraKapaliKasaCostAmount || 0,
      hasDikeyFon: order.hasDikeyFon || false,
      dikeyFonName: order.dikeyFonName || '',
      dikeyFonCount: order.dikeyFonCount || 1,
      dikeyFonAmount: order.dikeyFonAmount || 0,
      // Ahşap Jaluzi
      sistemTipi: order.sistemTipi || '',
      hasZigil: order.hasZigil || false,
      zigilAmount: order.zigilAmount || 0,
      zigilCostAmount: order.zigilCostAmount || 0,
      hasReduktor: order.hasReduktor || false,
      hasKurdela: order.hasKurdela || false,
      baseUnitPrice: order.baseUnitPrice !== undefined && order.baseUnitPrice !== '' ? order.baseUnitPrice : (order.unitPrice || ''),
      baseUnitCost: order.baseUnitCost !== undefined && order.baseUnitCost !== '' ? order.baseUnitCost : (order.unitCost || ''),
      // Metal Jaluzi
      hasMetalZigil: order.hasMetalZigil || false,
      metalZigilAmount: order.metalZigilAmount || 0,
      metalZigilCostAmount: order.metalZigilCostAmount || 0,
      hasMetalReduktor: order.hasMetalReduktor || false,
      hasIkiCamArasi: order.hasIkiCamArasi || false,
      ikiCamArasiCostAmount: order.ikiCamArasiCostAmount || 0,
      hasOzelTasarim: order.hasOzelTasarim || false,
      ozelTasarimProductName: order.ozelTasarimProductName || '',
      ozelTasarimProductVariant: order.ozelTasarimProductVariant || '',
      ozelTasarimWidth: order.ozelTasarimWidth || '',
      ozelTasarimHeight: order.ozelTasarimHeight || '',
      ozelTasarimUnitPrice: order.ozelTasarimUnitPrice || '',
      ozelTasarimUnitCost: order.ozelTasarimUnitCost || '',
      ozelTasarimBaseUnitPrice: order.ozelTasarimBaseUnitPrice || '',
      ozelTasarimBaseUnitCost: order.ozelTasarimBaseUnitCost || '',
      ozelTasarimPileModel: order.ozelTasarimPileModel || '',
      ozelTasarimPileDensity: order.ozelTasarimPileDensity || '',
      ozelTasarimUsedM2: order.ozelTasarimUsedM2 || '',
      ozelTasarimExtraPrice: order.ozelTasarimExtraPrice || '',
      ozelTasarimManualExtraCost: order.ozelTasarimManualExtraCost || '',
      ozelTasarimQuantity: order.ozelTasarimQuantity || '',
      ozelTasarimCapsuleUnitPrice: order.ozelTasarimCapsuleUnitPrice || '',
      ozelTasarimCapsuleUnitCost: order.ozelTasarimCapsuleUnitCost || '',
      ozelTasarimCapsulePrice: order.ozelTasarimCapsulePrice || '',
      ozelTasarimCapsuleCost: order.ozelTasarimCapsuleCost || '',
      ozelTasarimTotalPrice: order.ozelTasarimTotalPrice || '',
      hasAstar: order.hasAstar || false,
      astarProductName: order.astarProductName || '',
      astarProductVariant: order.astarProductVariant || '',
      astarUnitPrice: order.astarUnitPrice || '',
      astarUnitCost: order.astarUnitCost || '',
      astarPrice: order.astarPrice || '',
      astarCost: order.astarCost || '',
      kanatAccessories: order.kanatAccessories || [],
      hasOzelProje: order.hasOzelProje || false,
      ozelProjeKumasEni: order.ozelProjeKumasEni || '',
      ozelProjeTopBoyu: order.ozelProjeTopBoyu || '',
      ozelProjeRapor: order.ozelProjeRapor || '',
      ozelProjeBoyPayi: order.ozelProjeBoyPayi || '',
      ...pickStorZebraExtraOrderFields(order)
    };

    // Plise için hesaplamaları önden yap (Legacy dönüşüm sonrası doğru tutar için)
    if (order.productType === 'plise') {
      const { items, totalPrice, discountedPrice } = calculatePliseBatchTotals(
        pliseItems,
        initialOrder.pliseProduct1Price,
        initialOrder.pliseProduct2Price,
        initialOrder.mounting,
        initialOrder.discount
      );
      initialOrder.pliseItems = items;
      initialOrder.totalPrice = totalPrice;
      initialOrder.discountedPrice = discountedPrice;
    }

    setNewOrder(initialOrder);
    setIsAddingOrder(true);
    setEditingOrderId(order.id);

    // Form açıldıktan sonra scroll et
    setTimeout(() => {
      const orderFormElement = document.querySelector('.order-form');
      if (orderFormElement) {
        orderFormElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        const orderDetailsElement = document.querySelector('.order-details');
        if (orderDetailsElement) {
          orderDetailsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }, 100);
  };

  // Sipariş kopyala
  const handleCopyOrder = (order) => {
    setSelectedProductType(order.productType);

    // Plise için geriye dönük uyumluluk dönüşümü
    let pliseItems = order.pliseItems || [];
    if (order.productType === 'plise' && (!pliseItems || pliseItems.length === 0) && order.width && order.height) {
      pliseItems = [{
        id: Date.now(),
        width: order.width,
        height: order.height,
        usedM2: order.usedM2 || '1.00',
        quantity: order.quantity || '1',
        multisistem: order.multisistem || false,
        rowTotal: order.totalPrice || '0.00'
      }];
    }

    // Kopyalanan siparişin verilerini set et
    const copiedOrder = {
      roomName: order.roomName,
      productName: order.productName,
      productVariant: order.productVariant,
      width: order.width,
      height: order.height,
      pileModel: order.pileModel,
      pileDensity: order.pileDensity,
      usedM2: order.usedM2,
      usedCm: order.usedCm,
      usedMeter: order.usedMeter,
      olcuTamamlama: order.olcuTamamlama,
      unitPrice: order.unitPrice,
      unitCost: order.unitCost,
      mountingCost: order.mountingCost || '',
      rayExtraPrice: order.rayExtraPrice || '', // Ray sistemi: adet başına ek ücret
      rayExtraCost: order.rayExtraCost || '', // Ray sistemi: adet başına ek ücret maliyeti
      extraPrice: order.extraPrice,
      manualExtraCost: order.manualExtraCost || '',
      capsuleUnitPrice: order.capsuleUnitPrice,
      capsuleUnitCost: order.capsuleUnitCost,
      capsulePrice: order.capsulePrice,
      capsuleCost: order.capsuleCost,
      jumboSystem: order.jumboSystem,
      reductor: order.reductor,
      bottomSkirt: order.bottomSkirt,
      closedCase: order.closedCase,
      laser: order.laser,
      mounting: order.mounting,
      backgroundCurtain: order.backgroundCurtain,
      blackout: order.blackout,
      crepTul: order.crepTul,
      multisistem: order.multisistem,
      honeycomb: order.honeycomb,
      standartPlise: order.standartPlise,
      fabricPrice: order.fabricPrice,
      mechanismType: order.mechanismType,
      mechanismPrice: order.mechanismPrice,
      mechanismCost: order.mechanismCost || 0,
      accessoryPrice: order.accessoryPrice,
      accessories: order.accessories || [],
      rustikAksesuarlar: order.rustikAksesuarlar || [],
      rustikRaySecenekleri: order.rustikRaySecenekleri || [],
      ayakModel: order.ayakModel || '',
      ayakCap: order.ayakCap || '',
      ayakUzunluk: order.ayakUzunluk || '',
      ayakRenkKodu: order.ayakRenkKodu || '',
      ayakSayisi: order.ayakSayisi || '',
      ekAyakSayisi: order.ekAyakSayisi || '',
      ayakUcreti: order.ayakUcreti || 0,
      ayakUcretiCost: order.ayakUcretiCost || 0,
      aksesuarBirimTipi: order.aksesuarBirimTipi || 'adet',
      quantity: order.quantity,
      discount: order.discount,
      totalPrice: order.totalPrice,
      totalCost: order.totalCost,
      motorCost: order.motorCost || 0,
      discountedPrice: order.discountedPrice,
      notes: order.notes,
      motorType: order.motorType || '',
      motorPrice: order.motorPrice || 0,
      motorPriceCurrency: order.motorPriceCurrency || '',
      kumandaPriceCurrency: order.kumandaPriceCurrency || '',
      direction: order.direction || '',
      // Plise Özel
      pliseItems: pliseItems,
      pliseProduct1Name: order.pliseProduct1Name || (order.productType === 'plise' ? order.productName : ''),
      pliseProduct1Price: order.pliseProduct1Price || (order.productType === 'plise' ? (order.unitPrice || 0) : 0),
      pliseProduct1Cost: order.pliseProduct1Cost || (order.productType === 'plise' ? (order.unitCost || 0) : 0),
      pliseProduct2Name: order.pliseProduct2Name || '',
      pliseProduct2Price: order.pliseProduct2Price || 0,
      pliseProduct2Cost: order.pliseProduct2Cost || 0,
      pliseProduct1Variation: order.pliseProduct1Variation || '',
      pliseProduct2Variation: order.pliseProduct2Variation || '',
      pliseCaseVariation: order.pliseCaseVariation || '',
      hasStorJumbo: order.hasStorJumbo || false,
      hasAltEtekKatlama: order.hasAltEtekKatlama || false,
      hasOzelOlcu: order.hasOzelOlcu || false,
      hasStorAltEtekDilim: order.hasStorAltEtekDilim || false,
      hasStorKapaliKasa: order.hasStorKapaliKasa || false,
      hasStorAltEtekLazer: order.hasStorAltEtekLazer || false,
      hasStorDekoratifLama: order.hasStorDekoratifLama || false,
      hasStorDekoratifKasa: order.hasStorDekoratifKasa || false,
      hasStorAltEtekBeyazIs: order.hasStorAltEtekBeyazIs || false,
      storJumboAmount: order.storJumboAmount || 0,
      storAltEtekDilimAmount: order.storAltEtekDilimAmount || 0,
      storKapaliKasaAmount: order.storKapaliKasaAmount || 0,
      storAltEtekLazerAmount: order.storAltEtekLazerAmount || 0,
      storDekoratifLamaAmount: order.storDekoratifLamaAmount || 0,
      storDekoratifKasaAmount: order.storDekoratifKasaAmount || 0,
      storAltEtekBeyazIsAmount: order.storAltEtekBeyazIsAmount || 0,
      storJumboCostAmount: order.storJumboCostAmount || 0,
      storAltEtekDilimCostAmount: order.storAltEtekDilimCostAmount || 0,
      storKapaliKasaCostAmount: order.storKapaliKasaCostAmount || 0,
      storAltEtekLazerCostAmount: order.storAltEtekLazerCostAmount || 0,
      storDekoratifLamaCostAmount: order.storDekoratifLamaCostAmount || 0,
      storDekoratifKasaCostAmount: order.storDekoratifKasaCostAmount || 0,
      storAltEtekBeyazIsCostAmount: order.storAltEtekBeyazIsCostAmount || 0,
      hasZebraJumbo: order.hasZebraJumbo || false,
      hasZebraAltEtekDilim: order.hasZebraAltEtekDilim || false,
      hasZebraKapaliKasa: order.hasZebraKapaliKasa || false,
      zebraJumboAmount: order.zebraJumboAmount || 0,
      zebraAltEtekDilimAmount: order.zebraAltEtekDilimAmount || 0,
      zebraKapaliKasaAmount: order.zebraKapaliKasaAmount || 0,
      zebraJumboCostAmount: order.zebraJumboCostAmount || 0,
      zebraAltEtekDilimCostAmount: order.zebraAltEtekDilimCostAmount || 0,
      zebraKapaliKasaCostAmount: order.zebraKapaliKasaCostAmount || 0,
      hasDikeyFon: order.hasDikeyFon || false,
      dikeyFonName: order.dikeyFonName || '',
      dikeyFonCount: order.dikeyFonCount || 1,
      dikeyFonAmount: order.dikeyFonAmount || 0,
      // Ahşap Jaluzi
      sistemTipi: order.sistemTipi || '',
      hasZigil: order.hasZigil || false,
      zigilAmount: order.zigilAmount || 0,
      zigilCostAmount: order.zigilCostAmount || 0,
      hasReduktor: order.hasReduktor || false,
      hasKurdela: order.hasKurdela || false,
      baseUnitPrice: order.baseUnitPrice !== undefined && order.baseUnitPrice !== '' ? order.baseUnitPrice : (order.unitPrice || ''),
      baseUnitCost: order.baseUnitCost !== undefined && order.baseUnitCost !== '' ? order.baseUnitCost : (order.unitCost || ''),
      // Metal Jaluzi
      hasMetalZigil: order.hasMetalZigil || false,
      metalZigilAmount: order.metalZigilAmount || 0,
      metalZigilCostAmount: order.metalZigilCostAmount || 0,
      hasMetalReduktor: order.hasMetalReduktor || false,
      hasIkiCamArasi: order.hasIkiCamArasi || false,
      ikiCamArasiCostAmount: order.ikiCamArasiCostAmount || 0,
      hasOzelTasarim: order.hasOzelTasarim || false,
      ozelTasarimProductName: order.ozelTasarimProductName || '',
      ozelTasarimProductVariant: order.ozelTasarimProductVariant || '',
      ozelTasarimWidth: order.ozelTasarimWidth || '',
      ozelTasarimHeight: order.ozelTasarimHeight || '',
      ozelTasarimUnitPrice: order.ozelTasarimUnitPrice || '',
      ozelTasarimUnitCost: order.ozelTasarimUnitCost || '',
      ozelTasarimBaseUnitPrice: order.ozelTasarimBaseUnitPrice || '',
      ozelTasarimBaseUnitCost: order.ozelTasarimBaseUnitCost || '',
      ozelTasarimPileModel: order.ozelTasarimPileModel || '',
      ozelTasarimPileDensity: order.ozelTasarimPileDensity || '',
      ozelTasarimUsedM2: order.ozelTasarimUsedM2 || '',
      ozelTasarimExtraPrice: order.ozelTasarimExtraPrice || '',
      ozelTasarimManualExtraCost: order.ozelTasarimManualExtraCost || '',
      ozelTasarimQuantity: order.ozelTasarimQuantity || '',
      ozelTasarimCapsuleUnitPrice: order.ozelTasarimCapsuleUnitPrice || '',
      ozelTasarimCapsuleUnitCost: order.ozelTasarimCapsuleUnitCost || '',
      ozelTasarimCapsulePrice: order.ozelTasarimCapsulePrice || '',
      ozelTasarimCapsuleCost: order.ozelTasarimCapsuleCost || '',
      ozelTasarimTotalPrice: order.ozelTasarimTotalPrice || '',
      hasAstar: order.hasAstar || false,
      astarProductName: order.astarProductName || '',
      astarProductVariant: order.astarProductVariant || '',
      astarUnitPrice: order.astarUnitPrice || '',
      astarUnitCost: order.astarUnitCost || '',
      astarPrice: order.astarPrice || '',
      astarCost: order.astarCost || '',
      kanatAccessories: order.kanatAccessories || [],
      hasOzelProje: order.hasOzelProje || false,
      ozelProjeKumasEni: order.ozelProjeKumasEni || '',
      ozelProjeTopBoyu: order.ozelProjeTopBoyu || '',
      ozelProjeRapor: order.ozelProjeRapor || '',
      ozelProjeBoyPayi: order.ozelProjeBoyPayi || '',
      ...pickStorZebraExtraOrderFields(order)
    };

    // Plise için hesaplamaları önden yap (Kopyalama sonrası doğru tutar ve maliyet için)
    if (order.productType === 'plise') {
      const { items, totalPrice, discountedPrice, totalCost } = calculatePliseBatchTotals(
        pliseItems,
        copiedOrder.pliseProduct1Price,
        copiedOrder.pliseProduct2Price,
        copiedOrder.mounting,
        copiedOrder.discount,
        copiedOrder.quantity,
        copiedOrder.extraPrice,
        copiedOrder.pliseProduct1Cost,
        copiedOrder.pliseProduct2Cost,
        copiedOrder.manualExtraCost,
        copiedOrder.mountingCost
      );
      copiedOrder.pliseItems = items;
      copiedOrder.totalPrice = totalPrice;
      copiedOrder.discountedPrice = discountedPrice;
      copiedOrder.totalCost = totalCost;
    }

    setNewOrder(copiedOrder);
    setIsAddingOrder(true); // Yeni sipariş moduna geç
    setEditingOrderId(null); // Edit modu değil, kopyalama modu

    // Form açıldıktan sonra scroll et
    setTimeout(() => {
      const orderFormElement = document.querySelector('.order-form');
      if (orderFormElement) {
        orderFormElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        const orderDetailsElement = document.querySelector('.order-details');
        if (orderDetailsElement) {
          orderDetailsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }, 100);
  };

  // Toplam tutarları hesapla
  const totalAmount = orders.reduce((sum, order) => {
    return sum + parseFloat(order.totalPrice || 0);
  }, 0);

  // İndirimli tutarı hesapla (eğer siparişte indirimli tutar varsa onu, yoksa toplam tutarı kullan)
  const discountedTotalAmount = orders.reduce((sum, order) => {
    if (order.discountedPrice && parseFloat(order.discount) > 0) {
      return sum + parseFloat(order.discountedPrice || 0);
    } else {
      return sum + parseFloat(order.totalPrice || 0);
    }
  }, 0);

  // Genel iskonto hesaplama - Ara Toplam üzerinden
  const iskontoluToplam = genelIskontoYuzde && parseFloat(genelIskontoYuzde) > 0
    ? totalAmount - (totalAmount * parseFloat(genelIskontoYuzde) / 100)
    : totalAmount;

  // Vadeli toplam hesaplama - Ara Toplam üzerinden
  const vadeliToplam = vadeliYuzde && parseFloat(vadeliYuzde) > 0
    ? totalAmount + (totalAmount * parseFloat(vadeliYuzde) / 100)
    : totalAmount;

  // Genel toplam hesaplama (Hiyerarşi: Vade Farkı > Genel İskonto > Ürün İndirimi > Ara Toplam)
  const computedGrandTotal = (() => {
    const vYuzde = parseFloat(vadeliYuzde) || 0;
    const gIskonto = parseFloat(genelIskontoYuzde) || 0;

    if (vYuzde > 0) {
      return totalAmount + (totalAmount * vYuzde / 100);
    }
    if (gIskonto > 0) {
      return totalAmount - (totalAmount * gIskonto / 100);
    }
    if (discountedTotalAmount !== totalAmount && discountedTotalAmount > 0) {
      return discountedTotalAmount;
    }
    return totalAmount;
  })();

  const toplamOdeme = manualGrandTotal !== '' && manualGrandTotal !== null && !isNaN(parseFloat(manualGrandTotal)) 
    ? parseFloat(manualGrandTotal) 
    : computedGrandTotal;

  // Kalan ödeme hesaplama
  const calculateRemainingAmount = () => {
    const paid = parseFloat(paidAmount) || 0;

    // Eğer ödeme tamamlandı checkbox'ı işaretliyse, kalan 0
    if (isPaymentCompleted) {
      return 0;
    }

    // Genel toplam (toplamOdeme) üzerinden kalanı hesapla
    return toplamOdeme - paid;

    return toplamOdeme - paid;
  };

  const remainingAmount = calculateRemainingAmount();

  // Ödeme bilgileri değiştiğinde parent'a bildir (yeni müşteri ekleme modu için)
  useEffect(() => {
    if (!isEditMode && onCustomerSaved && customerId && !customerId.startsWith('temp_')) {
      const currentRemaining = calculateRemainingAmount();
      const paidValue = parseFloat(paidAmount) || 0;
      onCustomerSaved({
        paidAmount: paidValue,
        isPaymentCompleted: isPaymentCompleted,
        remainingAmount: currentRemaining
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidAmount, isPaymentCompleted, totalAmount, discountedTotalAmount, toplamOdeme, isEditMode, customerId, orders]);

  // Ödeme durumunu kaydet ve parent'a bildir
  const savePaymentState = async ({ paid, history, completed }) => {
    const remaining = completed ? 0 : Math.max(0, calculateTotalOrderAmount() - paid);
    await updateDoc(doc(db, 'customers', customerId), {
      paidAmount: paid,
      paymentHistory: history,
      isPaymentCompleted: completed,
      remainingAmount: remaining,
      updatedAt: new Date()
    });
    setPaymentHistory(history);
    setPaidAmount(paid.toString());
    setIsPaymentCompleted(completed);
    if (onCustomerSaved) {
      onCustomerSaved({
        ...customerInfo,
        paidAmount: paid,
        paymentHistory: history,
        isPaymentCompleted: completed,
        remainingAmount: remaining
      });
    }
  };

  // Ödeme tamamlandı checkbox değiştiğinde
  const handlePaymentCompletedChange = async (checked) => {
    if (!customerId || customerId.startsWith('temp_')) {
      setIsPaymentCompleted(checked);
      return;
    }

    const currentPaid = parseFloat(paidAmount) || 0;
    const remaining = Math.round((calculateTotalOrderAmount() - currentPaid) * 100) / 100;

    // Kalan ödeme varsa önce tarih ve ödeme tipi sorulur; onaylanınca ödeme geçmişine kayıt eklenir
    if (checked && remaining > 0) {
      setCompletePaymentForm({ date: toDateTimeLocal(), type: PAYMENT_TYPES.CASH });
      return;
    }

    try {
      setIsSavingPayment(true);
      if (checked) {
        // Kalan yok: sadece tamamlandı olarak işaretle
        await savePaymentState({ paid: currentPaid, history: paymentHistory, completed: true });
      } else {
        // İşaret kaldırılınca "Ödeme Tamamlandı" ile eklenen kayıt geri alınır
        const completionTotal = paymentHistory
          .filter(p => p.isCompletion)
          .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        const history = paymentHistory.filter(p => !p.isCompletion);
        const historyTotal = history.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        // Eski kayıtlarda tamamlama ödeme geçmişine yazılmıyordu; o durumda ödenen = geçmiş toplamı
        const paid = completionTotal > 0 ? Math.max(0, currentPaid - completionTotal) : historyTotal;
        await savePaymentState({ paid, history, completed: false });
      }
    } catch (error) {
      console.error('Ödeme tamamlandı güncellenirken hata:', error);
      showToast('Ödeme tamamlandı bilgisi kaydedilemedi: ' + error.message);
    } finally {
      setIsSavingPayment(false);
    }
  };

  // Kalan ödemeyi seçilen tarih ve tiple ödeme geçmişine ekleyip ödemeyi tamamla
  const handleConfirmCompletePayment = async () => {
    if (!completePaymentForm?.date) {
      showToast('Lütfen ödeme tarihini seçin.');
      return;
    }
    const currentPaid = parseFloat(paidAmount) || 0;
    const remaining = Math.round((calculateTotalOrderAmount() - currentPaid) * 100) / 100;
    if (remaining <= 0) {
      setCompletePaymentForm(null);
      return handlePaymentCompletedChange(true);
    }

    try {
      setIsSavingPayment(true);
      const completionPayment = {
        id: Date.now().toString(),
        amount: remaining,
        type: completePaymentForm.type,
        date: new Date(completePaymentForm.date).toISOString(),
        createdBy: user?.uid || null,
        isCompletion: true // "Ödeme Tamamlandı" ile eklendi (işaret kaldırılınca geri alınır)
      };
      await savePaymentState({
        paid: currentPaid + remaining,
        history: [...paymentHistory, completionPayment],
        completed: true
      });
      setCompletePaymentForm(null);
    } catch (error) {
      console.error('Ödeme tamamlanırken hata:', error);
      showToast('Ödeme tamamlanamadı: ' + error.message);
    } finally {
      setIsSavingPayment(false);
    }
  };

  // Ödeme bilgilerini kaydet
  // Yeni ödeme ekleme fonksiyonu
  const handleAddPayment = async () => {
    if (!customerId || customerId.startsWith('temp_')) {
      return;
    }

    const paymentAmount = parseFloat(newPaymentAmount);
    if (!paymentAmount || paymentAmount <= 0) {
      showToast('Lütfen geçerli bir ödeme tutarı girin.');
      return;
    }

    try {
      setIsAddingPayment(true);

      // Yeni ödeme kaydı oluştur
      const newPayment = {
        id: Date.now().toString(),
        amount: paymentAmount,
        type: newPaymentType,
        date: new Date(newPaymentDate || Date.now()).toISOString(),
        createdBy: user?.uid || null
      };

      // Güncel ödeme geçmişi
      const updatedPaymentHistory = [...paymentHistory, newPayment];

      // Toplam ödenen tutarı hesapla
      const totalPaid = updatedPaymentHistory.reduce((sum, p) => sum + p.amount, 0);

      // Kalan ödemeyi hesapla
      const totalAmount = calculateTotalOrderAmount();
      const newRemaining = Math.max(0, totalAmount - totalPaid);
      const paymentCompleted = newRemaining === 0;

      await updateDoc(doc(db, 'customers', customerId), {
        paidAmount: totalPaid,
        paymentHistory: updatedPaymentHistory,
        isPaymentCompleted: paymentCompleted,
        remainingAmount: newRemaining,
        updatedAt: new Date()
      });

      // Local state'leri güncelle
      setPaymentHistory(updatedPaymentHistory);
      setPaidAmount(totalPaid.toString());
      setIsPaymentCompleted(paymentCompleted);
      setNewPaymentAmount('');
      setNewPaymentType(PAYMENT_TYPES.CASH);
      setNewPaymentDate(toDateTimeLocal());

      // Parent'a güncellenmiş bilgileri bildir
      const updatedCustomerInfo = {
        ...customerInfo,
        paidAmount: totalPaid,
        paymentHistory: updatedPaymentHistory,
        isPaymentCompleted: paymentCompleted,
        remainingAmount: newRemaining
      };

      if (onCustomerSaved) {
        onCustomerSaved(updatedCustomerInfo);
      }

    } catch (error) {
      console.error('Ödeme eklenirken hata:', error);
      showToast('Ödeme eklenirken bir hata oluştu: ' + error.message);
    } finally {
      setIsAddingPayment(false);
    }
  };

  // Ödeme iptal fonksiyonu
  const handleCancelPayment = async (paymentToCancel) => {
    if (!customerId || customerId.startsWith('temp_')) return;

    try {
      setIsAddingPayment(true);

      // Ödemeyi listeden çıkar
      const updatedPaymentHistory = paymentHistory.filter(p => p.id !== paymentToCancel.id);

      // Yeni toplam ödenen tutarı hesapla
      const totalPaid = updatedPaymentHistory.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

      // Kalan ödemeyi hesapla
      const totalAmount = calculateTotalOrderAmount();
      const newRemaining = Math.max(0, totalAmount - totalPaid);
      const paymentCompleted = newRemaining === 0;

      await updateDoc(doc(db, 'customers', customerId), {
        paidAmount: totalPaid,
        paymentHistory: updatedPaymentHistory,
        isPaymentCompleted: paymentCompleted,
        remainingAmount: newRemaining,
        updatedAt: new Date()
      });

      // Local state'leri güncelle
      setPaymentHistory(updatedPaymentHistory);
      setPaidAmount(totalPaid.toString());
      setIsPaymentCompleted(paymentCompleted);
      setCancelPaymentConfirm(null);

      // Parent'a güncellenmiş bilgileri bildir
      if (onCustomerSaved) {
        onCustomerSaved({
          ...customerInfo,
          paidAmount: totalPaid,
          paymentHistory: updatedPaymentHistory,
          isPaymentCompleted: paymentCompleted,
          remainingAmount: newRemaining
        });
      }
    } catch (error) {
      console.error('Ödeme iptal edilirken hata:', error);
      showToast('Ödeme iptal edilirken bir hata oluştu: ' + error.message);
    } finally {
      setIsAddingPayment(false);
    }
  };

  // Toplam sipariş tutarını hesapla (Genel Toplam)
  const calculateTotalOrderAmount = () => {
    return toplamOdeme;
  };

  const handleSavePayment = async () => {
    if (!customerId || customerId.startsWith('temp_')) {
      return;
    }

    try {
      setIsSavingPayment(true);

      // Ödenen tutarı parse et - boş string kontrolü
      const paidAmountValue = (paidAmount && paidAmount.trim() !== '') ? parseFloat(paidAmount) : 0;

      // Kalan ödemeyi tekrar hesapla (güncel değerlerle)
      const currentRemaining = calculateRemainingAmount();

      await updateDoc(doc(db, 'customers', customerId), {
        paidAmount: paidAmountValue,
        isPaymentCompleted: isPaymentCompleted,
        remainingAmount: currentRemaining,
        paymentHistory: paymentHistory,
        updatedAt: new Date()
      });

      // Local state'i güncelle - müşteri bilgilerini güncelle
      const updatedCustomerInfo = {
        ...customerInfo,
        paidAmount: paidAmountValue,
        isPaymentCompleted: isPaymentCompleted,
        remainingAmount: currentRemaining,
        paymentHistory: paymentHistory
      };

      // Parent'a güncellenmiş bilgileri bildir (sadece ilgili müşteriyi güncelle, tüm listeyi yenileme)
      // onCustomerSaved callback'i zaten "Ödeme bilgisi başarıyla güncellendi!" mesajını gösteriyor
      if (onCustomerSaved) {
        onCustomerSaved(updatedCustomerInfo);
      }

      // onSaveVadeliSuccess sadece vadeli yüzde kaydedildiğinde çağrılmalı, ödeme bilgisi kaydedildiğinde değil
    } catch (error) {
      console.error('Ödeme bilgileri kaydedilirken hata:', error);
      showToast('Ödeme bilgileri kaydedilirken bir hata oluştu: ' + error.message);
    } finally {
      setIsSavingPayment(false);
    }
  };

  // 2. adım: PDF dosyasını paylaş (link değil dosya gider; WhatsApp'ta aynı sohbet seçilir)
  const handleShareCustomerPdf = async (kind) => {
    try {
      const result = kind === 'teklif'
        ? await shareCustomerPreOrderPDF(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, manualGrandTotal)
        : await shareCustomerPDF(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, parseFloat(paidAmount) || 0, remainingAmount, manualGrandTotal);
      if (result === 'downloaded') {
        showToast('Bu tarayıcı dosya paylaşımını desteklemiyor; PDF müşteri adıyla indirildi. WhatsApp\'ta sohbete ekleyebilirsiniz.', 'success');
      }
    } catch (error) {
      if (error?.name === 'AbortError') return; // paylaşım menüsü kapatıldı
      console.error('PDF paylaşılamadı:', error);
      showToast('PDF paylaşılırken bir hata oluştu.', 'error');
    }
  };

  // Keşif ölçüsünü siparişe dönüştür: formu ürün tipi + oda + not ile aç, ölçüler tip seçildikten sonra uygulanır
  const handleConvertKesifItem = (item) => {
    handleProductTypeChange(item.productType);
    setNewOrder(prev => ({ ...prev, roomName: item.roomName || '', notes: item.notes || '' }));
    setEditingOrderId(null);
    setIsAddingOrder(true);
    setConvertingKesifId(item.id);
    setKesifFill(item);

    setTimeout(() => {
      const orderFormElement = document.querySelector('.order-form');
      if (orderFormElement) orderFormElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // En/boy/adet handleFieldChange ile girilir ki m² ve fiyat hesapları elle girilmiş gibi çalışsın
  useEffect(() => {
    if (!kesifFill || selectedProductType !== kesifFill.productType) return;
    const { width, height, quantity } = kesifFill;
    if (selectedProductType === 'plise') {
      handleFieldChange('pliseItems', [{
        id: Date.now(),
        width: width || '',
        height: height || '',
        usedM2: '1.00',
        quantity: quantity || '1',
        multisistem: false,
        rowTotal: '0.00'
      }]);
    } else {
      if (width) handleFieldChange('width', width);
      if (height) handleFieldChange('height', height);
      if (quantity) handleFieldChange('quantity', quantity);
    }
    setKesifFill(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kesifFill, selectedProductType]);

  // Sipariş ekleme formunu aç/kapat (önceden butonun içindeki aynı mantık)
  const handleToggleAddOrder = () => {
    setConvertingKesifId(null);
    setKesifFill(null);
    if (isAddingOrder) {
      // İptal butonuna basıldığında formu temizle
      setIsAddingOrder(false);
      setEditingOrderId(null);
      setSelectedProductType('');
      setNewOrder({
        roomName: '',
        productName: '',
        width: '',
        height: '',
        pileModel: '',
        pileDensity: '',
        usedM2: '',
        unitPrice: '',
        extraPrice: '',
        capsuleUnitPrice: '',
        capsulePrice: '',
        jumboSystem: '',
        reductor: '',
        bottomSkirt: '',
        closedCase: '',
        laser: '',
        mounting: '',
        backgroundCurtain: '',
        pliseProduct1Name: '',
        pliseProduct1Price: '',
        pliseProduct1Variation: '',
        pliseProduct2Name: '',
        pliseProduct2Price: '',
        pliseProduct2Variation: '',
        fabricPrice: '',
        mechanismType: '',
        mechanismPrice: '',
        accessoryPrice: '',
        accessories: [],
        quantity: '',
        discount: '',
        totalPrice: '',
        discountedPrice: '',
        notes: '',
        hasOzelTasarim: false,
        ozelTasarimProductName: '',
        ozelTasarimProductVariant: '',
        ozelTasarimWidth: '',
        ozelTasarimHeight: '',
        ozelTasarimUnitPrice: '',
        ozelTasarimUnitCost: '',
        ozelTasarimBaseUnitPrice: '',
        ozelTasarimBaseUnitCost: '',
        ozelTasarimPileModel: '',
        ozelTasarimPileDensity: '',
        ozelTasarimUsedM2: '',
        ozelTasarimExtraPrice: '',
        ozelTasarimManualExtraCost: '',
        ozelTasarimQuantity: '',
        ozelTasarimCapsuleUnitPrice: '',
        ozelTasarimCapsuleUnitCost: '',
        ozelTasarimCapsulePrice: '',
        ozelTasarimCapsuleCost: '',
        kanatAccessories: [],
        ozelProjeKumasEni: '',
        ozelProjeTopBoyu: '',
        ozelProjeRapor: '',
        ozelProjeBoyPayi: '',
        hasOzelProje: false,
        ...emptyStorZebraExtraOrderFields()
      });
    } else {
      // Yeni sipariş ekle butonuna basıldığında formu temizle
      setIsAddingOrder(true);
      setEditingOrderId(null);
      setSelectedProductType('');
      setNewOrder({
        roomName: '',
        productName: '',
        width: '',
        height: '',
        pileModel: '',
        pileDensity: '',
        usedM2: '',
        unitPrice: '',
        extraPrice: '',
        capsuleUnitPrice: '',
        capsulePrice: '',
        jumboSystem: '',
        reductor: '',
        bottomSkirt: '',
        closedCase: '',
        laser: '',
        mounting: '',
        backgroundCurtain: '',
        pliseProduct1Name: '',
        pliseProduct1Price: '',
        pliseProduct1Variation: '',
        pliseProduct2Name: '',
        pliseProduct2Price: '',
        pliseProduct2Variation: '',
        fabricPrice: '',
        mechanismType: '',
        mechanismPrice: '',
        accessoryPrice: '',
        accessories: [],
        quantity: '',
        discount: '',
        totalPrice: '',
        discountedPrice: '',
        notes: '',
        hasOzelTasarim: false,
        ozelTasarimProductName: '',
        ozelTasarimProductVariant: '',
        ozelTasarimWidth: '',
        ozelTasarimHeight: '',
        ozelTasarimUnitPrice: '',
        ozelTasarimUnitCost: '',
        ozelTasarimBaseUnitPrice: '',
        ozelTasarimBaseUnitCost: '',
        ozelTasarimPileModel: '',
        ozelTasarimPileDensity: '',
        ozelTasarimUsedM2: '',
        ozelTasarimExtraPrice: '',
        ozelTasarimManualExtraCost: '',
        ozelTasarimQuantity: '',
        ozelTasarimCapsuleUnitPrice: '',
        ozelTasarimCapsuleUnitCost: '',
        ozelTasarimCapsulePrice: '',
        ozelTasarimCapsuleCost: '',
        kanatAccessories: [],
        ozelProjeKumasEni: '',
        ozelProjeTopBoyu: '',
        ozelProjeRapor: '',
        ozelProjeBoyPayi: '',
        hasOzelProje: false,
        ...emptyStorZebraExtraOrderFields()
      });
    }
  };

  return (
    <div className="order-details">
      <div className="order-header">
        <div className="order-header-left">
          <h4>Sipariş Detayları</h4>
          {customerName && (
            <div className="customer-info">
              <div className="customer-name">
                <strong>Müşteri:</strong> {customerName} {customerInfo?.tcNo && <span style={{ marginLeft: '10px', fontSize: '12px', color: '#64748b' }}>(TC/Vergi No: {customerInfo.tcNo})</span>}
              </div>
              <div className="order-status-selector">
                <span className="od-status-label" id="od-status-label">Sipariş durumu</span>
                <div className="od-status-steps" role="radiogroup" aria-labelledby="od-status-label" ref={statusStepsRef}>
                  {Object.entries(ORDER_STATUS_LABELS).map(([key, label], index) => {
                    const currentStatus = orderStatus || ORDER_STATUS.NEW_ORDER;
                    const currentIndex = Object.keys(ORDER_STATUS_LABELS).indexOf(currentStatus);
                    const isCurrent = key === currentStatus;
                    // Montajcı sadece Teslim Edildi yapabilir; teslim edildiyse değiştiremez
                    const isBlocked = isLoading || (isMontajci && (currentStatus === ORDER_STATUS.DELIVERED || (key !== ORDER_STATUS.DELIVERED && !isCurrent)));
                    return (
                      <button
                        key={key}
                        type="button"
                        role="radio"
                        aria-checked={isCurrent}
                        className={`od-status-step od-status-${key}${isCurrent ? ' is-current' : ''}${index < currentIndex ? ' is-done' : ''}`}
                        disabled={isBlocked}
                        onClick={() => {
                          if (isCurrent || !onOrderStatusChange) return;
                          if (isMontajci && key !== ORDER_STATUS.DELIVERED) return;
                          onOrderStatusChange(key);
                        }}
                      >
                        <span className="od-status-dot" aria-hidden="true">{index < currentIndex ? '✓' : index + 1}</span>
                        <span className="od-status-text">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Keşif ölçüleri: müşteri gelmeden oda oda ölçü girilir, sonra siparişe dönüştürülür */}
      {!isMontajci && permissions?.canCreateOrder && customerId && !customerId.startsWith('temp_') && (
        <KesifMeasurements
          items={kesifItems}
          onSave={saveKesifItems}
          onConvert={handleConvertKesifItem}
          disabled={isLoading}
          activeItemId={isAddingOrder ? convertingKesifId : null}
        />
      )}

      {/* Siparişler başlığı + Sipariş ekle (Montajcı sipariş listesini görmez) */}
      {!isMontajci && (
      <div className="od-list-head">
        <h5 className="od-list-title">
          Siparişler
          <span className="od-list-count">{orders.length}</span>
          {orders.length > 0 && (
            <span
              className={`od-supplier-count${orders.every(hasSupplierOrder) ? ' is-done' : ''}`}
              title="Firmaya sipariş geçilen ürün sayısı"
            >
              Firmaya geçildi {orders.filter(hasSupplierOrder).length}/{orders.length}
            </span>
          )}
        </h5>
        {permissions?.canCreateOrder && (orders.length > 0 || isAddingOrder) && (
          <button
            type="button"
            onClick={handleToggleAddOrder}
            className={`add-order-btn${isAddingOrder ? ' is-cancel' : ''}`}
            disabled={isLoading}
          >
            {isAddingOrder ? 'Vazgeç' : '+ Sipariş ekle'}
          </button>
        )}
      </div>
      )}

      {/* Yeni Sipariş Ekleme Formu */}
      {isAddingOrder && (
        <OrderForm
          isAddingOrder={isAddingOrder}
          editingOrderId={editingOrderId}
          selectedProductType={selectedProductType}
          newOrder={newOrder}
          isLoading={isLoading}
          customerInfo={customerInfo}
          customerId={customerId}
          onProductTypeChange={handleProductTypeChange}
          onFieldChange={handleFieldChange}
          onSubmit={handleAddOrder}
          onCancel={() => { setIsAddingOrder(false); setConvertingKesifId(null); }}
          onSupplierOrderSent={handleSupplierOrderSent}
          onSupplierOrderCancel={handleSupplierOrderCancelInForm}
          supplierBusyId={supplierBusyId}
          pileModels={pileModels}
        />
      )}

      {/* Sipariş Listesi */}
      <div className="orders-list">
        {isMontajci ? null : orders.length === 0 ? (
          !isAddingOrder && (
            <div className="no-orders">
              <p>Henüz sipariş eklenmemiş.</p>
              {permissions?.canCreateOrder && (
                <button type="button" onClick={handleToggleAddOrder} className="add-order-btn" disabled={isLoading}>
                  + İlk siparişi ekle
                </button>
              )}
            </div>
          )
        ) : (
          <>
            {/* Desktop Tablo Görünümü */}
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Ürün</th>
                  <th>Ölçü (cm)</th>
                  <th>Pile</th>
                  <th>Kullanılan</th>
                  <th className="od-col-num">Birim fiyat</th>
                  <th className="od-col-num">Adet</th>
                  <th className="od-col-num">Toplam</th>
                  <th className="od-col-actions"><span className="od-sr-only">İşlemler</span></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td className="od-product-cell">
                      <div className="od-product-meta">
                        <span className="od-type-chip" data-type={order.productType}>{PRODUCT_TYPES[order.productType]?.label || order.productType}</span>
                        {renderBrandChips(order)}
                        {order.roomName && <span className="od-room">{order.roomName}</span>}
                      </div>
                      {order.productType === 'plise' ? (
                        <div className="product-name-text">
                          {order.pliseProduct1Name}
                          {order.pliseProduct2Name && ` + ${order.pliseProduct2Name}`}
                        </div>
                      ) : order.productType === 'rustik' ? (
                        <>
                          <div className="product-name-text">{order.productName}{order.productVariant ? ` - ${order.productVariant}` : ''}</div>
                          <div className="product-sub-info" style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                            {order.ayakModel && <span>{order.ayakModel}</span>}
                            {order.ayakCap && <span> | Çap: {order.ayakCap}</span>}
                            {order.ayakUzunluk && <span> | Uz: {order.ayakUzunluk}</span>}
                            {order.ayakRenkKodu && <span> | Renk: {order.ayakRenkKodu}</span>}
                          </div>
                          {(order.rustikAksesuarlar?.length > 0 || order.rustikRaySecenekleri?.length > 0) && (
                            <div className="product-sub-info" style={{ fontSize: '10px', color: '#1a1a1a', marginTop: '1px' }}>
                              <span>{(order.rustikAksesuarlar?.length || 0) + (order.rustikRaySecenekleri?.length || 0)} Ek Seçenek</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="product-name-text">
                            {(order.productType === 'kanat' && (order.hasOzelTasarim || !!order.ozelTasarimProductName)) ? 'Ana: ' : ''}
                            {order.productName}{order.productVariant ? ` - ${order.productVariant}` : ''}
                          </div>
                          {order.productType === 'kanat' && (order.hasOzelTasarim || !!order.ozelTasarimProductName) && (
                            <div className="product-name-text" style={{ color: '#1a1a1a', marginTop: '4px' }}>
                              Özel: {order.ozelTasarimProductName}{order.ozelTasarimProductVariant ? ` - ${order.ozelTasarimProductVariant}` : ''}
                              {order.ozelTasarimTotalPrice && parseFloat(order.ozelTasarimTotalPrice) > 0 ? ` (Tutar: ${formatPrice(order.ozelTasarimTotalPrice)} ₺)` : ''}
                            </div>
                          )}
                          {order.productType === 'kanat' && (order.hasAstar || !!order.astarProductName) && (
                            <div className="product-name-text" style={{ color: '#7c3aed', marginTop: '4px' }}>
                              Astar: {order.astarProductName}{order.astarProductVariant ? ` - ${order.astarProductVariant}` : ''}
                              {order.astarPrice && parseFloat(order.astarPrice) > 0 ? ` (Tutar: ${formatPrice(order.astarPrice)} ₺)` : ''}
                            </div>
                          )}
                          {order.productType === 'tul' && order.hasAltEtekKatlama && (
                            <div className="product-sub-info" style={{ fontSize: '11px', color: '#1a1a1a', marginTop: '2px', fontWeight: 'bold' }}>
                              <span>Alt Etek Katlama</span>
                            </div>
                          )}
                          {(order.productType === 'tul' || order.productType === 'kanat') && order.hasOzelOlcu && (
                            <div className="product-sub-info" style={{ fontSize: '11px', color: '#b91c1c', marginTop: '2px', fontWeight: 'bold' }}>
                              <span>Özel Ölçü (Kumaş 90° Çevrilmiş)</span>
                            </div>
                          )}
                          {(order.productType === 'tul' || order.productType === 'kanat') && order.hasOzelProje && (() => {
                            const proje = calculateOzelProje({
                              width: order.width, height: order.height, pileModel: order.pileModel, pileDensity: order.pileDensity,
                              quantity: order.quantity, fabricWidthCm: order.ozelProjeKumasEni, rollLengthM: order.ozelProjeTopBoyu,
                              repeatCm: order.ozelProjeRapor, hemAllowanceCm: order.ozelProjeBoyPayi
                            });
                            return (
                              <div className="product-sub-info" style={{ fontSize: '11px', color: '#0f766e', marginTop: '2px', fontWeight: 'bold' }}>
                                <span>
                                  Özel Proje{proje && !proje.error
                                    ? `: ${proje.totalPanels} panel × ${formatPrice(proje.cutLengthM)} m = ${formatPrice(proje.totalFabricM)} m kumaş (fire ${formatPrice(proje.totalWasteM)} m)`
                                    : ''}
                                </span>
                              </div>
                            );
                          })()}
                          {order.direction && (
                            <div className="product-sub-info" style={{ fontSize: '11px', color: '#64748b' }}>
                              (Yön: {order.direction})
                            </div>
                          )}
                          {(order.productType === 'kanat' || order.productType === 'tul') && Array.isArray(order.kanatAccessories) && order.kanatAccessories.length > 0 && (
                            <div style={{ fontSize: '11px', color: '#0f172a', marginTop: '4px', borderTop: '1px dashed #cbd5e1', paddingTop: '4px' }}>
                              <strong>Aksesuarlar:</strong>
                              {order.kanatAccessories.map((acc, index) => (
                                <div key={acc.id || index}>
                                  • {acc.name} ({acc.unitType === 'metre' ? `${acc.meter} m x ${acc.quantity} ad` : `${acc.quantity} ad`}) - {formatPrice(acc.price)} ₺ (Tutar: {formatPrice(acc.total)} ₺)
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                      {order.hasDikeyFon && (
                        <div className="product-sub-info od-sub">Fon: {order.dikeyFonName || 'Var'} ({order.dikeyFonCount || 1} Adet)</div>
                      )}
                      {renderSupplierCell(order)}
                      {order.notes && <div className="od-note">{order.notes}</div>}
                    </td>
                    <td>
                      {order.productType === 'kanat' && (order.hasOzelTasarim || !!order.ozelTasarimProductName) ? (
                        <>
                          <div>Ana: {order.width} x {order.height}</div>
                          <div style={{ color: '#1a1a1a', marginTop: '4px' }}>Özel: {order.ozelTasarimWidth} x {order.ozelTasarimHeight}</div>
                        </>
                      ) : (
                        <>
                          {order.width && `${order.width}`}
                          {order.width && order.height && ' x '}
                          {order.height && `${order.height}`}
                        </>
                      )}
                    </td>
                    <td>
                      {order.productType === 'kanat' && (order.hasOzelTasarim || !!order.ozelTasarimProductName) ? (
                        <>
                          <div>Ana: {order.pileModel || '-'}</div>
                          <div style={{ color: '#1a1a1a', marginTop: '4px' }}>Özel: {order.ozelTasarimPileModel || '-'}</div>
                        </>
                      ) : (
                        order.pileModel || '-'
                      )}
                    </td>
                    <td>
                      {order.productType === 'kanat' && (order.hasOzelTasarim || !!order.ozelTasarimProductName) ? (
                        <>
                          <div>Ana: {order.usedM2 || '-'}</div>
                          <div style={{ color: '#1a1a1a', marginTop: '4px' }}>Özel: {order.ozelTasarimUsedM2 || '-'}</div>
                        </>
                      ) : (
                        order.productType === 'raySistemi'
                          ? (order.usedMeter ? `${order.usedMeter} m` : '-')
                          : order.productType === 'rustik'
                            ? (order.usedCm ? `${order.usedCm} cm` : '-')
                            : (order.usedM2 || '-')
                      )}
                    </td>
                    <td className="od-col-num">
                      {order.productType === 'plise' ? (
                        <>
                          {formatPrice(order.pliseProduct1Price)}
                          {order.pliseProduct2Price && parseFloat(order.pliseProduct2Price) > 0 && ` + ${formatPrice(order.pliseProduct2Price)}`}
                          {' ₺'}
                        </>
                      ) : order.productType === 'katlamaliPerde' ? (
                        `${formatPrice(order.fabricPrice)} / ${formatPrice(order.mechanismPrice)} ₺`
                      ) : order.productType === 'kanat' && (order.hasOzelTasarim || !!order.ozelTasarimProductName) ? (
                        <>
                          <div>Ana: {formatPrice(order.unitPrice)} ₺</div>
                          <div style={{ color: '#1a1a1a', marginTop: '4px' }}>Özel: {formatPrice(order.ozelTasarimUnitPrice)} ₺</div>
                        </>
                      ) : order.unitPrice && parseFloat(order.unitPrice) > 0 ? (
                        `${formatPrice(order.unitPrice)} ₺`
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="od-col-num">
                      {order.productType === 'plise'
                        ? (order.pliseItems?.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0) || 0)
                        : order.productType === 'kanat' && (order.hasOzelTasarim || !!order.ozelTasarimProductName) ? (
                          <>
                            <div>Ana: {order.quantity || '1'}</div>
                            <div style={{ color: '#1a1a1a', marginTop: '4px' }}>Özel: {order.ozelTasarimQuantity || '1'}</div>
                          </>
                        ) : (order.quantity || '1')}
                    </td>
                    <td className="od-col-num od-total-cell">
                      {order.discountedPrice && parseFloat(order.discount) > 0 ? (
                        <>
                          <span className="od-price-original">{formatPrice(order.totalPrice)} ₺</span>
                          <span className="od-price-discounted">
                            <span className="od-discount-chip">−%{parseFloat(order.discount).toLocaleString('tr-TR')}</span>
                            {formatPrice(order.discountedPrice)} ₺
                          </span>
                        </>
                      ) : (
                        <span className="od-price">{formatPrice(order.totalPrice)} ₺</span>
                      )}
                    </td>
                    <td className="od-col-actions">
                      <div className="action-buttons">
                        {/* Düzenleme - Montajcı hariç */}
                        {permissions?.canEditOrder && (
                          <button
                            onClick={() => handleEditOrder(order)}
                            className="edit-btn"
                            disabled={isLoading}
                            title="Düzenle"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                        )}
                        {/* Kopyalama - Montajcı hariç */}
                        {permissions?.canCreateOrder && (
                          <button
                            onClick={() => handleCopyOrder(order)}
                            className="copy-btn"
                            disabled={isLoading}
                            title="Kopyala"
                          >
                            <i className="fas fa-copy"></i>
                          </button>
                        )}
                        {/* Silme - Montajcı hariç */}
                        {permissions?.canDeleteOrder && (
                          <button
                            onClick={() => handleDeleteConfirm(order)}
                            className="delete-btn"
                            disabled={isLoading}
                            title="Sil"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobil ve Tablet Kart Görünümü */}
            <div className="orders-cards">
              {orders.map((order) => (
                <div key={order.id} className="order-card">
                  <div className="order-card-header">
                    <div className="order-card-title od-product-meta">
                      <span className="od-type-chip" data-type={order.productType}>{PRODUCT_TYPES[order.productType]?.label || order.productType}</span>
                      {renderBrandChips(order)}
                      {order.roomName && <span className="od-room">{order.roomName}</span>}
                    </div>
                    <div className="order-card-actions">
                      {/* Tablette firmaya geçildi kutusu butonların yanında */}
                      <div className="od-card-head-check">{renderSupplierCell(order)}</div>
                      {/* Düzenleme - Montajcı hariç */}
                      {permissions?.canEditOrder && (
                        <button
                          onClick={() => handleEditOrder(order)}
                          className="edit-btn"
                          disabled={isLoading}
                          title="Düzenle"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                      )}
                      {/* Kopyalama - Montajcı hariç */}
                      {permissions?.canCreateOrder && (
                        <button
                          onClick={() => handleCopyOrder(order)}
                          className="copy-btn"
                          disabled={isLoading}
                          title="Kopyala"
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                      )}
                      {/* Silme - Montajcı hariç */}
                      {permissions?.canDeleteOrder && (
                        <button
                          onClick={() => handleDeleteConfirm(order)}
                          className="delete-btn"
                          disabled={isLoading}
                          title="Sil"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="order-card-content">
                    <div className="order-card-item">
                      <div className="order-card-label">Ürün Adı</div>
                      <div className="order-card-value">
                        {order.productType === 'plise' ? (
                          <>
                            {order.pliseProduct1Name}
                            {order.pliseProduct2Name && ` + ${order.pliseProduct2Name}`}
                          </>
                        ) : (
                          <>
                            {order.productType === 'kanat' && (order.hasOzelTasarim || !!order.ozelTasarimProductName) ? (
                              <>
                                <div><strong>Ana:</strong> {order.productName}{order.productVariant ? ` - ${order.productVariant}` : ''}</div>
                                <div style={{ color: '#1a1a1a', marginTop: '4px' }}>
                                  <strong>Özel:</strong> {order.ozelTasarimProductName}{order.ozelTasarimProductVariant ? ` - ${order.ozelTasarimProductVariant}` : ''}
                                  {order.ozelTasarimTotalPrice && parseFloat(order.ozelTasarimTotalPrice) > 0 ? ` (Tutar: ${formatPrice(order.ozelTasarimTotalPrice)} ₺)` : ''}
                                </div>
                              </>
                            ) : (
                              <>
                                {order.productName}{order.productVariant ? ` - ${order.productVariant}` : ''}
                              </>
                            )}
                            {order.productType === 'kanat' && (order.hasAstar || !!order.astarProductName) && (
                              <div style={{ color: '#7c3aed', marginTop: '4px' }}>
                                <strong>Astar:</strong> {order.astarProductName}{order.astarProductVariant ? ` - ${order.astarProductVariant}` : ''}
                                {order.astarPrice && parseFloat(order.astarPrice) > 0 ? ` (Tutar: ${formatPrice(order.astarPrice)} ₺)` : ''}
                              </div>
                            )}
                            {order.productType === 'tul' && order.hasAltEtekKatlama && (
                              <small style={{ display: 'block', color: '#1a1a1a', fontWeight: 'bold', marginTop: '2px' }}>
                                Alt Etek Katlama
                              </small>
                            )}
                            {(order.productType === 'tul' || order.productType === 'kanat') && order.hasOzelOlcu && (
                              <small style={{ display: 'block', color: '#b91c1c', fontWeight: 'bold', marginTop: '2px' }}>
                                Özel Ölçü (Kumaş 90° Çevrilmiş)
                              </small>
                            )}
                            {(order.productType === 'tul' || order.productType === 'kanat') && order.hasOzelProje && (() => {
                              const proje = calculateOzelProje({
                                width: order.width, height: order.height, pileModel: order.pileModel, pileDensity: order.pileDensity,
                                quantity: order.quantity, fabricWidthCm: order.ozelProjeKumasEni, rollLengthM: order.ozelProjeTopBoyu,
                                repeatCm: order.ozelProjeRapor, hemAllowanceCm: order.ozelProjeBoyPayi
                              });
                              return (
                                <small style={{ display: 'block', color: '#0f766e', fontWeight: 'bold', marginTop: '2px' }}>
                                  Özel Proje{proje && !proje.error ? `: ${proje.totalPanels} panel · ${formatPrice(proje.totalFabricM)} m kumaş` : ''}
                                </small>
                              );
                            })()}
                            {order.direction ? ` (Yön: ${order.direction})` : ''}
                          </>
                        )}
                        <div className="od-card-body-check">{renderSupplierCell(order)}</div>
                      </div>
                    </div>
                    {order.hasDikeyFon && (
                      <div className="order-card-item">
                        <div className="order-card-label">Fon</div>
                        <div className="order-card-value">{order.dikeyFonName || 'Var'} ({order.dikeyFonCount || 1} Adet)</div>
                      </div>
                    )}
                    <div className="order-card-item">
                      <div className="order-card-label">Ölçüler (cm)</div>
                      <div className="order-card-value">
                        {order.productType === 'kanat' && (order.hasOzelTasarim || !!order.ozelTasarimProductName) ? (
                          <>
                            <div>Ana: {order.width} x {order.height}</div>
                            <div style={{ color: '#1a1a1a' }}>Özel: {order.ozelTasarimWidth} x {order.ozelTasarimHeight}</div>
                          </>
                        ) : (
                          <>
                            {order.width && `${order.width}`}
                            {order.width && order.height && ' x '}
                            {order.height && `${order.height}`}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="order-card-item">
                      <div className="order-card-label">Pile Modeli</div>
                      <div className="order-card-value">
                        {order.productType === 'kanat' && (order.hasOzelTasarim || !!order.ozelTasarimProductName) ? (
                          <>
                            <div>Ana: {order.pileModel || '-'}</div>
                            <div style={{ color: '#1a1a1a' }}>Özel: {order.ozelTasarimPileModel || '-'}</div>
                          </>
                        ) : (
                          order.pileModel || '-'
                        )}
                      </div>
                    </div>
                    <div className="order-card-item">
                      <div className="order-card-label">
                        {order.productType === 'raySistemi' ? 'Kullanılan Metre' : order.productType === 'rustik' ? 'Kullanılan Cm' : 'Kullanılan M/Mt²'}
                      </div>
                      <div className="order-card-value">
                        {order.productType === 'kanat' && (order.hasOzelTasarim || !!order.ozelTasarimProductName) ? (
                          <>
                            <div>Ana: {order.usedM2 || '-'}</div>
                            <div style={{ color: '#1a1a1a' }}>Özel: {order.ozelTasarimUsedM2 || '-'}</div>
                          </>
                        ) : order.productType === 'raySistemi' ? (
                          <>
                            {order.usedMeter ? `${order.usedMeter} m` : '-'}
                            {order.olcuTamamlama && <small style={{ display: 'block', color: '#64748b', fontSize: '11px' }}>({order.olcuTamamlama === '25cm' ? '25cm Tamamlama' : order.olcuTamamlama === '50cm' ? '50cm Tamamlama' : 'Ürün Boyutu'})</small>}
                          </>
                        ) : order.productType === 'rustik' ? (
                          <>
                            {order.usedCm ? `${order.usedCm} cm` : '-'}
                            {(order.ayakSayisi || order.ekAyakSayisi) && (
                              <small style={{ display: 'block', color: '#64748b', fontSize: '11px' }}>
                                (Ayak: {order.ayakSayisi || 0}{order.ekAyakSayisi ? ` + Ek: ${order.ekAyakSayisi}` : ''})
                              </small>
                            )}
                          </>
                        ) : (
                          order.usedM2 || '-'
                        )}
                      </div>
                    </div>
                    <div className="order-card-item">
                      <div className="order-card-label">
                        {order.productType === 'plise' ? 'Ürün Fiyatları' : order.productType === 'katlamaliPerde' ? 'Kumaş / Mek.' : 'Birim Fiyat'}
                      </div>
                      <div className="order-card-value">
                        {order.productType === 'plise' ? (
                          <>
                            {formatPrice(order.pliseProduct1Price)}
                            {order.pliseProduct2Price && parseFloat(order.pliseProduct2Price) > 0 && ` + ${formatPrice(order.pliseProduct2Price)}`}
                            {' ₺'}
                          </>
                        ) : order.productType === 'katlamaliPerde' ? (
                          `${formatPrice(order.fabricPrice)} / ${formatPrice(order.mechanismPrice)} ₺`
                        ) : order.productType === 'kanat' && order.hasOzelTasarim ? (
                          <>
                            <div>Ana: {formatPrice(order.unitPrice)} ₺</div>
                            <div style={{ color: '#1a1a1a' }}>Özel: {formatPrice(order.ozelTasarimUnitPrice)} ₺</div>
                          </>
                        ) : order.unitPrice && parseFloat(order.unitPrice) > 0 ? (
                          `${formatPrice(order.unitPrice)} ₺`
                        ) : (
                          '-'
                        )}
                      </div>
                    </div>
                    <div className="order-card-item">
                      <div className="order-card-label">Adet</div>
                      <div className="order-card-value">
                        {order.productType === 'plise'
                          ? (order.pliseItems?.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0) || 0)
                          : order.productType === 'kanat' && order.hasOzelTasarim ? (
                            <>
                              <div>Ana: {order.quantity || '1'}</div>
                              <div style={{ color: '#1a1a1a' }}>Özel: {order.ozelTasarimQuantity || '1'}</div>
                            </>
                          ) : (order.quantity || '1')}
                      </div>
                    </div>
                    <div className="order-card-item od-total-cell">
                      <div className="order-card-label">Toplam</div>
                      <div className="order-card-value">
                        {order.discountedPrice && parseFloat(order.discount) > 0 ? (
                          <>
                            <span className="od-price-original">{formatPrice(order.totalPrice)} ₺</span>
                            <span className="od-price-discounted">
                              <span className="od-discount-chip">−%{parseFloat(order.discount).toLocaleString('tr-TR')}</span>
                              {formatPrice(order.discountedPrice)} ₺
                            </span>
                          </>
                        ) : (
                          <span className="od-price">{formatPrice(order.totalPrice)} ₺</span>
                        )}
                      </div>
                    </div>
                    {(order.productType === 'kanat' || order.productType === 'tul') && Array.isArray(order.kanatAccessories) && order.kanatAccessories.length > 0 && (
                      <div className="order-card-item" style={{ flexDirection: 'column', alignItems: 'flex-start', borderTop: '1px dashed #cbd5e1', paddingTop: '8px', marginTop: '8px' }}>
                        <div className="order-card-label" style={{ marginBottom: '4px' }}>Aksesuarlar</div>
                        <div className="order-card-value" style={{ width: '100%', fontSize: '11px', color: '#0f172a' }}>
                          {order.kanatAccessories.map((acc, index) => (
                            <div key={acc.id || index} style={{ marginBottom: '2px' }}>
                              • {acc.name} ({acc.unitType === 'metre' ? `${acc.meter} m x ${acc.quantity} ad` : `${acc.quantity} ad`}) - {formatPrice(acc.price)} ₺ (Tutar: {formatPrice(acc.total)} ₺)
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {order.notes && (
                      <div className="order-card-item">
                        <div className="order-card-label">Not</div>
                        <div className="order-card-value">{order.notes}</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {orders.length > 0 && (
          <div className="od-summary">
            {!isMontajci && (
            <div className="od-summary-cols">
            <section className="od-card od-card-pricing">
            <div className="od-group-head">
              <span className="od-group-title">Fiyatlandırma</span>
              {isEditMode && !isMontajci && <span className="od-group-hint">İskonto, vade farkı ve genel toplam</span>}
            </div>
            <div className="totals-grid od-pricing-grid">
              <div className="total-item">
                <span className="total-label">Ara Toplam</span>
                <span className="total-value">{formatPrice(totalAmount)} ₺</span>
              </div>

              {discountedTotalAmount !== totalAmount && (
                <div className="total-item discounted">
                  <span className="total-label">Ürün İndirimli Toplam</span>
                  <span className="total-value">{formatPrice(discountedTotalAmount)} ₺</span>
                </div>
              )}

              {/* Genel İskonto - Montajcı hariç */}
              {!isMontajci && (
                <div className="total-item iskontolu-input">
                  <span className="total-label">Genel İskonto (%)</span>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100px' }}>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      max="100"
                      value={genelIskontoYuzde || ''}
                      onChange={(e) => handleGenelIskontoYuzdeChange(e.target.value)}
                      placeholder="0"
                      style={{ 
                        width: '100%', 
                        textAlign: 'right', 
                        padding: '6px 22px 6px 8px',
                        border: '2px solid',
                        borderColor: (genelIskontoYuzde && parseFloat(genelIskontoYuzde) > 0) ? '#818cf8' : '#e2e8f0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '700',
                        outline: 'none',
                        transition: 'all 0.2s',
                        background: (genelIskontoYuzde && parseFloat(genelIskontoYuzde) > 0) ? '#f5f3ff' : '#fff',
                        color: (genelIskontoYuzde && parseFloat(genelIskontoYuzde) > 0) ? '#4338ca' : '#1e293b'
                      }}
                    />
                    <span style={{ position: 'absolute', right: '8px', color: (genelIskontoYuzde && parseFloat(genelIskontoYuzde) > 0) ? '#4338ca' : '#64748b', fontSize: '13px', fontWeight: 'bold' }}>%</span>
                  </div>
                </div>
              )}

              {genelIskontoYuzde && parseFloat(genelIskontoYuzde) > 0 && (
                <div className="total-item iskontolu">
                  <span className="total-label">İskontolu Toplam</span>
                  <span className="total-value">{formatPrice(iskontoluToplam)} ₺</span>
                </div>
              )}

              {/* Vadeli - Montajcı hariç */}
              {!isMontajci && (
                <div className="total-item vadeli-input">
                  <span className="total-label">Vade Farkı (%)</span>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100px' }}>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      max="100"
                      value={vadeliYuzde || ''}
                      onChange={(e) => handleVadeliYuzdeChange(e.target.value)}
                      placeholder="0"
                      style={{ 
                        width: '100%', 
                        textAlign: 'right', 
                        padding: '6px 22px 6px 8px',
                        border: '2px solid',
                        borderColor: (vadeliYuzde && parseFloat(vadeliYuzde) > 0) ? '#38bdf8' : '#e2e8f0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '700',
                        outline: 'none',
                        transition: 'all 0.2s',
                        background: (vadeliYuzde && parseFloat(vadeliYuzde) > 0) ? '#f0f9ff' : '#fff',
                        color: (vadeliYuzde && parseFloat(vadeliYuzde) > 0) ? '#0369a1' : '#1e293b'
                      }}
                    />
                    <span style={{ position: 'absolute', right: '8px', color: (vadeliYuzde && parseFloat(vadeliYuzde) > 0) ? '#0369a1' : '#64748b', fontSize: '13px', fontWeight: 'bold' }}>%</span>
                  </div>
                </div>
              )}

              {vadeliYuzde && parseFloat(vadeliYuzde) > 0 && (
                <div className="total-item vadeli">
                  <span className="total-label">Vadeli Toplam</span>
                  <span className="total-value">{formatPrice(vadeliToplam)} ₺</span>
                </div>
              )}

              <div className="total-item toplam-odeme" style={{ alignItems: 'center' }}>
                <span className="total-label" style={{ display: 'flex', flexDirection: 'column' }}>
                  Genel Toplam (Net)
                  {manualGrandTotal !== '' && manualGrandTotal !== null && (
                    <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginTop: '4px' }}>
                      Otomatik: {formatPrice(computedGrandTotal)} ₺
                    </span>
                  )}
                </span>
                  <div className="manual-total-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div className="input-with-icon" style={{ position: 'relative', width: '160px' }}>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={manualGrandTotal}
                        onChange={(e) => handleManualGrandTotalChange(e.target.value)}
                        style={{ 
                          width: '100%', 
                          textAlign: 'right', 
                          fontWeight: '700', 
                          color: manualGrandTotal !== '' ? '#c2410c' : '#1e293b', 
                          padding: '8px 42px 8px 10px',
                          margin: 0,
                          border: '2px solid',
                          borderColor: manualGrandTotal !== '' ? '#fdba74' : '#e2e8f0',
                          borderRadius: '10px',
                          backgroundColor: manualGrandTotal !== '' ? '#fffaf5' : '#fff',
                          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                          fontSize: '15px',
                          outline: 'none',
                          boxShadow: manualGrandTotal !== '' ? '0 2px 4px rgba(194, 65, 12, 0.08)' : 'none'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = '#6366f1';
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = manualGrandTotal !== '' ? '#fdba74' : '#e2e8f0';
                          e.currentTarget.style.boxShadow = manualGrandTotal !== '' ? '0 2px 4px rgba(194, 65, 12, 0.08)' : 'none';
                        }}
                        placeholder={formatPrice(computedGrandTotal)}
                      />
                      
                      {/* Birim Simgesi */}
                      <span style={{ 
                        position: 'absolute', 
                        right: '32px', 
                        fontWeight: '600', 
                        color: manualGrandTotal !== '' ? '#c2410c' : '#94a3b8',
                        fontSize: '14px',
                        pointerEvents: 'none'
                      }}>₺</span>

                      {/* Sihirli Değnek - Sadece boşsa görünür */}
                      {isEditMode && manualGrandTotal === '' && (
                        <button
                          type="button"
                          onClick={() => setManualGrandTotal(computedGrandTotal.toFixed(2))}
                          style={{
                            position: 'absolute',
                            right: '6px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#eff6ff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#3b82f6',
                            transition: 'all 0.2s ease',
                            padding: 0
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#dbeafe';
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#eff6ff';
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                          }}
                          title="Otomatik hesaplanan tutarı yansıt"
                        >
                          <i className="fas fa-magic" style={{ fontSize: '11px' }}></i>
                        </button>
                      )}
                      
                      {/* Temizle butonu - Sadece doluysa görünür */}
                      {isEditMode && manualGrandTotal !== '' && (
                        <button
                          type="button"
                          onClick={() => setManualGrandTotal('')}
                          style={{
                            position: 'absolute',
                            right: '6px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#fee2e2',
                            border: 'none',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            color: '#ef4444',
                            transition: 'all 0.2s ease',
                            padding: 0
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#fecaca';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fee2e2';
                          }}
                          title="Manuel düzeltmeyi iptal et"
                        >
                          <i className="fas fa-times" style={{ fontSize: '10px' }}></i>
                        </button>
                      )}
                    </div>
                  </div>
              </div>

            </div>

            {/* Fiyat Bilgilerini Kaydet - yukarıdaki fiyatlandırma alanları için (düzenleme modu, Montajcı hariç) */}
            {isEditMode && !isMontajci && (
              <div className="vadeli-save-section od-pricing-save">
                <span className="od-pricing-save-hint">
                  Genel iskonto, vade farkı veya genel toplamı değiştirdiyseniz kaydetmeyi unutmayın.
                </span>
                <button
                  type="button"
                  onClick={handleSavePricingFields}
                  className="od-save-pricing-btn"
                  disabled={isSavingVadeli || isLoading}
                >
                  {isSavingVadeli ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Kaydediliyor...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      <span>Fiyat Bilgilerini Kaydet</span>
                    </>
                  )}
                </button>
              </div>
            )}

            </section>

            {!isMontajci && (
            <section className="od-card od-card-payment">
            <div className="od-group-head">
              <span className="od-group-title">Ödeme</span>
            </div>
            <div className="totals-grid od-payment-grid">
              {/* Ödeme Bilgileri - Montajcı hariç */}
              {!isMontajci && (
                <div className="total-item payment-section">
                  <div className="payment-checkbox-wrapper">
                    <label className="payment-checkbox-label">
                      <input
                        type="checkbox"
                        checked={isPaymentCompleted}
                        onChange={(e) => handlePaymentCompletedChange(e.target.checked)}
                        disabled={isLoading || isSavingPayment}
                        className="payment-checkbox"
                      />
                      <span>Ödeme Tamamlandı</span>
                    </label>
                  </div>
                  {completePaymentForm && !isPaymentCompleted && (
                    <div className="payment-complete-panel">
                      <p>
                        Kalan <strong>{formatPrice(remainingAmount)} ₺</strong> ödemenin alındığı tarihi ve ödeme tipini seçin.
                        Ödeme geçmişine kayıt olarak eklenecek.
                      </p>
                      <div className="payment-complete-fields">
                        <label>
                          Ödeme Tarihi
                          <input
                            type="datetime-local"
                            value={completePaymentForm.date}
                            max={toDateTimeLocal()}
                            onChange={(e) => setCompletePaymentForm(prev => ({ ...prev, date: e.target.value }))}
                            disabled={isSavingPayment}
                          />
                        </label>
                        <label>
                          Ödeme Tipi
                          <select
                            value={completePaymentForm.type}
                            onChange={(e) => setCompletePaymentForm(prev => ({ ...prev, type: e.target.value }))}
                            disabled={isSavingPayment}
                          >
                            {Object.entries(PAYMENT_TYPE_LABELS).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <div className="payment-complete-actions">
                        <button type="button" className="payment-complete-cancel" onClick={() => setCompletePaymentForm(null)} disabled={isSavingPayment}>
                          Vazgeç
                        </button>
                        <button type="button" className="payment-complete-confirm" onClick={handleConfirmCompletePayment} disabled={isSavingPayment}>
                          {isSavingPayment ? <i className="fas fa-spinner fa-spin"></i> : <><i className="fas fa-check"></i> Ödemeyi Tamamla</>}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Toplam Ödenen */}
              {!isMontajci && (
                <div className="total-item paid-amount">
                  <span className="total-label">Toplam Ödenen (₺)</span>
                  <span className="total-value">
                    {formatPrice(parseFloat(paidAmount) || 0)}
                  </span>
                </div>
              )}

              {/* Kalan Ödeme */}
              {!isMontajci && (
                <div className="total-item remaining-amount">
                  <span className="total-label">Kalan Ödeme (₺)</span>
                  <span className={`total-value ${remainingAmount === 0 ? 'payment-completed' : ''}`}>
                    {remainingAmount === 0 ? (
                      <>
                        <i className="fas fa-check-circle"></i>
                        {isPaymentCompleted ? 'Ödeme Alındı' : '0.00'}
                      </>
                    ) : (
                      formatPrice(remainingAmount)
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* Ödeme Ekleme Bölümü - Sadece düzenleme modunda, sipariş varsa ve Montajcı değilse görünür */}
            {isEditMode && customerId && !customerId.startsWith('temp_') && orders.length > 0 && !isMontajci && !isPaymentCompleted && (
              <div className="payment-add-section">
                <h5><i className="fas fa-plus-circle"></i> Yeni Ödeme Ekle</h5>
                <div className="payment-add-form">
                  <div className="payment-add-row">
                    <div className="payment-type-select">
                      <label>Ödeme Tipi:</label>
                      <select
                        value={newPaymentType}
                        onChange={(e) => setNewPaymentType(e.target.value)}
                        disabled={isAddingPayment}
                      >
                        {Object.entries(PAYMENT_TYPE_LABELS).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="payment-date-input">
                      <label>Tarih:</label>
                      <input
                        type="datetime-local"
                        value={newPaymentDate}
                        max={toDateTimeLocal()}
                        onChange={(e) => setNewPaymentDate(e.target.value)}
                        disabled={isAddingPayment}
                      />
                    </div>
                    <div className="payment-amount-input">
                      <label>Tutar (₺):</label>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={newPaymentAmount}
                        onChange={(e) => setNewPaymentAmount(e.target.value)}
                        placeholder="0.00"
                        disabled={isAddingPayment}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleAddPayment}
                      className="payment-add-btn"
                      disabled={isAddingPayment || isLoading || !newPaymentAmount || !newPaymentDate}
                    >
                      {isAddingPayment ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <><i className="fas fa-plus"></i> Ekle</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Ödeme Geçmişi Listesi */}
            {!isMontajci && paymentHistory.length > 0 && (
              <div className="payment-history-section">
                <h5><i className="fas fa-history"></i> Ödeme Geçmişi</h5>
                <div className="payment-history-list">
                  {[...paymentHistory].sort((a, b) => new Date(a.date) - new Date(b.date)).map((payment, index) => (
                    <div key={payment.id || index} className="payment-history-item">
                      <div className="payment-history-info">
                        <span className={`payment-type-badge payment-type-${payment.type}`}>
                          {PAYMENT_TYPE_LABELS[payment.type] || payment.type}
                        </span>
                        <span className="payment-amount">{formatPrice(payment.amount)} ₺</span>
                      </div>
                      <div className="payment-history-right">
                        <div className="payment-history-date">
                          {new Date(payment.date).toLocaleDateString('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                        <button
                          type="button"
                          className="payment-cancel-btn"
                          onClick={() => setCancelPaymentConfirm(payment)}
                          title="Ödemeyi İptal Et"
                        >
                          <i className="fas fa-trash-alt"></i>
                          İptal
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            </section>
            )}
            </div>
            )}

            {/* Belgeler: PDF ve Fatura (Excel). Montajcı yalnızca Montaj PDF'ini görür */}
            <section className="od-card od-card-docs pdf-buttons-section">
              <div className="od-group-head">
                <span className="od-group-title">Belgeler</span>
              </div>
              <div className={`od-docs${isMontajci ? ' is-single' : ''}`}>
              {!isMontajci && (
              <div className="od-docs-group od-docs-customer">
                <span className="od-docs-label">Müşteri</span>
                {[
                  {
                    kind: 'teklif',
                    label: 'Teklif Formu',
                    open: () => generateCustomerPreOrderPDF(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, manualGrandTotal)
                  },
                  {
                    kind: 'siparis',
                    label: 'Sipariş Formu',
                    open: () => generateCustomerPDF(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, parseFloat(paidAmount) || 0, remainingAmount, manualGrandTotal)
                  }
                ].map(form => (
                  <div key={form.kind} className="od-doc-row">
                    <span className="od-doc-name">
                      <i className="fas fa-file-pdf"></i>
                      {form.label}
                    </span>
                    <div className="od-doc-actions">
                      <button
                        type="button"
                        onClick={form.open}
                        className={`pdf-btn customer-pdf od-doc-open`}
                        title={`Müşteri (${form.label}) PDF oluştur`}
                      >
                        <i className="fas fa-eye"></i>
                        <span>Aç</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCustomerWhatsAppMessage(form.kind)}
                        className="pdf-btn od-doc-message"
                        title="Müşteriyle WhatsApp sohbetini hazır mesajla aç"
                      >
                        <i className="fab fa-whatsapp"></i>
                        <span>Mesaj</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleShareCustomerPdf(form.kind)}
                        className="pdf-btn od-doc-share"
                        title="PDF dosyasını paylaş (WhatsApp'ta aynı sohbeti seçin)"
                      >
                        <i className="fas fa-share-alt"></i>
                        <span>Paylaş</span>
                      </button>
                    </div>
                  </div>
                ))}
                <p className="od-doc-hint">
                  <strong>1.</strong> <em>Mesaj</em> ile müşterinin sohbetini açıp mesajı gönderin. <strong>2.</strong> <em>Paylaş</em> ile PDF'i WhatsApp'ta aynı sohbete gönderin.
                </p>
              </div>
              )}
              <div className="od-docs-group">
                <span className="od-docs-label">{isMontajci ? 'Montaj' : 'İç kullanım'}</span>
              <div className="pdf-buttons">
                {!isMontajci && (
                <button
                  onClick={() => generateWorkshopPDF(customerInfo, orders)}
                  className="pdf-btn workshop-pdf"
                  title="Atölye için PDF oluştur"
                >
                  <i className="fas fa-file-pdf"></i>
                  <span>Atölye</span>
                </button>
                )}
                <button
                  onClick={() => generateInstallationPDF(customerInfo, orders)}
                  className="pdf-btn installation-pdf"
                  title="Montaj için PDF oluştur"
                >
                  <i className="fas fa-file-pdf"></i>
                  <span>Montaj</span>
                </button>
              </div>
              </div>
              {!isMontajci && (
              <div className="od-docs-group">
                <span className="od-docs-label">Muhasebe</span>
              <div className="pdf-buttons">
                <button
                  onClick={() => generateInvoiceExcel(customerInfo, orders, genelIskontoYuzde, vadeliYuzde)}
                  className="pdf-btn invoice-excel-btn"
                  title="e-Fatura / e-Arşiv için Excel indir"
                >
                  <i className="fas fa-file-excel"></i>
                  <span>Fatura</span>
                </button>
              </div>
              </div>
              )}
              </div>
            </section>

            {/* Montaj notu: montajcı yazar; yönetici ve satışçı da görür/düzenler */}
            {isEditMode && customerId && !customerId.startsWith('temp_') && (
              <section className="od-card od-card-note">
                <div className="od-group-head">
                  <span className="od-group-title">Montaj notu</span>
                  {montajNoteMeta?.at && (
                    <span className="od-group-hint">
                      Son güncelleme: {formatNoteDate(montajNoteMeta.at)}{montajNoteMeta.by ? ` · ${montajNoteMeta.by}` : ''}
                    </span>
                  )}
                </div>
                {canEditMontajNote ? (
                  <>
                    <textarea
                      className="od-note-input"
                      value={montajNote}
                      onChange={(e) => { setMontajNote(e.target.value); setMontajNoteDirty(true); }}
                      placeholder="Montajla ilgili notunuzu yazın (ör. eksik parça, müşteri talebi, ölçü farkı, tekrar gidilecek...)"
                      rows={4}
                      maxLength={2000}
                      disabled={isSavingMontajNote}
                      aria-label="Montaj notu"
                    />
                    <div className="od-note-actions">
                      <span className="od-note-count">{montajNote.length} / 2000</span>
                      <button
                        type="button"
                        className="od-note-save"
                        onClick={handleSaveMontajNote}
                        disabled={isSavingMontajNote || !montajNoteDirty}
                      >
                        <i className={`fas ${isSavingMontajNote ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
                        <span>{isSavingMontajNote ? 'Kaydediliyor...' : montajNoteDirty ? 'Notu kaydet' : 'Kaydedildi'}</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="od-note-readonly">{montajNote || 'Henüz montaj notu yok.'}</p>
                )}
              </section>
            )}
          </div>
        )}
      </div>

      {/* Sipariş Silme Onay Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Sipariş Sil</h3>
            <p>
              <strong>{deleteConfirm.productName}</strong> adlı siparişi silmek istediğinizden emin misiniz?
            </p>
            <p className="warning-text">Bu işlem geri alınamaz!</p>
            <div className="modal-actions">
              <button
                onClick={handleDeleteOrder}
                className="delete-confirm-btn"
                disabled={isLoading}
              >
                {isLoading ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="cancel-btn"
                disabled={isLoading}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ödeme İptal Onay Modal */}
      {cancelPaymentConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Ödemeyi İptal Et</h3>
            <p>
              <strong>{formatPrice(cancelPaymentConfirm.amount)} ₺</strong> tutarındaki{' '}
              <strong>{PAYMENT_TYPE_LABELS[cancelPaymentConfirm.type] || cancelPaymentConfirm.type}</strong>{' '}
              ödemesini iptal etmek istediğinizden emin misiniz?
            </p>
            <p className="warning-text">Bu işlem, ödeme geçmişinden kaydı silecek ve tutarı kalan borca ekleyecektir.</p>
            <div className="modal-actions">
              <button
                onClick={() => handleCancelPayment(cancelPaymentConfirm)}
                className="delete-confirm-btn"
                disabled={isAddingPayment}
              >
                {isAddingPayment ? 'İptal Ediliyor...' : 'Evet, İptal Et'}
              </button>
              <button
                onClick={() => setCancelPaymentConfirm(null)}
                className="cancel-btn"
                disabled={isAddingPayment}
              >
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sistem Seçim Onay Modal */}
      {showSystemConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#f59e0b', marginBottom: '15px' }}></i>
              <h3 style={{ marginTop: '15px', color: '#1f2937' }}>Sistem Seçilmedi</h3>
              <p style={{ color: '#4b5563', marginBottom: '10px' }}>Herhangi bir sistem (Jumbo, Alt Etek, vb.) seçmediniz.</p>
              <p style={{ color: '#4b5563', fontWeight: '500' }}>Siparişinizi bu şekilde kaydetmeyi onaylıyor musunuz?</p>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowSystemConfirm(false);
                  handleAddOrder(null, true);
                }}
                className="delete-confirm-btn"
                style={{ backgroundColor: '#2563eb' }}
                disabled={isLoading}
              >
                {isLoading ? 'Kaydediliyor...' : 'Evet, Onayla'}
              </button>
              <button
                onClick={() => setShowSystemConfirm(false)}
                className="cancel-btn"
                disabled={isLoading}
              >
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification (Portal ile Body'ye Eklenir) */}
      {toast.show && typeof document !== 'undefined' && createPortal(
        <div className={`toast-notification ${toast.type}`}>
          <div className="toast-content">
            <div className="toast-icon">
              <i className={`fas ${toast.type === 'success' ? 'fa-check' : toast.type === 'error' ? 'fa-exclamation' : 'fa-info'}`}></i>
            </div>
            <div className="toast-message">{toast.message}</div>
            <button className="toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="toast-progress"></div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default OrderDetails; 